// @ts-nocheck
/**
 * Birch Registry — unified create + edit page.
 *
 * One page (`/pages/registry-create`) serves two states:
 *   1. No registry yet → AI form, DIY browse, simple Save button.
 *   2. Registry exists → AI form relabelled, items marked Added, settings
 *      dialog (due date + end), right sidebar with publish/delivery, etc.
 *
 * State is detected from URL `?id=`, then from a localStorage cache, then
 * confirmed by API.list(). When confirmed, `birchRegistryId` is cached so
 * future visits (and the header nav rewrite) can short-circuit the round trip.
 */
(function () {
  "use strict";

  const LS_KEY = "birchRegistryId";

  // ── Guest mode ─────────────────────────────────────────────────────────────
  //
  // The builder now renders for signed-out shoppers too. Their registry is real
  // and server-persisted (owned by an HMAC-signed guest session cookie) and gets
  // claimed on sign-in — so building, adding, removing and AI refinement all
  // behave identically. What differs is the set of actions that need a Shopify
  // customer behind them: publish, share, delivery/shipping, cash funds, group
  // gifting, ending a registry, and the multi-registry surfaces.
  //
  // Those are ALL enforced server-side (they stay behind requireCustomer). The
  // gating here exists so the UI never offers an action that would bounce — a
  // guest sees the affordance, understands why it's held back, and gets a
  // one-tap route to sign in.

  function isGuest() {
    return document.body.dataset.regAuth === "guest";
  }

  // Sign-in URL with a return-to-here target. Liquid renders it (correctly
  // encoded, query string intact) into body[data-reg-signin); the fallback
  // covers surfaces that haven't been updated to stamp it.
  function signInHref() {
    return (
      document.body.dataset.regSignin ||
      window.BirchRegistry?.signInUrl?.() ||
      "/customer_authentication/login"
    );
  }

  /**
   * Intercept a sign-in-only action for a guest. Returns true when it handled
   * the click (caller must bail); false for a signed-in customer (proceed).
   *
   * Explaining beats hiding here: a guest who can't find "Share" assumes the
   * product can't do it. A guest who taps Share and is told "sign in to share —
   * your registry is already saved" learns both what they get and that they
   * won't lose anything.
   */
  function guestGate(actionLabel, detail) {
    if (!isGuest()) return false;
    openSignInPrompt(actionLabel, detail);
    return true;
  }

  function openSignInPrompt(actionLabel, detail) {
    const esc = window.BirchRegistry?.escapeHtml || ((s) => String(s ?? ""));
    const backdrop = document.createElement("div");
    backdrop.className = "registry-modal-backdrop";
    backdrop.innerHTML = `
      <div class="registry-modal" role="dialog" aria-modal="true" aria-labelledby="rcp-signin-title" style="max-width:420px;text-align:center">
        <div style="font-size:1.75rem;line-height:1;margin-bottom:12px" aria-hidden="true">✦</div>
        <h2 id="rcp-signin-title" style="margin:0 0 10px;font-size:1.2rem">Sign in to ${esc(actionLabel)}</h2>
        <p style="font-size:0.9rem;color:var(--reg-muted);margin:0 0 6px;line-height:1.55">
          ${esc(detail || "This step needs a Birch account.")}
        </p>
        <p style="font-size:0.85rem;color:var(--reg-muted);margin:0 0 20px;line-height:1.55">
          Everything you've built so far is already saved — signing in attaches it to your
          account. Nothing is lost.
        </p>
        <a class="registry-btn registry-btn--primary registry-btn--block" href="${esc(signInHref())}">Sign in or create account</a>
        <button type="button" class="registry-btn registry-btn--secondary registry-btn--block" data-action="close" style="margin-top:8px">Keep building</button>
      </div>`;
    document.body.appendChild(backdrop);
    const close = () => backdrop.remove();
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
    backdrop.querySelector("[data-action=close]").addEventListener("click", close);
  }

  /**
   * Sticky "N items saved — sign in to keep it" bar.
   *
   * Raised only once the guest has something to lose (>=1 live item). Before
   * that the standing banner above the hero is enough, and a second permanent
   * reminder on an empty registry is just nagging.
   */
  function renderGuestBar() {
    if (!isGuest()) return;
    let bar = document.getElementById("rcp-guest-bar");
    const count = (state.registry?.items || []).filter((i) => i && i.status !== "removed").length;
    if (count === 0) {
      if (bar) bar.hidden = true;
      return;
    }
    if (!bar) {
      bar = document.createElement("div");
      bar.id = "rcp-guest-bar";
      bar.className = "rcp-guest-bar";
      bar.setAttribute("role", "status");
      document.body.appendChild(bar);
    }
    const esc = window.BirchRegistry?.escapeHtml || ((s) => String(s ?? ""));
    bar.innerHTML = `
      <span class="rcp-guest-bar__text">
        <strong>${count} item${count === 1 ? "" : "s"}</strong> saved to your guest registry
      </span>
      <a class="rcp-guest-bar__cta" href="${esc(signInHref())}">Sign in to keep it</a>`;
    bar.hidden = false;
  }

  // Birch Credit reward percent shown on product-card badges. Single source:
  // a theme-set global (window.BIRCH_REWARD_PERCENT) falls back to 5. Keep in
  // sync with the `registry_reward_percent` theme setting + backend rewardPercent.
  const REWARD_PCT = Number(window.BIRCH_REWARD_PERCENT) || 5;

  // "Remember for the chat session" (multi-registry resolution). When the user
  // picks which registry to manage from the picker — or simply opens one — we
  // store it keyed to the AI chat sessionId so the choice is scoped to the
  // current conversation (memory scope = chat session, NOT persisted forever).
  // The chat sessionId is owned by birch-ai-companion.js (localStorage
  // "birch_ai_session"); we only read it here.
  const SESSION_CHOICE_KEY = "birch:reg:sessionChoice";
  function chatSessionId() {
    try { return (JSON.parse(localStorage.getItem("birch_ai_session") || "null") || {}).id || null; }
    catch (_) { return null; }
  }
  function readSessionRegistryChoice() {
    try {
      const raw = JSON.parse(localStorage.getItem(SESSION_CHOICE_KEY) || "null");
      const sid = chatSessionId();
      if (raw && sid && raw.sessionId === sid) return raw.registryId || null;
    } catch (_) {}
    return null;
  }
  function writeSessionRegistryChoice(registryId) {
    try {
      const sid = chatSessionId();
      if (sid && registryId) {
        localStorage.setItem(SESSION_CHOICE_KEY, JSON.stringify({ sessionId: sid, registryId: registryId }));
      }
    } catch (_) {}
  }

  // Multi-registry chooser for the EDIT intent: shown when the customer has >1
  // active registry and lands on the builder with no explicit/remembered target.
  // Resolves with the chosen registry object, the string "new" (start fresh), or
  // null if dismissed. The BUILD buttons never call this — they create new
  // without asking. Reuses .registry-modal + .registry-pick styles (registry.css).
  function pickRegistryToEdit(registries) {
    const esc = (window.BirchRegistry && window.BirchRegistry.escapeHtml) || ((s) => String(s));
    return new Promise((resolve) => {
      const backdrop = document.createElement("div");
      backdrop.className = "registry-modal-backdrop";
      const rows = registries.map((r) => {
        const count = r._count && r._count.items != null
          ? r._count.items
          : (r.itemCount != null ? r.itemCount : null);
        const meta = (count != null ? (count + " item" + (count === 1 ? "" : "s")) : "")
          + (r.status === "draft" ? " · draft" : "");
        return '<button type="button" class="registry-pick__row" data-pick-id="' + esc(r.id) + '">'
          + '<span class="registry-pick__name">' + esc(r.name || "Your Registry") + '</span>'
          + (meta ? '<span class="registry-pick__meta">' + esc(meta) + '</span>' : '')
          + '</button>';
      }).join("");
      backdrop.innerHTML =
        '<div class="registry-modal" role="dialog" aria-modal="true" aria-label="Choose a registry" style="max-width:420px">'
        + '<h2 style="margin:0 0 4px">Which registry?</h2>'
        + '<p class="registry-modal__subtitle" style="margin:0 0 16px">You have ' + registries.length
        + ' active registries. Pick one to keep building, or start a new one.</p>'
        + '<div class="registry-pick__list">' + rows + '</div>'
        + '<div class="registry-modal__actions" style="margin-top:16px">'
        + '<button type="button" class="registry-btn registry-btn--secondary" data-pick-new>+ Start a new registry</button>'
        + '</div>'
        + '</div>';
      document.body.appendChild(backdrop);
      const done = (val) => { backdrop.remove(); resolve(val); };
      backdrop.addEventListener("click", (e) => {
        const t = e.target;
        if (t === backdrop) return done(null);
        if (t.closest("[data-pick-new]")) return done("new");
        const row = t.closest("[data-pick-id]");
        if (row) {
          const id = row.getAttribute("data-pick-id");
          return done(registries.find((r) => r.id === id) || null);
        }
      });
    });
  }

  const state = {
    registry: null,
    itemByProductId: {},
    itemByHandle: {},
    aiInjectedCounts: {},
    previewItems: null,   // non-null when browsing a preset (read-only, no registry changes)
  };

  // Publish the registry the user is currently viewing so the AI companion can
  // pass it as a reference on EVERY chat call — the AI then edits THIS registry
  // instead of grabbing the most-recent one, and creates a new one when there's
  // none in view. A live getter (closes over `state`) stays correct across every
  // load/create/build/clear path without having to touch each assignment site.
  try {
    Object.defineProperty(window, "__birchActiveRegistryId", {
      configurable: true,
      get() {
        return state.registry && state.registry.id && state.registry.status !== "ended"
          ? state.registry.id : null;
      },
    });
  } catch (_e) { /* defineProperty blocked — companion falls back to no reference */ }

  // ── Registry Revamp feature flag ──────────────────────────────────────────
  // NEW_IA = the stacked "All Items" information architecture: category tabs are
  // retired into one scrolling view, the sidebar becomes a scroll-nav, and a
  // single "Currently in your registry" block with horizontal macro tabs sits on
  // top (replacing the per-category in-registry cards + standalone suggestions
  // box). Flip to false to fully restore the legacy panel-switching UI.
  const NEW_IA = true;
  // Frozen carousel ordering band per snapshot member (#2): id → 0|1|2
  // (pick | already-added | rest). Computed on the first render of a member and
  // never recomputed in-session, so a live add keeps the card in place; a page
  // reload starts empty so already-added items re-sort to right after the picks.
  const _carouselBand = new Map();
  // Group-2 "other on-registry items" carousel members, FROZEN at first load (like
  // the curatedSuggestions snapshot) so the carousel order stays constant on
  // add/remove until the next reload. null = not captured yet; an array thereafter.
  let _carouselExtras = null;

  // Active macro tab in the top "Currently in your registry" block. Seeded to
  // the first real macro on render (there is no "All" tab).
  let _curRegTab = null;
  // View mode for that block: "carousel" (horizontal bands — default) or "grid".
  // The last explicit user choice is persisted and wins over the default.
  let _curRegView = (() => { try { return localStorage.getItem("birch:reg:curView") || "carousel"; } catch (_e) { return "carousel"; } })();

  // ── Card affordances (trust score + find similar) ─────────────────────────
  // Trust score to render on a card. Returns null when no real score is
  // available — callers omit the chip entirely (no default, no "★ 0").
  function displayTrust(raw) {
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.max(1, Math.min(100, Math.round(n)));
  }
  const FIND_SIMILAR_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>`;
  // Trust score badge — the SAME ring UI as the PDP product card
  // (snippets/product-card-trust-score.liquid). Returns "" when no real score.
  const trustChipHtml = (raw) => {
    const t = displayTrust(raw);
    if (t == null) return "";
    return `<div class="product-card__trust-score-stack rcp-trust-stack">
      <div class="product-card__trust-score" style="--trust-score-value: ${t};" aria-label="Trust score ${t} out of 100">
        <svg class="product-card__trust-score-ring" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
          <circle class="product-card__trust-score-track" cx="20" cy="20" r="16" pathLength="100"></circle>
          <circle class="product-card__trust-score-progress" cx="20" cy="20" r="16" pathLength="100"></circle>
        </svg>
        <span class="product-card__trust-score-value"><span class="product-card__trust-score-number">${t}</span></span>
      </div>
      <span class="product-card__trust-score-label">TrustScore</span>
    </div>`;
  };
  // Structured identity attrs for a find-similar button. The click handler
  // forwards these to the AI as `findSimilar` so the worker resolves the seed
  // card deterministically (external LushLife items have no Shopify handle —
  // title-only queries made the AI guess one and miss). Tolerant of the
  // different card shapes: every field self-omits when absent.
  const findSimilarAttrs = (it, esc) => {
    const src = it.source || (it.lushlifeProductId ? "external" : (it.shopifyHandle || it.handle) ? "shopify" : "");
    return ` data-fs-source="${esc(src)}"` +
      ` data-fs-llid="${esc(it.lushlifeProductId || "")}"` +
      ` data-fs-handle="${esc(it.shopifyHandle || it.handle || "")}"` +
      ` data-fs-category="${esc(it.productType || it.category || it.stage || "")}"`;
  };
  // Generic browse-card action line: Find similar + trust (no "Why this" — that's
  // AI-only). title drives the find-similar AI query (data-find-similar); the
  // data-fs-* attrs carry the card's structured identity.
  const browseActionRow = (it, esc) =>
    `<div class="rcp-sugg-actions-row">
      <button type="button" class="rcp-sugg-icon rcp-sugg-findsimilar" data-find-similar="${esc(it.title || "")}"${findSimilarAttrs(it, esc)} title="Find a similar option" aria-label="Find a similar option to ${esc(it.title || "this")}">${FIND_SIMILAR_SVG}</button>
      ${trustChipHtml(it.trustScore)}
    </div>`;

  // Attach anything built as a guest to the customer who just signed in, and
  // return the registry to land on (or null).
  //
  // The server ALSO claims implicitly on the registries-list read, so this
  // can't be the only mechanism and doesn't need to be reliable — its job is to
  // learn WHICH registry was adopted so we can open it directly instead of
  // guessing. With no guest cookie it short-circuits server-side.
  //
  // Runs before registry resolution so the adopted registry is already in the
  // customer's list by the time we look.
  async function claimGuestRegistryOnLoad() {
    if (isGuest()) return null;
    // No breadcrumb ⇒ this browser never held a guest session ⇒ no round trip.
    // Keeps the signed-in hot path exactly as fast as it was before guest mode.
    if (!window.BirchRegistry?.hasGuestPending?.()) return null;
    const form = document.getElementById("registry-create-form");
    try {
      const claimed = await window.BirchRegistry.claimGuestWork({
        registrantFirst: form?.dataset.customerFirst || "",
        registrantLast: form?.dataset.customerLast || "",
        customerEmail: form?.dataset.customerEmail || "",
      });
      if (!claimed.length) return null;
      // Most items wins when a guest somehow built more than one — that's the
      // registry they actually worked on.
      const best = claimed.slice().sort((a, b) => (b.itemCount || 0) - (a.itemCount || 0))[0];
      window.BirchRegistry.showToast?.(
        claimed.length === 1
          ? "Your registry is now saved to your account."
          : `${claimed.length} registries are now saved to your account.`,
      );
      return best || null;
    } catch (_) {
      return null;
    }
  }

  async function init() {
    const { API } = window.BirchRegistry;

    // Wire pieces that are mode-independent.
    wireCategoryNav();
    wireNavToggle();
    wireSaveButton();
    wireAddUrlButton();
    wireExternalUrlBar();
    wireCopyButton();
    wireCopyUrlBar();
    wireSettingsButton();
    wireEditDetailsButton();
    wireBrowsePicks();
    wireAiFormSubmit();
    wireGiftCardPanel();
    ensureControlsListener();
    wireLiveFeed();
    wireSuggestionActions();
    wireCashFundsPanel();
    wireProfileNudge();

    // Registry Revamp: switch the builder into the stacked "All Items" IA and
    // wire its scroll-nav + the top "Currently in your registry" tab block.
    if (NEW_IA) {
      document.querySelector(".rcp-builder")?.classList.add("rcp-builder--stacked");
      wireCurrentRegistryTabs();
      wireScrollSpy();
      wireCarouselArrows();
    }

    // #5: "Remove all suggestions" — bulk-dismiss every pending AI suggestion.
    document.getElementById("rcp-remove-all-sugg")?.addEventListener("click", handleRemoveAllSuggestions);

    // #9: scroll-to-top — reveal after scrolling down, smooth-scroll to top.
    const scrollTopBtn = document.getElementById("rcp-scroll-top");
    if (scrollTopBtn) {
      scrollTopBtn.addEventListener("click", () => window.scrollTo({ top: 0, behavior: "smooth" }));
      let _stTick = false;
      let _stLastY = window.scrollY;
      window.addEventListener(
        "scroll",
        () => {
          if (_stTick) return;
          _stTick = true;
          requestAnimationFrame(() => {
            const y = window.scrollY;
            const scrollingUp = y < _stLastY;
            // Only reveal while the user is scrolling up and far enough down the
            // page; hide on any downward scroll or near the top.
            scrollTopBtn.hidden = !(scrollingUp && y > 600);
            _stLastY = y;
            _stTick = false;
          });
        },
        { passive: true },
      );
    }

    // Try to discover an existing registry. "Ended" registries are NOT
    // considered active — once a registry is ended the customer is back in
    // create mode for any future registry, so we ignore them in auto-discovery
    // (cache + list probe) and clear the stale cache. They can still be
    // opened explicitly via ?id=<endedId> for read-only review.
    const params = new URLSearchParams(location.search);
    const idFromQuery = params.get("id");
    // Two "new registry" intents land here from CTAs (see registry-create.liquid,
    // which funnels every legacy /pages/registry-create link):
    //   • ?new=1    — explicit "+ Start a new registry": ALWAYS mint a fresh row.
    //   • ?create=1 — generic "Start your registry": eagerly create, reusing an
    //                 existing EMPTY draft if one exists (idempotent on refresh).
    // Both ignore the cached/last-opened registry and skip reuse-most-recent.
    const forceNew = params.get("new") === "1";
    const createIntent = params.get("create") === "1";
    const freshIntent = forceNew || createIntent;
    // Eager creation requires SOME identity — a signed-in customer or a guest
    // session. Both qualify: a guest registry is a real persisted row that gets
    // claimed on sign-in.
    //
    // This used to test for #registry-create-form, on the reasoning that Liquid
    // only rendered the builder form for a logged-in customer. That marker
    // stopped meaning "signed in" the moment the builder started rendering for
    // guests too, so identity now comes from body[data-reg-auth] — stamped by
    // registry-edit.liquid, which is the only thing that actually knows.
    const signedIn = !isGuest();
    if (freshIntent) {
      try { localStorage.removeItem(LS_KEY); } catch (_) {}
    }
    // Trusted explicit target: an ?id= in the URL, else the registry remembered
    // for THIS chat session. Fresh-start intents ignore the session memory.
    const sessionChoice = freshIntent ? null : readSessionRegistryChoice();
    // Just signed in with guest work in tow → adopt it and make it the target,
    // so the customer lands on the registry they were building rather than an
    // empty create screen. Outranks the remembered session choice (which points
    // at the pre-sign-in guest id) but not an explicit ?id=.
    const claimedRegistry = await claimGuestRegistryOnLoad();
    const targetId = idFromQuery || claimedRegistry?.id || sessionChoice || null;
    const paintHint = targetId || (freshIntent ? null : localStorage.getItem(LS_KEY)) || null;

    // Optimistic paint: if we have any hint of edit mode, relabel the CTA and
    // reveal Settings immediately — before the API round trip. The real state
    // (incl. empty vs populated) is applied by enterEditMode()/enterCreateMode().
    if (paintHint) {
      document.body.dataset.regMode = "edit";
      document.body.dataset.regEmpty = "0"; // optimistic; corrected after load
      setAiButtonLabel(true);
      setSettingsButtonVisibility(true);
    }

    let registry = null;
    // 1. Explicit target (URL ?id= or this chat session) → load it.
    if (targetId) {
      try {
        const res = await API.get(targetId);
        registry = res?.registry || null;
      } catch (_) { /* stale id; fall through */ }
    }
    // Ended registries never put the unified page into edit mode — once a
    // registry is over, the customer is back at create. We clear the cache,
    // strip ?id= from the URL so reloads don't bring it back, and fall through.
    // Past/ended registries are still reachable from /pages/registry-manage.
    if (registry && registry.status === "ended") {
      registry = null;
      try { localStorage.removeItem(LS_KEY); } catch (_) {}
      try { sessionStorage.removeItem("birchRegistryProbe"); } catch (_) {}
      if (idFromQuery) {
        try {
          const u = new URL(location.href);
          u.searchParams.delete("id");
          history.replaceState(null, "", u.toString());
        } catch (_) {}
      }
    }

    // 2. Fresh-start intent (?new=1 / ?create=1) → mint the registry up-front so
    //    the builder always has a concrete target, then pin ?id= so a refresh
    //    reuses it instead of re-creating. ?new=1 always inserts; ?create=1
    //    reuses an empty draft when present. Skips the reuse-most-recent path
    //    below entirely (a "Start" CTA must never adopt a populated registry).
    // No auth precondition any more: everyone reaching this page has an
    // identity to create against — a signed-in customer, or a guest session
    // that request() mints on the first call.
    if (!registry && freshIntent) {
      // Guests always get their single reused registry — the server ignores
      // forceNew for them (MAX_GUEST_REGISTRIES), so "+ Start a new registry"
      // resolves to the one they already have rather than stacking up drafts
      // nobody will claim.
      registry = await eagerCreateRegistry({ forceNew: forceNew && signedIn });
    }

    // 3. No explicit target or fresh intent → discover an existing registry,
    //    asking via the picker when there's more than one. A signed-in customer
    //    with none still gets one eagerly so the builder always works against a
    //    real registry (reusing an empty draft keeps repeat visits idempotent).
    if (!registry && !freshIntent) {
      try {
        const list = await API.list();
        const active = ((list && list.registries) || [])
          .filter((r) => r.status !== "ended")
          .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        if (active.length === 1) {
          registry = active[0];
        } else if (active.length > 1) {
          // Ambiguous → ask which to manage, with a "start a new one" escape hatch.
          const chosen = await pickRegistryToEdit(active);
          if (chosen === "new") {
            registry = await eagerCreateRegistry({ forceNew: true });
          } else if (chosen && chosen.id) {
            registry = chosen;
            writeSessionRegistryChoice(chosen.id); // remember for the chat session
          } else {
            // Dismissed without choosing → fall back to the most recent so we
            // never trap the user on a blank page.
            registry = active[0];
          }
        } else {
          // 0 active → eager-create (reusing an empty draft if any). Guests
          // included: this is what gives them a concrete registry to build
          // against from the first click.
          registry = await eagerCreateRegistry({ forceNew: false });
        }
      } catch (_) { /* unauthenticated or offline — continue in create mode */ }
    }

    // "All registries" chip — show when the customer has more than one
    // registry total (current + any past). We already pulled the list above
    // when no cachedId existed; if we used the cached path we need a second
    // probe, but only fire it once and tolerate failures silently.
    revealAllRegistriesChipIfPast(registry);

    if (registry) {
      enterEditMode(registry);
      // Phase 11 (live build): if we arrived mid-build (?building=1) or the
      // registry reports an in-flight build, poll D1 for liveness — the build is
      // running server-side and items land at the end; D1, not the (now torn-down)
      // SSE stream, is the source of truth.
      const arrivedBuilding = new URLSearchParams(location.search).get("building") === "1";
      if (arrivedBuilding || registry.buildStatus === "building") {
        // Note: the builder card is intentionally NOT re-locked here — a reload
        // always starts unlocked. The card only locks in response to a live
        // build_started/rebuild_started event during the session, so a stuck or
        // crashed server-side build can never leave the card disabled forever.
        pollBuildStatus();
        // Arrived mid-build (e.g. navigated here on build_started) — drop the
        // user at "Your Registry" so they watch it populate, not the page top.
        scrollToBuilder();
      }
    } else {
      enterCreateMode();
    }

    // Last, because it needs the registry decided: the intake page's "Smart
    // Registry Builder" hands the build over to this page rather than running
    // it on its own (see below).
    startAiHandoffBuild(registry);

    // After the page is drawn, so the scroll lands on the final layout.
    openImportFromLink();
  }

  // ── AI-build handoff from /pages/registry-start ────────────────────────────
  // The intake fork parks a `birch:open-with-query` payload in sessionStorage
  // and redirects here with `?aibuild=1`. We run it once the registry in view is
  // settled, so the build streams over a connection that stays alive to the end
  // — on the intake page the redirect used to kill it mid-stream.
  //
  // Identical for guests and signed-in families: `registry` above is already
  // resolved through whichever identity this browser has, and the build is
  // pinned to THAT row's id.
  function startAiHandoffBuild(registry) {
    const params = new URLSearchParams(location.search);
    if (params.get("aibuild") !== "1") return;
    // Strip the flag before anything can throw: this is a one-shot instruction,
    // and a reload that still carried it would start a second build.
    try {
      const u = new URL(location.href);
      u.searchParams.delete("aibuild");
      history.replaceState(null, "", u.toString());
    } catch (_) {}

    const handoff = window.BirchRegistry.takeAiBuildHandoff();
    // No payload (private-mode sessionStorage, or a hand-typed URL) is not a
    // dead end — the flag alone still means "build this registry". The intake
    // answers are on the registry row and in the customer profile either way;
    // only the synthesized brief is lost.
    const detail = (handoff && handoff.detail) || {
      query: "Build me a complete registry.",
      fresh: true,
      intent: "registry_builder",
      mode: "full_build",
      metadata: { brief: "" },
    };

    // Build into the registry actually on screen. It normally equals the id the
    // intake page parked, but the editor's own resolution wins if they differ
    // (a stale guest token, a claimed registry) — building into a row the user
    // can't see is the one outcome worth ruling out.
    const targetId = (registry && registry.id) || (handoff && handoff.registryId) || null;
    if (targetId) {
      detail.registryId = targetId;
      delete detail.startNew;
    } else {
      delete detail.registryId;
      detail.startNew = true;
    }
    // Full screen: this is the whole task right now, not a side conversation.
    detail.fullscreen = true;

    // Put "Your Registry" behind the panel so closing the chat lands on the list
    // filling in, not the top of the page. Instant, because the chat is opening
    // in the same tick (see scrollToBuilder).
    scrollToBuilder({ instant: true });
    window.dispatchEvent(new CustomEvent("birch:open-with-query", { detail }));
  }

  async function revealAllRegistriesChipIfPast(currentRegistry) {
    const chip = document.getElementById("rcp-all-registries-chip");
    if (!chip) return;
    // Guests hold exactly one registry and /pages/registry-manage is sign-in
    // only — the chip would lead nowhere useful, so it stays hidden.
    if (isGuest()) return;
    try {
      const { API } = window.BirchRegistry;
      const list = await API.list();
      const regs = (list && list.registries) || [];
      const hasPast = regs.length > 1 ||
        regs.some((r) => r.status === "ended" && r.id !== currentRegistry?.id);
      if (hasPast) chip.hidden = false;
    } catch (_) { /* tolerate */ }
  }

  // Eager registry creation for the "new registry" CTAs (?new=1 / ?create=1).
  // Mints a registry — or, for the generic Start CTA (reuseEmpty), reuses the
  // customer's existing empty draft — so the builder always has a concrete
  // target, then rewrites the URL to ?id=<registryId>, stripping the create/new
  // intent flag so a refresh loads the SAME registry instead of minting another.
  // Returns the registry row, or null on failure (caller falls back to the
  // no-registry onboarding UI).
  async function eagerCreateRegistry({ forceNew } = {}) {
    const { API } = window.BirchRegistry;
    const form = document.getElementById("registry-create-form");
    const payload = {
      registrantFirst: form?.dataset.customerFirst || "",
      registrantLast: form?.dataset.customerLast || "",
    };
    // Generic "Start" reuses an empty draft if one exists (idempotent); the
    // explicit "+ Start a new registry" (forceNew) always inserts a fresh row.
    // customerEmail is intentionally NOT sent — an empty shell stays silent; the
    // REGISTRY_CREATED milestone fires on first content (build/add) instead.
    if (!forceNew) payload.reuseEmpty = true;
    let registry;
    try {
      const res = await API.create(payload);
      registry = res?.registry || null;
    } catch (_) { return null; }
    if (registry) {
      try {
        const u = new URL(location.href);
        u.searchParams.delete("create");
        u.searchParams.delete("new");
        u.searchParams.set("id", registry.id);
        history.replaceState(null, "", u.toString());
      } catch (_) {}
      writeSessionRegistryChoice(registry.id);
    }
    return registry;
  }

  // ── Mode entries ──────────────────────────────────────────────────────────
  function enterCreateMode() {
    localStorage.removeItem(LS_KEY);
    document.body.dataset.regMode = "create";
    // No registry at all (logged out / create failed) → the full onboarding UI:
    // "Surprise Me" + "Let's Build Together" both show (gated on data-reg-empty).
    document.body.dataset.regEmpty = "1";
    applyHero({ status: "draft" });
    renderRightSidebar(null);
    setAiButtonLabel(false);
    // No registry yet → nothing for Settings to act on, so keep the chip
    // hidden. It becomes visible again when enterEditMode runs.
    setSettingsButtonVisibility(false);
    // No registry yet → the top block's heading reads "Your Registry" rather than
    // "Currently in your registry" (registryDisplayName(null) === "Your Registry").
    // Mirrors enterEditMode, which titles the block with the registry's name.
    applyRegistryNameTitles(null);
    // Even with no registry yet, render the (empty) "Currently in your registry"
    // boxes + the always-visible "All items" view (issues 1 & 2), and load the
    // LushLife browse feed for the static rows (Case 1) — renderSuggestions()/
    // setupBrowseFeeds() otherwise only run in edit mode.
    renderInjectedItems();
    updateCategoryCounts();
    setupBrowseFeeds();
  }

  function enterEditMode(registry) {
    state.registry = registry;
    // Review Box: split AI "suggested" items into a separate bucket. The
    // editor's active logic (counts, "added" marks, completion %) stays
    // active-only; the suggestions render in inline "Suggested for you"
    // sections under each category's "Currently in your registry" card (and an
    // aggregate group in "All items") via renderSuggestions().
    state.suggestions = (registry.items || []).filter((it) => it.status === "suggested");
    state.registry.items = (registry.items || []).filter((it) => it.status !== "suggested");
    state.itemByProductId = {};
    state.itemByHandle = {};
    (state.registry.items || []).forEach((it) => {
      if (it.source !== "shopify") return;
      if (it.shopifyProductId) state.itemByProductId[String(it.shopifyProductId)] = it;
      if (it.shopifyHandle) state.itemByHandle[String(it.shopifyHandle)] = it;
    });
    localStorage.setItem(LS_KEY, registry.id);
    // Remember this as the chat-session's working registry so an ambiguous
    // reload within the same conversation doesn't re-prompt the picker.
    writeSessionRegistryChoice(registry.id);
    document.body.dataset.regMode = "edit";
    // The onboarding UI ("Surprise Me" + "Let's Build Together", builder copy) is
    // driven by EMPTINESS, not id-presence: a freshly-created (or otherwise empty)
    // registry presents exactly like a new one. Populated → "Update My Registry"
    // and "Let's Build Together" hides (CSS on body[data-reg-empty="0"]).
    const isEmpty = (state.registry.items || []).length === 0;
    document.body.dataset.regEmpty = isEmpty ? "1" : "0";
    applyHero(registry);
    applyRegistryNameTitles(registry);
    applyCompletionBanner(registry);
    setAiButtonLabel(!isEmpty);
    setSettingsButtonVisibility(true);
    markAddedProducts();
    renderInjectedItems();
    renderSuggestions();
    updateCategoryCounts();
    renderRightSidebar(registry);
    maybeDefaultToAllItems();
    // Notify cash funds panel that registry is now available
    if (window._rcpOnRegistryReady) window._rcpOnRegistryReady(registry);
  }

  // ── Hero ──────────────────────────────────────────────────────────────────
  function applyHero(registry) {
    const head = document.querySelector("[data-rcp-hero-headline]");
    const lede = document.querySelector("[data-rcp-hero-lede]");
    if (!registry || registry.status === "draft" || !state.registry) {
      if (head) head.innerHTML = "Your Registry, <span class=\"rcp-hero__accent\">Thoughtfully Built.</span>";
      if (lede) {
        lede.textContent =
          "Every pick is research-backed, then made yours. Refine with Birch AI, share with your village, and receive everything exactly when it matters most.";
      }
      return;
    }
    const niceName = registry.babyName ? `Baby ${registry.babyName}` : registry.name;
    const isPublished = registry.status === "published";
    if (head) {
      head.textContent = isPublished
        ? `${niceName}'s registry is live.`
        : `${niceName}'s registry is taking shape.`;
    }
    if (lede) {
      lede.textContent = isPublished
        ? "Edit anytime — changes apply instantly. Share the link to invite gifts."
        : "Review what's covered, add anything still missing, and publish when it feels just right.";
    }
  }

  // #10 — surface the registry's own name where the page used the generic
  // "Your Registry" / "Your full registry" labels. Falls back to the babyName
  // form, then a sensible default if the registry is unnamed.
  function registryDisplayName(r) {
    if (!r) return "Your Registry";
    if (r.name && r.name.trim()) return r.name.trim();
    if (r.babyName && r.babyName.trim()) return `Baby ${r.babyName.trim()}'s Registry`;
    return "Your Registry";
  }
  function applyRegistryNameTitles(registry) {
    const name = registryDisplayName(registry);
    const navTitle = document.querySelector(".rcp-builder-nav__title");
    if (navTitle) navTitle.textContent = name;
    const allTitle = document.querySelector("#rcp-cat-all .rcp-cat-header__title");
    if (allTitle) {
      // preserve the live total span that JS appends after the text node
      const totalSpan = allTitle.querySelector(".rcp-all-total");
      allTitle.textContent = name;
      if (totalSpan) allTitle.appendChild(totalSpan);
    }
    const curTitle = document.querySelector(".rcp-current-registry__title");
    if (curTitle) {
      // Preserve the live count badge JS appends after the title text node.
      const badge = curTitle.querySelector(".rcp-current-registry__count-badge");
      curTitle.textContent = name;
      if (badge) curTitle.appendChild(badge);
    }
  }

  function applyCompletionBanner(registry) {
    const host = document.getElementById("re-completion-banner-host");
    if (!host) return;
    host.setAttribute(
      "data-registry",
      JSON.stringify({
        status: registry.status,
        completionDiscountCode: registry.completionDiscountCode || null,
        completionDiscountStartsAt: registry.completionDiscountStartsAt || null,
        completionDiscountEndsAt: registry.completionDiscountEndsAt || null,
      }),
    );
    host.__decorated = false;
  }

  function setAiButtonLabel(_isPopulated) {
    // The DIY card button jumps to the recommended-picks browser; its label is
    // action-based so it reads the same in create and edit mode.
    const diyBtn = document.getElementById("rcp-diy-btn") || document.querySelector(".rcp-diy-card .rcp-secondary-btn");
    if (diyBtn) {
      diyBtn.innerHTML = "Explore recommended picks →";
      if (!diyBtn.dataset.rcpWired) {
        diyBtn.dataset.rcpWired = "1";
        diyBtn.addEventListener("click", (e) => {
          e.preventDefault();
          // If sitting on the aggregate "All items" tab, switch to the first
          // browsable category (Nursery) so the user lands on real picks.
          // Otherwise stay on the current category. Either way, scroll to the
          // builder so the picks are in view.
          const allActive = document.getElementById("rcp-cat-all")?.classList.contains("rcp-cat-panel--active");
          if (allActive) activatePanel("rcp-cat-1");
          document.getElementById("rcp-browse")?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    }
  }

  function setSettingsButtonVisibility(visible) {
    const btn = document.getElementById("rcp-settings-btn");
    if (btn) btn.hidden = !visible;
    // The nav-header pencil (edit name / owner / co-registrant) follows the
    // same "a registry exists" gate as the hero Settings chip.
    const editBtn = document.getElementById("rcp-edit-details-btn");
    if (editBtn) editBtn.hidden = !visible;
  }

  // ── Catalog mark-added ────────────────────────────────────────────────────
  function lookupCardItem(card) {
    const pid = card.dataset.productId;
    const handle = card.dataset.productHandle;
    return (pid && state.itemByProductId[pid]) || (handle && state.itemByHandle[handle]) || null;
  }

  function markAddedProducts() {
    document.querySelectorAll(".rcp-pgrid-card[data-product-id]").forEach((card) => {
      const btn = card.querySelector("[data-rcp-add]");
      if (!btn) return;
      const item = lookupCardItem(card);
      if (item) setAdded(btn, card, item.id);
    });
  }

  function setAdded(btn, card, itemId) {
    btn.classList.add("rcp-pgrid-add-btn--added");
    // When the registry item linked to this catalog tile has purchases (or
    // unresolved contributions), removal is blocked. Render the tile in a
    // settled "Gifted" / "Locked" state and skip the remove click handler —
    // mirrors the backend's guardDelete so the UI doesn't dangle a control
    // the server will reject.
    const item = lookupItemById(itemId);
    const blocked = !!itemRemovalBlockReason(item);
    const purchased = Number(item?.quantityPurchased || 0);
    const wanted = Number(item?.quantityWanted || 1);
    const fullyGifted = purchased > 0 && purchased >= wanted;
    // #6 — an added card shows a "✕ Remove" affordance (clicking removes it),
    // except when it's locked/gifted (can't be removed) → keep "Gifted".
    btn.innerHTML = fullyGifted
      ? `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> Gifted`
      : `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> Remove`;
    btn.classList.toggle("rcp-pgrid-add-btn--locked", blocked);
    btn.setAttribute(
      "aria-label",
      blocked
        ? (card.dataset.productTitle || "") + " — already gifted, cannot remove"
        : (card.dataset.productTitle || "") + " — remove from registry",
    );
    btn.dataset.registryItemId = itemId;
    const fresh = btn.cloneNode(true);
    btn.replaceWith(fresh);
    if (blocked) {
      // Soft no-op + explainer toast keeps screen-reader semantics intact
      // without confusing keyboard users who hit the button.
      fresh.addEventListener("click", (e) => {
        e.preventDefault();
        const reason = itemRemovalBlockReason(lookupItemById(itemId));
        if (reason) window.BirchRegistry.showToast?.(reason);
      });
    } else {
      fresh.addEventListener("click", () => handleRemove(fresh, card));
    }
    // Note: do NOT inject re-card-ctrls into browse tiles (rcp-pgrid-card,
    // including the rcp-subcat-section variants). Controls (qty + variant) live
    // only on the matching card in the "Currently in your registry" section so
    // there's a single source of truth and no duplicate stepper UI. Strip any
    // leftover controls from a prior render.
    removeTileControls(card);
  }

  function setUnadded(btn, card) {
    btn.classList.remove("rcp-pgrid-add-btn--added");
    btn.removeAttribute("data-registry-item-id");
    btn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
      Add
    `;
    btn.setAttribute("aria-label", "Add " + (card.dataset.productTitle || "") + " to registry");
    const fresh = btn.cloneNode(true);
    btn.replaceWith(fresh);
    fresh.addEventListener("click", () => handleAdd(fresh, card));
    removeTileControls(card);
  }

  function injectTileControls(card, itemId) {
    const body = card.querySelector(".rcp-pgrid-card__body");
    if (!body) return;
    const item = lookupItemById(itemId);
    if (!item) return;
    const esc = window.BirchRegistry.escapeHtml;
    const html = renderCardControls(item, esc);
    const existing = body.querySelector("[data-controls-for]");
    const wrap = document.createElement("div");
    wrap.innerHTML = html.trim();
    const fresh = wrap.firstElementChild;
    if (existing) existing.replaceWith(fresh);
    else body.appendChild(fresh);
  }

  function removeTileControls(card) {
    const node = card.querySelector("[data-controls-for]");
    if (node) node.remove();
  }

  // ── Browse Add button handlers ────────────────────────────────────────────
  function wireBrowsePicks() {
    document.querySelectorAll(".rcp-pgrid-card[data-product-id]").forEach((card) => {
      const btn = card.querySelector("[data-rcp-add]");
      if (!btn || btn.dataset.unifiedWired) return;
      btn.dataset.unifiedWired = "1";
      btn.addEventListener("click", () => handleAdd(btn, card));
    });
  }

  async function getOrCreateRegistry() {
    if (state.registry) return state.registry.id;
    const form = document.getElementById("registry-create-form");

    // Delegate the create to the shared SDK helper so naming/defaults live in
    // one place (server-side default name). This page only owns the local UI
    // state it sets up afterwards.
    const { registry } = await window.BirchRegistry.getOrCreateRegistry({
      registrantFirst: form?.dataset.customerFirst || "",
      registrantLast: form?.dataset.customerLast || "",
      customerEmail: form?.dataset.customerEmail || "",
    });
    state.registry = registry;
    state.registry.items = state.registry.items || [];
    localStorage.setItem(LS_KEY, state.registry.id);
    document.body.dataset.regMode = "edit";
    // This lazy path runs only as part of adding an item, so the registry is
    // about to be populated → present as a populated registry.
    document.body.dataset.regEmpty = "0";
    applyHero(state.registry);
    setAiButtonLabel(true);
    setSettingsButtonVisibility(true);
    renderRightSidebar(state.registry);
    return state.registry.id;
  }

  // A throwaway id for an optimistically-inserted item, swapped for the real
  // server id once the add settles. Local-only — never sent to the API.
  function provisionalId() {
    return "tmp-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 8);
  }

  // Swap a provisional (temp-id) row for the server's real item once an add
  // settles, so later edits/removes target the real id. A `merged` add folds
  // into an existing row, so the provisional is dropped and the real row upserted.
  function reconcileProvisional(tmpId, item) {
    const items = state.registry.items || (state.registry.items = []);
    const provIdx = items.findIndex((it) => it.id === tmpId);
    if (provIdx >= 0) items.splice(provIdx, 1);
    const realIdx = items.findIndex((it) => it.id === item.id);
    if (realIdx >= 0) items[realIdx] = item;
    else items.push(item);
    if (item.shopifyProductId) state.itemByProductId[String(item.shopifyProductId)] = item;
    if (item.shopifyHandle) state.itemByHandle[String(item.shopifyHandle)] = item;
  }

  // Pull a failed optimistic row back out of local state + the lookup maps.
  function dropProvisional(tmpId) {
    if (state.registry?.items) {
      state.registry.items = state.registry.items.filter((it) => it.id !== tmpId);
    }
    for (const map of [state.itemByProductId, state.itemByHandle]) {
      for (const k of Object.keys(map)) if (map[k]?.id === tmpId) delete map[k];
    }
  }

  async function handleAdd(btn, card) {
    const { API, showToast } = window.BirchRegistry;
    if (btn.classList.contains("rcp-pgrid-add-btn--added")) {
      return handleRemove(btn, card);
    }
    btn.disabled = true;

    // Need a registry to attach the item to. An existing one lets us insert
    // optimistically; the very first add must await the create before there's
    // anything to render "Currently in your registry" into.
    let registryId;
    try {
      registryId = state.registry ? state.registry.id : await getOrCreateRegistry();
    } catch (_e) {
      btn.disabled = false;
      showToast("Could not add item");
      return;
    }
    if (!state.registry.items) state.registry.items = [];

    // WHAT YOU SEE IS WHAT YOU ADD. The tile is bound to ONE variant (Liquid's
    // selected_or_first_available_variant) and renders THAT variant's photo and
    // price, so the add registers that same variant. Omitting it for
    // multi-variant products (the old rule) never avoided the guess — the server
    // resolves its own deterministic fallback either way — it only made the
    // guess invisible, so the tile could show one colourway and register
    // another. The registrant can change the option in the product sheet's
    // picker, which is the surface that exists for exactly that.
    const tileVariantId = card.dataset.variantId || null;

    // Optimistic insert: build a provisional row from the card, drop it into
    // local state, and re-render so it shows in "Currently in your registry"
    // instantly. The API call runs below; on failure we pull it back out.
    const tmpId = provisionalId();
    const provItem = {
      id: tmpId,
      status: "active",
      source: "shopify",
      shopifyProductId: card.dataset.productId || null,
      shopifyVariantId: tileVariantId,
      shopifyHandle: card.dataset.productHandle || null,
      title: card.dataset.productTitle || "",
      imageUrl: card.dataset.productImage || null,
      priceCents: parseInt(card.dataset.priceCents, 10) || null,
      currency: card.dataset.currency || "USD",
      stage: window.BirchRegistry.classifyStage?.({
        title: card.dataset.productTitle,
        handle: card.dataset.productHandle,
        tags: card.dataset.productTags || "",
      }),
      priority: "nice_to_have",
      quantityWanted: 1,
      quantityPurchased: 0,
    };
    // Stamp the macro-category + subcategory of the card we're adding from so the
    // item classifies correctly even after its tile is re-rendered away (#1).
    stampOrigin(provItem, card);
    state.registry.items.push(provItem);
    if (provItem.shopifyProductId) state.itemByProductId[String(provItem.shopifyProductId)] = provItem;
    if (provItem.shopifyHandle) state.itemByHandle[String(provItem.shopifyHandle)] = provItem;
    setAdded(btn, card, tmpId);
    // renderInjectedItems writes state.aiInjectedCounts — must run first
    // so updateCategoryCounts reads the fresh number.
    renderInjectedItems();
    updateCategoryCounts();
    renderCategoryAlternates(); // drop the just-added item from the alternates row
    renderRightSidebar(state.registry);
    btn.disabled = false;

    const payload = {
      source: "shopify",
      shopifyProductId: card.dataset.productId,
      shopifyVariantId: tileVariantId,
      shopifyHandle: card.dataset.productHandle,
      title: card.dataset.productTitle,
      imageUrl: card.dataset.productImage,
      priceCents: parseInt(card.dataset.priceCents, 10) || null,
      currency: card.dataset.currency || "USD",
      priority: "nice_to_have",
      quantityWanted: 1,
    };
    // Persist the origin subcategory as productType when the card has no canonical
    // type of its own, so the item classifies correctly on a later page load too.
    if (provItem.productType) payload.productType = provItem.productType;
    try {
      const { item, merged } = await API.addItem(registryId, payload);
      reconcileProvisional(tmpId, item);
      // reconcileProvisional swaps in the server item (which has no in-memory
      // origin hint) — re-stamp it so this session's classification stays correct
      // even if the server didn't echo back a usable category.
      stampOrigin(lookupItemById(item.id) || item, card);
      // Rebind tiles/controls to the real id and reflect the server's canonical
      // category + quantity.
      markAddedProducts();
      const live = card.querySelector("[data-rcp-add]");
      if (live && live.dataset.registryItemId === tmpId) setAdded(live, card, item.id);
      renderInjectedItems();
      updateCategoryCounts();
      renderRightSidebar(state.registry);
      showToast(merged ? `Quantity increased to ${item.quantityWanted}` : "Added to registry");
    } catch (_e) {
      // Revert the optimistic insert and reset the tile.
      dropProvisional(tmpId);
      const live = card.querySelector("[data-rcp-add]");
      if (live) setUnadded(live, card);
      renderInjectedItems();
      updateCategoryCounts();
      renderCategoryAlternates();
      showToast("Something went wrong — couldn't add to your registry. Please try again.");
    }
  }

  // Add a single previewed preset item to the real registry (cherry-pick).
  // Builds the payload from the button's data-attrs so it works for BOTH a Birch
  // (shopify) and an external preset item, then mirrors it into local state so it
  // shows once the user taps "Back to my registry".
  async function handleAddPreviewItem(btn) {
    const { API, showToast } = window.BirchRegistry;
    if (btn.classList.contains("re-preview-add--added")) return;
    const origText = btn.textContent;
    btn.disabled = true;
    const d = btn.dataset;
    const cents = d.cents ? parseInt(d.cents, 10) : undefined;
    const common = {
      title: d.title || "",
      imageUrl: d.img || undefined,
      priceCents: Number.isFinite(cents) ? cents : undefined,
      currency: "USD",
      productType: d.ptype || undefined,
      stage: d.stage || undefined,
      priority: "nice_to_have",
      quantityWanted: 1,
    };
    const payload = d.src === "external"
      ? { source: "external", externalUrl: d.url || undefined, externalRetailer: d.retailer || undefined, lushlifeProductId: d.llid || undefined, ...common }
      : { source: "shopify", shopifyProductId: d.pid || undefined, shopifyVariantId: d.vid || undefined, shopifyHandle: d.handle || undefined, ...common };

    // Need a registry before we can render the item into "Currently in your
    // registry". An existing one is used as-is; the first add awaits the create.
    let registryId;
    try {
      registryId = state.registry ? state.registry.id : await getOrCreateRegistry();
    } catch (_e) {
      btn.disabled = false;
      showToast("Could not add item");
      return;
    }
    if (!state.registry.items) state.registry.items = [];

    // Optimistic insert: drop a provisional row into local state and re-render
    // so it shows in "Currently in your registry" instantly. Reconciled with the
    // server's real item below, or pulled back out on failure.
    const tmpId = provisionalId();
    const provItem = { id: tmpId, status: "active", quantityPurchased: 0, ...payload };
    state.registry.items.push(provItem);
    if (provItem.shopifyProductId) state.itemByProductId[String(provItem.shopifyProductId)] = provItem;
    if (provItem.shopifyHandle) state.itemByHandle[String(provItem.shopifyHandle)] = provItem;
    // Reflect the add live (no reload, issue 2): the re-render below replaces this
    // preset row with a working "✕ Remove" pill and surfaces the item in "Currently
    // in your registry". The clicked button stays disabled (set above) until the
    // re-render swaps it. markAddedProducts keeps any matching browse tile in sync.
    markAddedProducts();
    renderInjectedItems();
    updateCategoryCounts();
    renderRightSidebar(state.registry);

    try {
      const { item, merged } = await API.addItem(registryId, payload);
      reconcileProvisional(tmpId, item);
      markAddedProducts();
      renderInjectedItems();
      updateCategoryCounts();
      renderRightSidebar(state.registry);
      showToast(merged ? "Already in your registry" : "Added to your registry");
    } catch (_e) {
      // Revert the optimistic insert and reset the preset row.
      dropProvisional(tmpId);
      btn.classList.remove("re-preview-add--added");
      btn.textContent = origText;
      btn.disabled = false;
      markAddedProducts();
      renderInjectedItems();
      updateCategoryCounts();
      renderRightSidebar(state.registry);
      showToast("Something went wrong — couldn't add to your registry. Please try again.");
    }
  }

  function itemHasUnresolvedFunds(it) {
    if (!it) return false;
    const funded = Number(it.groupGiftFunded || 0);
    return funded > 0 && !it.groupGiftResolvedAt && !it.groupGiftResolutionId;
  }

  // A pure contribution vehicle — the Birch Gift Card. Friends contribute any
  // amount into its group-gift pool, so unlike a normal product it must stay on
  // the registry once ANY money has been contributed (removing it would orphan
  // the contributions) — even after the pool is otherwise "resolved", which is
  // why this is stricter than itemHasUnresolvedFunds.
  function isContributionItem(it) {
    if (!it) return false;
    const pt = String(it.productType || "").toLowerCase();
    const handle = String(it.shopifyHandle || it.handle || "");
    return pt === "gift card"
      || handle === "birch-gift-card"
      || /\/products\/birch-gift-card(?:[/?#]|$)/.test(String(it.externalUrl || ""));
  }

  function itemHasContributions(it) {
    if (!it) return false;
    if (Number(it.groupGiftFunded || 0) > 0) return true;
    if (Number(it.contributionsCount || 0) > 0) return true;
    return Array.isArray(it.contributions) && it.contributions.length > 0;
  }

  // Mirror of the backend guardDelete: any completed purchase (or contribution to
  // a contribution item) locks the row so RegistryPurchase / contribution records
  // aren't orphaned. Surface a friendly toast here rather than hitting the 409.
  function itemRemovalBlockReason(it) {
    if (!it) return null;
    if (isContributionItem(it) && itemHasContributions(it)) {
      return "Friends have already contributed to this — it can't be removed.";
    }
    if (itemHasUnresolvedFunds(it)) {
      return "Resolve the contribution pool first (Top up to buy, or Convert to Birch credit).";
    }
    const purchased = Number(it.quantityPurchased || 0);
    if (purchased > 0) {
      const wanted = Number(it.quantityWanted || 1);
      return purchased >= wanted
        ? "This item has already been fully gifted — it can't be removed."
        : `${purchased} of ${wanted} have already been gifted — drop the quantity instead.`;
    }
    return null;
  }

  async function handleRemove(btn, card) {
    if (!state.registry) return;
    const { API, showToast } = window.BirchRegistry;
    const itemId = btn.dataset.registryItemId;
    if (!itemId) return;
    const item = lookupItemById(itemId);
    const blockedReason = itemRemovalBlockReason(item);
    if (blockedReason) { showToast(blockedReason); return; }
    if (!confirm("Remove this item from your registry?")) return;
    btn.disabled = true;
    try {
      await API.removeItem(state.registry.id, itemId);
      state.registry.items = state.registry.items.filter((it) => it.id !== itemId);
      if (card.dataset.productId) delete state.itemByProductId[card.dataset.productId];
      if (card.dataset.productHandle) delete state.itemByHandle[card.dataset.productHandle];
      setUnadded(btn, card);
      renderInjectedItems();
      updateCategoryCounts();
      renderCategoryAlternates(); // a removed item may become eligible as an alternate again
      showToast("Removed from registry");
    } catch (e) {
      const msg = e?.body?.message || (e?.status === 409 ? "Can't remove — see contributions." : "Could not remove");
      showToast(msg);
    } finally {
      btn.disabled = false;
    }
  }

  async function handleRemoveById(itemId) {
    if (!state.registry) return;
    const { API, showToast } = window.BirchRegistry;
    const item = lookupItemById(itemId);
    const blockedReason = itemRemovalBlockReason(item);
    if (blockedReason) { showToast(blockedReason); return; }
    if (!confirm("Remove this item from your registry?")) return;
    try {
      await API.removeItem(state.registry.id, itemId);
      state.registry.items = state.registry.items.filter((it) => it.id !== itemId);
      const pid = Object.keys(state.itemByProductId).find((k) => state.itemByProductId[k].id === itemId);
      if (pid) {
        const card = document.querySelector(`.rcp-pgrid-card[data-product-id="${CSS.escape(pid)}"]`);
        const btn = card?.querySelector(".rcp-pgrid-add-btn--added");
        if (btn) setUnadded(btn, card);
        delete state.itemByProductId[pid];
      }
      const handle = Object.keys(state.itemByHandle).find((k) => state.itemByHandle[k].id === itemId);
      if (handle) {
        const card = document.querySelector(`.rcp-pgrid-card[data-product-handle="${CSS.escape(handle)}"]`);
        const btn = card?.querySelector(".rcp-pgrid-add-btn--added");
        if (btn) setUnadded(btn, card);
        delete state.itemByHandle[handle];
      }
      renderInjectedItems();
      updateCategoryCounts();
      renderCategoryAlternates(); // refresh subcat rows: the removed pick flips back to "Add"
      showToast("Removed from registry");
    } catch (e) {
      const msg = e?.body?.message || (e?.status === 409 ? "Can't remove — see contributions." : "Could not remove");
      showToast(msg);
    }
  }

  // ── Classify items into category panels ───────────────────────────────────
  // ONE classifier, used by EVERY path that puts an item on the page (AI add,
  // paste-a-link, Chrome extension, copy-registry import, and preset preview).
  // Fail-proof ordering:
  //   1. AUTHORITATIVE — the item's canonical category (productType) maps
  //      EXACTLY to an icon. AI/build/preset items carry a canonical Birch
  //      category here (e.g. "Sleep sacks & swaddles"), so they bucket
  //      deterministically with zero title guessing.
  //   2. KEYWORD — for scraped/imported items with no canonical productType,
  //      match title+productType against keyword PHRASES, MOST-SPECIFIC FIRST
  //      (multi-word before single-word) with word-boundary matching. This is
  //      what kills the collisions: "Swaddle Wraps" → clothes (gear no longer
  //      claims a bare "wrap"); "diaper bag" → gear before "diaper"; "Bedside
  //      Sleeper" → nursery before clothes' "sleeper".
  //   3. STAGE — last-resort fallback for brand-only titles.
  // Keep the keyword list roughly in sync with app/lib/classifyStage.js.

  // Canonical category (productType) → icon. Keys are the exact Birch / preset
  // category names the server stores. Extended at runtime with any merchant
  // product-type mappings declared on the nav panels (data-cat-types).
  const CANONICAL_CATEGORY_ICON = {
    // nursery — sleep furniture + room
    "cribs & bassinets": "nursery", "bassinet": "nursery", "crib mattress": "nursery",
    "crib sheets": "nursery", "white noise machine": "nursery", "sound machine": "nursery",
    "bouncers & rockers": "nursery",
    // clothes — apparel + sleepwear (swaddles/sleep sacks live here, not nursery)
    "sleep sacks & swaddles": "clothes", "bodysuits & onesies": "clothes",
    "baby sleepers": "clothes", "baby hats": "clothes", "baby socks": "clothes",
    // Shopify productType on Birch sleepwear ("Bamboo Blend Pajamas"). The
    // keyword matcher is word-boundary based, so the bare plural must be an
    // exact-map entry or it never matches the singular "pajama" keyword.
    "pajamas": "clothes", "pyjamas": "clothes",
    // gear — travel
    "strollers": "gear", "car seats": "gear", "baby carriers": "gear",
    // feeding
    "baby bottles": "feeding", "breast pumps": "feeding", "baby formula": "feeding",
    "nursing pillow": "feeding", "burp cloths": "feeding", "bibs": "feeding",
    "pacifiers": "feeding", "teethers": "feeding",
    // diapering
    "diapers": "diaper", "baby wipes": "diaper", "diaper rash cream": "diaper",
    "changing pad": "diaper",
    // bath & grooming
    "bath tub": "grooming", "bath towels": "grooming", "baby wash & shampoo": "grooming",
    "baby skincare": "grooming", "thermometer": "grooming", "nail care": "grooming",
    // tech
    "baby monitors": "tech",
    // toys
    "baby toys": "toys", "toys": "toys", "teething toys": "toys",
    "play gym & activity mat": "toys", "books": "toys",
    // parent & recovery — mom/parent-focused AI categories (see CATEGORY-MAP.md, D1)
    "postpartum recovery essentials": "parent", "postpartum recovery": "parent",
    "nursing comfort": "parent", "hydration & snack support": "parent",
    // …plus the current catalog/AI product_types the postpartum section actually
    // emits (these were unmapped → items fell out of the preset preview count).
    "maternity underwear": "parent", "maternity bras": "parent",
    "maternity body pillow": "parent", "nursing pads": "parent",
    "nursing & lactation care": "parent", "cooling gel packs": "parent",
    "perineal massage": "parent",
    // Nursing Comfort row leaves added 2026-09-26 (covers, nipple shields, bras).
    "nursing cover": "parent", "nursing covers": "parent", "multi-use cover": "parent",
    "nipple shield": "parent", "nipple shields": "parent",
    "nursing bra": "parent", "nursing bras": "parent",
    // Skin Care row gained insect repellent 2026-09-26 — without this the bare
    // "wipes" keyword files "Insect Repellent Wipes" under Diaper.
    "insect repellent": "grooming", "insect repellent wipes": "grooming",
    // health device (lives with the grooming/health panel, alongside thermometer
    // / nasal aspirator / first-aid).
    "humidifier": "grooming",
    // loose blankets (a plain receiving/cozy blanket — distinct from a wearable
    // blanket, which already keyword-maps to clothes).
    "blankets": "clothes", "blanket": "clothes",
  };

  // De-collided keyword phrases per icon. AMBIGUOUS bare words removed: gear no
  // longer claims "wrap" (a "swaddle wrap" is sleepwear) — only explicit carrier
  // phrases. Multi-word phrases are matched before single words (see below), so
  // a generic word never steals a more-specific match.
  const ICON_KEYWORDS = [
    { icon: "tech", kws: ["baby monitor", "video monitor", "audio monitor", "monitor", "camera"] },
    { icon: "diaper", kws: ["diaper rash cream", "diaper pail", "diaper caddy", "baby wipes", "changing pad", "changing mat", "diapers", "diaper", "wipes", "wipe", "diapering"] },
    { icon: "feeding", kws: ["breast pump", "bottle warmer", "milk storage", "nursing pillow", "drying rack", "baby bottle", "high chair", "burp cloth", "burp cloths", "bottle", "pump", "formula", "nursing", "bib", "bibs", "pacifier", "teether", "sterilizer", "burp"] },
    { icon: "grooming", kws: ["insect repellent", "bug spray", "nasal aspirator", "nose sucker", "snot sucker", "first aid kit", "first aid", "baby wash", "body wash", "baby shampoo", "nail clipper", "nail care", "bath tub", "bath towel", "grooming kit", "health kit", "medicine", "aspirator", "booger", "shampoo", "wash", "lotion", "cream", "balm", "oil", "skincare", "brush", "comb", "nail", "thermometer", "soap", "towel"] },
    { icon: "gear", kws: ["travel system", "infant car seat", "car seat", "baby carrier", "wrap carrier", "baby wrap", "woven wrap", "ring sling", "diaper bag", "stroller", "carseat", "pram", "pushchair", "carrier", "sling"] },
    { icon: "clothes", kws: ["sleep sack", "sleeping bag", "wearable blanket", "muslin swaddle", "footed sleeper", "gift set", "baby hat", "bodysuit", "onesie", "swaddle", "sleeper", "pajamas", "pajama", "pyjamas", "pyjama", "romper", "cocoon", "mittens", "beanie", "hat", "socks", "sock", "outfit", "footie", "sleepsack", "pants", "shirt"] },
    { icon: "toys", kws: ["activity gym", "play gym", "play mat", "playmat", "tummy time", "board book", "stuffed animal", "shape sorter", "wooden toy", "teething toy", "stacking", "stacker", "rattle", "plush", "puzzle", "musical", "sensory", "lovey", "doll", "book", "toy", "blocks", "stuffed"] },
    { icon: "nursery", kws: ["bedside sleeper", "convertible crib", "mini crib", "travel crib", "crib mattress", "crib sheet", "fitted sheet", "co-sleeper", "co sleeper", "moses basket", "sound machine", "white noise", "night light", "changing table", "crib", "bassinet", "cradle", "mattress", "sheet", "nightlight", "mobile", "blackout", "rocker", "glider", "bouncer", "swing", "nursery", "dresser"] },
    // parent & recovery — multi-word phrases beat feeding's bare "nursing" via the specificity sort.
    { icon: "parent", kws: ["postpartum recovery", "postpartum", "perineal", "sitz bath", "nursing pads", "nursing cover", "nursing bra", "nipple shield", "lactation", "nipple cream", "belly band", "padsicle", "electrolyte", "snack support", "hydration"] },
  ];

  // Stage → icon fallback for stages that map cleanly to ONE category panel.
  // Used when neither product_type nor title keywords resolve a category, so an
  // item never falls to Others just because its title is generic ("Lovevery
  // Play Kit", a brand-only scraped title from the Chrome extension, etc.).
  // Stages that span categories (sleep → sleep-sacks=clothes AND bassinet=
  // nursery; before_baby/newborn/first_week/three_to_six/consider_later) are
  // intentionally NOT mapped — product_type/title is more precise there.
  const STAGE_TO_ICON = { play: "toys", on_the_go: "gear", bath: "grooming", feeding: "feeding" };

  // Reverse of CANONICAL_CATEGORY_ICON: a representative canonical productType per
  // category icon. When the registrant picks a Category in the add-by-link review
  // popup, we stamp this productType on the item so the choice SURVIVES a page
  // reload (categoryIcon is in-memory only; productType is persisted server-side
  // and re-classifies to the same section). Covers every placeable icon in the
  // browse_category "Icon" vocabulary except giftcard (not a paste target), plus
  // the catch-all "other" — the sentinel productType "other" that
  // classifyItemToIcon short-circuits back to the Other bucket.
  const ICON_TO_CANONICAL_TYPE = {
    nursery: "cribs & bassinets",
    clothes: "bodysuits & onesies",
    gear: "strollers",
    feeding: "baby bottles",
    diaper: "diapers",
    grooming: "baby skincare",
    tech: "baby monitors",
    toys: "baby toys",
    parent: "postpartum recovery essentials",
    other: "other",
  };

  const _escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // FUTURE-PROOFING: the nav panels (browse_category blocks) remain the source
  // of truth for which buckets exist. Each panel may still declare extra title
  // keywords (data-cat-keywords) and product types (data-cat-types) in the
  // theme editor; we MERGE those onto the built-ins. So a new nav category, or a
  // new product type a panel lists, just works — and the built-ins guarantee a
  // sensible default even when (as on most stores) the panels declare nothing.
  let _classifierCache = null;
  function getClassifier() {
    if (_classifierCache) return _classifierCache;
    const parseList = (s) => (s || "").split(/[,\n]/).map((x) => x.trim().toLowerCase()).filter(Boolean);
    const panelIcons = new Set();
    const exactMap = new Map(Object.entries(CANONICAL_CATEGORY_ICON));
    // Flat keyword list: { kw, icon, words } — built-ins + merchant keywords.
    const flat = [];
    ICON_KEYWORDS.forEach((d, order) => d.kws.forEach((kw) => flat.push({ kw, icon: d.icon, order })));
    const iconOrder = new Map(ICON_KEYWORDS.map((d, i) => [d.icon, i]));

    document.querySelectorAll("[data-cat-panel][data-cat-icon]").forEach((p) => {
      const icon = p.getAttribute("data-cat-icon");
      if (!icon) return;
      panelIcons.add(icon);
      // Merchant product-type mappings are authoritative (exact match).
      for (const t of parseList(p.getAttribute("data-cat-types"))) exactMap.set(t, icon);
      // Merchant keywords join the phrase matcher.
      const ord = iconOrder.has(icon) ? iconOrder.get(icon) : ICON_KEYWORDS.length;
      for (const kw of parseList(p.getAttribute("data-cat-keywords"))) flat.push({ kw, icon, order: ord });
    });

    // Every subcategory row label is itself an authoritative type→icon hint: an
    // item whose stamped productType IS a row label ("Sound Machines") belongs to
    // that row's macro-category, even when its title carries no keyword. Lets the
    // origin stamp survive a page reload (categoryIcon is in-memory only, but the
    // sub-label we persist as productType re-classifies the same way).
    document.querySelectorAll(".rcp-subcat-row[data-feed-sub]").forEach((row) => {
      const sub = (row.getAttribute("data-feed-sub") || "").trim().toLowerCase();
      const panel = row.closest("[data-cat-panel][data-cat-icon]");
      const icon = panel && panel.getAttribute("data-cat-icon");
      if (sub && icon && !exactMap.has(sub)) exactMap.set(sub, icon);
    });

    // Most-specific first: more words wins, then longer string, then the
    // built-in icon precedence. This makes a generic word ("diaper", "sleeper",
    // "wrap"-free) never beat a specific phrase ("diaper bag", "bedside
    // sleeper").
    const wc = (s) => s.split(/\s+/).length;
    flat.sort((a, b) => wc(b.kw) - wc(a.kw) || b.kw.length - a.kw.length || a.order - b.order);
    const kwRules = flat.map(({ kw, icon }) => ({ icon, re: new RegExp("\\b" + _escapeRe(kw) + "\\b", "i") }));

    _classifierCache = { exactMap, kwRules, panelIcons };
    return _classifierCache;
  }

  // True when no panels exist yet (defensive) or the icon has a live panel.
  function _iconUsable(icon, panelIcons) {
    return !!icon && (panelIcons.size === 0 || panelIcons.has(icon));
  }

  function classifyItemToIcon(item) {
    const { exactMap, kwRules, panelIcons } = getClassifier();
    // Explicit "Other" override (registrant filed the item under Other in the
    // add-by-link review popup): keep it in the catch-all bucket instead of
    // re-guessing from its title keywords. Two forms carry the choice: the
    // in-memory categoryIcon (this session) and the persisted "other" sentinel
    // productType (durable across reloads, checked before keyword matching).
    if (item.categoryIcon === "other") return null;
    if ((item.productType || "").toLowerCase().trim() === "other") return null;
    // 0. ORIGIN HINT (most reliable): the macro-category of the card the item was
    //    added from, stamped at add-time. Survives feed re-renders that drop the
    //    originating tile (external LushLife cards are removed on every alternates
    //    pass), so a brand/model-only title like "Dreamegg Sunrise" no longer
    //    falls through to the keyword guess below and lands in "Other".
    if (item.categoryIcon && _iconUsable(item.categoryIcon, panelIcons)) return item.categoryIcon;
    const pt = (item.productType || item.category || "").toLowerCase().trim();
    // 1. AUTHORITATIVE: canonical productType → icon, exact match. AI/build/
    //    preset items carry a canonical Birch category, so they never reach the
    //    keyword guessing below.
    if (pt && exactMap.has(pt) && _iconUsable(exactMap.get(pt), panelIcons)) return exactMap.get(pt);
    // 2. KEYWORD: most-specific phrase first, word-boundary matched against
    //    productType + title.
    const hay = (pt + " " + (item.title || "")).toLowerCase();
    if (hay.trim()) {
      for (const { icon, re } of kwRules) {
        if (_iconUsable(icon, panelIcons) && re.test(hay)) return icon;
      }
    }
    // 3. Stage fallback, only when that icon actually has a panel.
    const stageIcon = STAGE_TO_ICON[item.stage];
    if (stageIcon && _iconUsable(stageIcon, panelIcons)) return stageIcon;
    return null;
  }

  // Plus glyph that prefixes the carousel "Add" pill (matches the server-rendered
  // category tiles + registry-browse.js so every Add button reads identically).
  const ADD_PLUS_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>`;
  const REMOVE_X_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;
  const CHECK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`;

  function renderItemCard(it, esc, opts = {}) {
    // All three registry sections render the same carousel-style product card
    // (.rcp-pgrid-card) so they read identically to the category rows. Clicking
    // the title or image opens the in-page product-detail popup (trust score,
    // key features, pros/cons, specs) instead of navigating to the PDP — keeps
    // registrants on the edit page. data-open-detail is handled by a single
    // delegated listener (wireDetailOpeners) so it works across every grid.
    // Brand → carousel "vendor" line: show the product BRAND (e.g. "Nuna"),
    // never the marketplace hostname. Mirrors the category cards (brand, then the
    // external retailer for non-Birch items). No "Birch pick" fallback. The div
    // ALWAYS renders — a non-breaking space when no brand is known — so cards keep
    // a consistent height whether or not a brand is available.
    const brandLabel = it.brand || it.externalRetailer || "";
    const vendorHtml = `<div class="rcp-pgrid-card__vendor">${esc(brandLabel) || "&nbsp;"}</div>`;
    const imgHtml = it.imageUrl
      ? `<img src="${esc(it.imageUrl)}" alt="" class="rcp-pgrid-card__img" loading="lazy">`
      : `<div class="rcp-pgrid-card__img-placeholder" aria-hidden="true">🌿</div>`;
    // Preset preview ("In this preset"): a curated starting point — per-item
    // "Add" so the parent can cherry-pick (the banner offers "Add all"). No
    // detail popup / remove control. The Add button carries the full payload via
    // data-attrs so the delegated handler can persist a Birch or external item.
    if (opts.preview) {
      const priceCents = typeof it.priceCents === "number" ? it.priceCents : null;
      const priceText = priceCents != null
        ? `$${(priceCents / 100).toFixed(2)}`
        : (it.price ? `$${Number(it.price).toFixed(2)}` : "");
      // An already-added preset item carries a WORKING "✕ Remove" pill in the
      // footer — the same affordance as the suggestion/browse cards — so it can be
      // pulled straight back off, never a dead "Added ✓". The remove maps to the
      // real registry item when on the registry, or dismisses the pending
      // Review-Box suggestion when it's matched only there. Gifted/locked items
      // (the server would reject removal) keep a non-interactive "Gifted" chip.
      const added = !!opts.alreadyAdded;
      const addedItem = added ? registryItemFor(it) : null;
      const addedId = addedItem ? addedItem.id : null;
      const suggId = added && !addedId ? suggestionIdFor(it) : null;
      const previewBlocked = addedItem ? !!itemRemovalBlockReason(addedItem) : false;
      // Treat as "added" only when we can actually act on it; an unresolvable
      // match falls back to a working Add rather than a dead remove.
      const isAddedActionable = added && (addedId || suggId || previewBlocked);
      const removeBtn = ""; // remove lives in the footer for preview cards (carousel parity)
      let addBtn;
      if (!isAddedActionable) {
        addBtn = `<button type="button" class="rcp-pgrid-add-btn re-preview-add" data-preview-add
                data-src="${esc(it.source || "")}"
                data-handle="${esc(it.shopifyHandle || "")}"
                data-pid="${esc(it.shopifyProductId || "")}"
                data-vid="${esc(it.shopifyVariantId || "")}"
                data-url="${esc(it.externalUrl || "")}"
                data-retailer="${esc(it.externalRetailer || "")}"
                data-llid="${esc(it.lushlifeProductId || "")}"
                data-title="${esc(it.title || "")}"
                data-img="${esc(it.imageUrl || "")}"
                data-cents="${priceCents != null ? priceCents : ""}"
                data-ptype="${esc(it.productType || "")}"
                data-stage="${esc(it.stage || "")}">${ADD_PLUS_SVG} Add</button>`;
      } else if (previewBlocked) {
        addBtn = `<span class="rcp-pgrid-add-btn rcp-pgrid-add-btn--added rcp-pgrid-add-btn--locked" aria-label="${esc(it.title || "")} — already gifted, cannot remove">${CHECK_SVG} Gifted</span>`;
      } else {
        const removeAttr = addedId ? `data-remove-item="${esc(addedId)}"` : `data-sugg-remove="${esc(suggId)}"`;
        addBtn = `<button type="button" class="rcp-pgrid-add-btn rcp-pgrid-add-btn--added" ${removeAttr} aria-label="Remove ${esc(it.title || "")} from registry">${REMOVE_X_SVG} Remove</button>`;
      }
      // Same affordance line as the suggestion/browse cards: Find similar
      // (title-based AI query), an optional "Why this?" flip when the preset item
      // carries a reason, and the PDP-parity trust ring. Each self-omits when its
      // data is absent — external preset items have no trustScore, so the ring
      // simply doesn't render for them (Birch items do).
      const hasWhy = !!it.reasonText;
      const whyId = esc(String(it.shopifyProductId || it.lushlifeProductId || it.shopifyHandle || it.title || ""));
      const findSimilarBtn = `<button type="button" class="rcp-sugg-icon rcp-sugg-findsimilar" data-find-similar="${esc(it.title || "")}"${findSimilarAttrs(it, esc)} title="Find a similar option" aria-label="Find a similar option to ${esc(it.title || "this")}">${FIND_SIMILAR_SVG}</button>`;
      const whyBtn = hasWhy
        ? `<button type="button" class="rcp-sugg-why-btn" data-flip-open="${whyId}" aria-label="Why this pick?"><span class="rcp-sugg-why-btn__icon" aria-hidden="true">✨</span>Why this?</button>`
        : "";
      const actionsRow = `<div class="rcp-sugg-actions-row">${findSimilarBtn}${whyBtn}${trustChipHtml(it.trustScore)}</div>`;
      // Open the product-detail popup on image/title click, like every other card.
      // Preview items aren't registry items (no it.id), so they carry an inline
      // data-detail-* descriptor on the card root (the browse-card path) instead of
      // data-open-detail. The backfilled lushlifeProductId lets the popup fetch real
      // LushLife detail (key features, pros/cons) for external items.
      const ext = it.source === "external";
      const detailAttrs =
        `data-detail-source="${ext ? "external" : "shopify"}" ` +
        `data-detail-title="${esc(it.title || "")}" ` +
        `data-detail-image="${esc(it.imageUrl || "")}" ` +
        `data-detail-price-cents="${priceCents != null ? priceCents : ""}" ` +
        `data-detail-currency="${esc(it.currency || "USD")}" ` +
        `data-detail-brand="${esc(it.brand || "")}" ` +
        `data-detail-handle="${ext ? "" : esc(it.shopifyHandle || "")}" ` +
        `data-detail-url="${ext ? esc(it.externalUrl || "") : ""}" ` +
        `data-detail-llid="${ext ? esc(it.lushlifeProductId || "") : ""}"` +
        (it.trustScore != null ? ` data-detail-trust="${esc(String(it.trustScore))}"` : "");
      const front = `
          <div class="rcp-pgrid-card__img-wrap">
            ${removeBtn}
            <button type="button" class="rcp-pgrid-card__img-btn" data-card-detail aria-label="View details for ${esc(it.title || "")}">${imgHtml}</button>
          </div>
          <div class="rcp-pgrid-card__body">
            ${vendorHtml}
            <button type="button" class="rcp-pgrid-card__title" data-card-detail>${esc(it.title || "")}</button>
            <div class="rcp-pgrid-card__footer">
              <span class="rcp-pgrid-card__price">${esc(priceText)}</span>
              ${addBtn}
            </div>
            ${actionsRow}
          </div>`;
      if (!hasWhy) {
        return `
        <div class="rcp-pgrid-card rcp-pgrid-card--preview re-added-card--preview" draggable="false" ${detailAttrs}>
          ${front}
        </div>`;
      }
      // Reason present → reuse the suggestion-card 3D "Why this?" flip (toggled by
      // the document-level delegated handlers via the nearest .re-flip-card).
      return `
        <div class="rcp-pgrid-card rcp-pgrid-card--preview re-added-card--preview re-flip-card" draggable="false" ${detailAttrs}>
          <div class="re-flip">
            <div class="re-flip__face re-flip__face--front">
              ${front}
            </div>
            <div class="re-flip__face re-flip__face--back" aria-hidden="true">
              <div class="rcp-why-back">
                <div class="rcp-why-back__head"><span class="rcp-why-back__spark" aria-hidden="true">✨</span>Why this pick</div>
                <p class="rcp-why-back__text">${esc(it.reasonText)}</p>
                <button type="button" class="rcp-why-back__close" data-flip-close="${whyId}"><span aria-hidden="true">←</span> Back to card</button>
              </div>
            </div>
          </div>
        </div>`;
    }
    // "Currently in your registry": carousel card + a top-left remove cross +
    // the quantity stepper (renderCardControls) in the footer instead of an Add
    // button. Always render the × so every card carries the remove control. When
    // removal is blocked (purchases or unresolved contributions) the button
    // stays visible but renders in a muted "locked" state; the click is
    // intercepted by handleRemoveById, which surfaces the block reason as a
    // toast instead of attempting a removal the server would reject.
    const fulfilled = it.decisionState === "fulfilled";
    const doneBadge = fulfilled
      ? `<span class="re-added-card__done-badge" title="Marked as done">Done</span>`
      : "";
    const removalBlocked = !!itemRemovalBlockReason(it);
    const removeLabel = removalBlocked ? "Already gifted — can't remove" : "Remove from registry";
    const removeBtn = `<button class="rcp-pgrid-card__remove${removalBlocked ? " rcp-pgrid-card__remove--locked" : ""}" data-remove-item="${esc(it.id)}" aria-label="${esc(removeLabel)}" title="${esc(removeLabel)}">
           <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
         </button>`;
    return `
      <div class="rcp-pgrid-card rcp-pgrid-card--registry ${fulfilled ? "rcp-pgrid-card--done" : ""}" data-card-item="${esc(it.id)}">
        <div class="rcp-pgrid-card__img-wrap">
          ${removeBtn}
          <button type="button" class="rcp-pgrid-card__img-btn" data-open-detail="${esc(it.id)}" aria-label="View details for ${esc(it.title || "")}">
            ${imgHtml}
            ${doneBadge}
          </button>
        </div>
        <div class="rcp-pgrid-card__body">
          ${vendorHtml}
          <button type="button" class="rcp-pgrid-card__title" data-open-detail="${esc(it.id)}">${esc(it.title || "")}</button>
          ${renderCardControls(it, esc)}
        </div>
      </div>`;
  }

  function renderCardControls(it, esc) {
    const id = esc(it.id);
    const fulfilled = it.decisionState === "fulfilled";
    const qtyWanted = Math.max(1, Number(it.quantityWanted || 1));
    const qtyPurchased = Number(it.quantityPurchased || 0);
    const qtyMin = Math.max(1, qtyPurchased);
    const fullyPurchased = qtyPurchased >= qtyWanted;
    const groupFunded = !!it.groupGiftFundedAt;
    const itemSettled = fullyPurchased || groupFunded;

    if (fulfilled) {
      // Read-only on the edit page — marking/undoing a purchase now lives on the
      // registry-progress page. Here we just reflect the settled state.
      return `
        <div class="re-card-ctrls re-card-ctrls--locked" data-controls-for="${id}">
          <div class="re-card-ctrls__status">Purchased</div>
        </div>`;
    }

    if (itemSettled) {
      const label = groupFunded ? "Group gift funded" : "Fully gifted";
      return `
        <div class="re-card-ctrls re-card-ctrls--locked" data-controls-for="${id}">
          <div class="re-card-ctrls__status">${label}</div>
        </div>`;
    }

    const qtyStatus = qtyPurchased > 0
      ? `<div class="re-card-ctrls__status-line">${qtyPurchased} of ${qtyWanted} gifted</div>`
      : "";

    // Price replaces the old "Qty" label. The separate price line above the
    // controls is removed in renderItemCard — keeping price next to the
    // stepper makes the row read as "this much × this many" at a glance.
    const priceCents = typeof it.priceCents === "number" ? it.priceCents : null;
    const priceText = priceCents != null
      ? `$${(priceCents / 100).toFixed(2)}`
      : (it.price ? `$${Number(it.price).toFixed(2)}` : "");

    const qtyRow = `
      <div class="re-card-ctrls__row">
        <span class="rcp-pgrid-card__price re-card-ctrls__price-inline">${esc(priceText)}</span>
        <div class="re-card-ctrls__stepper">
          <button type="button" class="re-card-ctrls__step" data-action="qty-dec" data-item-id="${id}" ${qtyWanted <= qtyMin ? "disabled" : ""} aria-label="Decrease quantity">–</button>
          <span class="re-card-ctrls__qty">${qtyWanted}</span>
          <button type="button" class="re-card-ctrls__step" data-action="qty-inc" data-item-id="${id}" aria-label="Increase quantity">+</button>
        </div>
      </div>`;

    // Variant switching moved INTO the product-detail popup (Registry Revamp #4)
    // — it now covers both Birch (Shopify) and external (toprack same-retailer)
    // items, so the per-card "Change variant" button is retired here. The popup's
    // "View details" CTA below the card is the single entry point.

    // Contribution (group-gift) management and "mark as purchased" now live on
    // the registry-progress page. The edit page stays focused on building the
    // registry — choosing items, quantities, and variants.
    return `
      <div class="re-card-ctrls" data-controls-for="${id}">
        ${qtyStatus}
        ${qtyRow}
      </div>`;
  }

  /**
   * For each registry item that doesn't match a catalog card, find the right
   * category panel by keyword classification and inject a card into a single
   * mixed grid inside that panel. Unclassifiable items go to "Other items".
   * Items added from AI flow are tagged AI in the card markup.
   */
  // Find the catalog tile for a registry item, if it's on the current page.
  // Module-scoped so both renderInjectedItems and renderSuggestions classify
  // items into panels identically.
  function cardSelectorFor(it) {
    if (it.shopifyProductId) {
      const c = document.querySelector(`.rcp-pgrid-card[data-product-id="${CSS.escape(String(it.shopifyProductId))}"]`);
      if (c) return c;
    }
    if (it.shopifyHandle) {
      const c = document.querySelector(`.rcp-pgrid-card[data-product-handle="${CSS.escape(String(it.shopifyHandle))}"]`);
      if (c) return c;
    }
    return null;
  }

  // AI-added items carry reasonType="why_we_suggested_it" — set by every AI
  // insertion path. Reliable across Birch + LushLife items, and across both
  // build_registry_draft and the per-item refinement tools.
  const isAiItem = (it) => it?.reasonType === "why_we_suggested_it";

  // Classify the REAL registry items into category panels. Two passes: (1)
  // catalog-tile matched (already marked "Added" on a browse tile), (2)
  // keyword classification for items without a tile. Dedupes across both so the
  // same item never renders twice. Returns the per-panel membership + the
  // unclassifiable remainder for the "Other items" panel.
  function partitionRegistryIntoPanels(items) {
    const allPanels = document.querySelectorAll("[data-cat-panel][data-cat-icon]");
    const panelMembership = new Map();
    const trulyOther = [];
    const seenItemIds = new Set();
    for (const panel of allPanels) {
      panel.querySelectorAll(".rcp-pgrid-add-btn--added").forEach((btn) => {
        const id = btn.dataset.registryItemId;
        if (!id || seenItemIds.has(id)) return;
        const it = lookupItemById(id);
        if (!it) return;
        seenItemIds.add(id);
        const arr = panelMembership.get(panel) || [];
        arr.push({ item: it, isAi: isAiItem(it) });
        panelMembership.set(panel, arr);
      });
    }
    const unmatched = items.filter((it) => !cardSelectorFor(it));
    const byIcon = {};
    for (const it of unmatched) {
      const icon = classifyItemToIcon(it);
      if (icon) (byIcon[icon] ??= []).push(it);
      else trulyOther.push(it);
    }
    for (const [icon, list] of Object.entries(byIcon)) {
      const panel = document.querySelector(`[data-cat-panel][data-cat-icon="${CSS.escape(icon)}"]`);
      const target = panel ? panelMembership.get(panel) || [] : null;
      for (const it of list) {
        const key = it.id || it.shopifyHandle || it.title;
        if (!key || seenItemIds.has(key)) continue;
        seenItemIds.add(key);
        if (target) target.push({ item: it, isAi: isAiItem(it) });
        else trulyOther.push(it);
      }
      if (panel && target) panelMembership.set(panel, target);
    }
    return { panelMembership, trulyOther };
  }

  // Classify preset PREVIEW items into panels (keyword only — preset items
  // aren't tiles on the page, so there's nothing to catalog-match).
  function partitionPreviewIntoPanels(items) {
    const panelMembership = new Map();
    const trulyOther = [];
    for (const it of items) {
      const icon = classifyItemToIcon(it);
      // No icon (unmapped product_type) OR a mapped icon with no live panel on
      // this store → route to the catch-all "Other" group rather than silently
      // dropping it from the preview (and from the "N in preset" count).
      const panel = icon ? document.querySelector(`[data-cat-panel][data-cat-icon="${CSS.escape(icon)}"]`) : null;
      if (!panel) { trulyOther.push(it); continue; }
      const arr = panelMembership.get(panel) || [];
      arr.push(it);
      panelMembership.set(panel, arr);
    }
    return { panelMembership, trulyOther };
  }

  // The "Currently in your registry" card. Always rendered on a browse category
  // (issue 11) so it stays visible (even empty, as a drop target) and never
  // disappears while a preset is being previewed (issue 2).
  function renderCurrentItemsCardHtml(members, esc, opts = {}) {
    const count = members.length;
    // In the All-items tab with no suggestions/preset there's nothing "below" to
    // tap Add on, so drop the hint and just say the box is empty (opts.hideHint).
    const emptyMsg = opts.hideHint
      ? `Nothing here yet.`
      : `Nothing here yet — tap <strong>Add</strong> on a product below, or drag one into this box.`;
    const body = count
      ? `<div class="re-added-cards">${members.map(({ item }) => renderItemCard(item, esc)).join("")}</div>`
      : `<div class="rcp-current-items__empty">${emptyMsg}</div>`;
    return `
      <div class="rcp-current-items" data-injected-grid="1" data-drop-zone>
        <div class="rcp-current-items__header">
          <h3 class="rcp-current-items__title">Currently in your registry</h3>
          <span class="rcp-current-items__count">${count} item${count === 1 ? "" : "s"}</span>
        </div>
        ${body}
      </div>`;
  }

  // The "In this preset" card — preview items with per-item "Add" (and "Added ✓"
  // for ones already on the registry, issue 3). Shown ABOVE the registry card.
  function renderPresetCardHtml(items, addedSet, esc) {
    const count = items.length;
    return `
      <div class="rcp-current-items rcp-preset-items" data-injected-grid="1">
        <div class="rcp-current-items__header">
          <h3 class="rcp-current-items__title">In this preset</h3>
          <span class="rcp-current-items__count">${count} item${count === 1 ? "" : "s"}</span>
        </div>
        <div class="re-added-cards">
          ${items.map((it) => renderItemCard(it, esc, {
            preview: true,
            alreadyAdded: addedSet ? isAlreadyAdded(it, addedSet) : false,
          })).join("")}
        </div>
      </div>`;
  }

  // Wire the remove (registry) + add (preview) controls within one injected slot.
  function wireInjectedSlot(slot) {
    slot.querySelectorAll("[data-remove-item]").forEach((b) => {
      b.addEventListener("click", (e) => {
        e.stopPropagation();
        handleRemoveById(b.dataset.removeItem);
      });
    });
    slot.querySelectorAll("[data-preview-add]").forEach((b) => {
      if (b.dataset.alreadyAdded) return; // already on the registry — inert
      b.addEventListener("click", (e) => {
        e.stopPropagation();
        handleAddPreviewItem(b);
      });
    });
  }

  // ── Registry Revamp: top "Currently in your registry" block w/ macro tabs ──
  // Replaces the per-category in-registry cards. One block, with a default "All"
  // tab + one tab per macro-category that has items (same labels as the left
  // bar). Cards reuse renderItemCard; filtering is CSS-class based (no re-fetch).
  function macroLabelForPanel(panel) {
    const h = panel.querySelector(".rcp-cat-header__title");
    return h ? h.textContent.trim() : (panel.dataset.catIcon || "");
  }

  function renderCurrentRegistryTabs(panelMembership, trulyOther, esc, preview, addedSet) {
    const host = document.getElementById("rcp-current-registry");
    if (!host) return;

    // Build macro groups in left-nav order. The gift-card panel IS included now —
    // the Birch Gift Card surfaces as its own group + tab here (editing its
    // denominations still lives in the dedicated Birch Card section). The previewed
    // preset is grouped by the SAME macro icon so the tabs cover preview + registry
    // as one.
    const groups = [];
    let total = 0;
    const presetByIcon = new Map();
    let presetTotal = 0;
    document.querySelectorAll("[data-cat-panel][data-cat-icon]").forEach((panel) => {
      const icon = panel.dataset.catIcon;
      if (!icon) return;
      const members = (panelMembership.get(panel) || []).map((m) => m.item);
      if (members.length) {
        groups.push({ icon, label: macroLabelForPanel(panel), items: members });
        total += members.length;
      }
      const pMembers = preview ? (preview.panelMembership.get(panel) || []) : [];
      if (pMembers.length) {
        presetByIcon.set(icon, { icon, label: macroLabelForPanel(panel), items: pMembers });
        presetTotal += pMembers.length;
      }
    });
    // Preset preview catch-all: items the classifier couldn't place in a live
    // panel surface in an "Other" group (and tab) instead of vanishing — so the
    // "N in preset" count always equals what "Add all" will insert.
    if (preview && preview.trulyOther && preview.trulyOther.length) {
      presetByIcon.set("other", { icon: "other", label: "Other", items: preview.trulyOther.slice() });
      presetTotal += preview.trulyOther.length;
    }
    if (trulyOther && trulyOther.length) {
      groups.push({ icon: "other", label: "Other", items: trulyOther.slice() });
      total += trulyOther.length;
    }
    const hasPreview = presetTotal > 0;

    // "Remove all suggestions" CTA — visible whenever AI suggestions are pending
    // (they surface, highlighted, in the carousels). #5.
    const hasSuggestions = (state.suggestions || []).length > 0;
    const removeAllBtn = document.getElementById("rcp-remove-all-sugg");
    if (removeAllBtn) removeAllBtn.hidden = !hasSuggestions;

    // The block stays visible even at 0 registry items — it shows a centered
    // placeholder for the registry and (while previewing) the preset above it.
    host.hidden = false;

    // Composite count on the "… — currently added" title: registry items, plus
    // suggested (when AI suggestions are pending) and preset (while previewing) —
    // an at-a-glance summary of everything the block is showing. Recreated if
    // applyRegistryNameTitles() wiped it via textContent.
    let regCountEl = document.getElementById("rcp-current-registry-count");
    const titleEl = document.querySelector(".rcp-current-registry__title");
    if (!regCountEl && titleEl) {
      regCountEl = document.createElement("span");
      regCountEl.id = "rcp-current-registry-count";
      regCountEl.className = "rcp-current-registry__count-badge";
      titleEl.appendChild(regCountEl);
    }
    if (regCountEl) {
      const suggTotal = (state.suggestions || []).length;
      const parts = [`${total} item${total === 1 ? "" : "s"}`];
      if (suggTotal > 0) parts.push(`${suggTotal} suggested`);
      if (presetTotal > 0) parts.push(`${presetTotal} in preset`);
      const show = total > 0 || suggTotal > 0 || presetTotal > 0;
      regCountEl.hidden = !show;
      regCountEl.textContent = show ? ` · ${parts.join(" · ")}` : "";
    }

    // ── Preset preview region (rendered ABOVE the registry grid) ──
    const presetWrap = document.getElementById("rcp-current-preset");
    const presetGrid = document.getElementById("rcp-current-preset-grid");
    if (presetGrid) {
      if (hasPreview) {
        const presetGroups = [];
        document.querySelectorAll("[data-cat-panel][data-cat-icon]").forEach((panel) => {
          const g = presetByIcon.get(panel.dataset.catIcon);
          if (g) presetGroups.push(g);
        });
        // "Other" has no panel of its own — append it last so unclassified preset
        // items still render (mirrors the registry grid's Other group).
        const presetOther = presetByIcon.get("other");
        if (presetOther) presetGroups.push(presetOther);
        // Mirror the registry grid: each macro is a labeled group box (Nursery /
        // Gear / …) so the preset reads as the SAME category structure, and the
        // top tabs can scroll-target the preset's per-macro group too.
        presetGrid.innerHTML = presetGroups
          .map((g) => {
            const cells = g.items
              .map((it) => `<div class="rcp-current-cell rcp-current-cell--preset" data-current-icon="${esc(g.icon)}">${renderItemCard(it, esc, { preview: true, alreadyAdded: addedSet ? isAlreadyAdded(it, addedSet) : false })}</div>`)
              .join("");
            return (
              `<div class="rcp-current-group" data-current-icon="${esc(g.icon)}">` +
                `<div class="rcp-current-group__head"><span class="rcp-current-group__label">${esc(g.label)}</span><span class="rcp-current-group__count">${g.items.length}</span></div>` +
                `<div class="rcp-current-group__inner">${cells}</div>` +
              `</div>`
            );
          })
          .join("");
        presetGrid.querySelectorAll("[data-preview-add]").forEach((b) => {
          if (b.dataset.alreadyAdded) return; // already on the registry — inert
          b.addEventListener("click", (e) => {
            e.stopPropagation();
            handleAddPreviewItem(b);
          });
        });
        // Already-added preview cards carry the registry remove × — wire it to
        // the same removal path as the registry grid below.
        presetGrid.querySelectorAll("[data-remove-item]").forEach((b) => {
          b.addEventListener("click", (e) => {
            e.stopPropagation();
            handleRemoveById(b.dataset.removeItem);
          });
        });
      } else {
        presetGrid.innerHTML = "";
      }
    }
    if (presetWrap) {
      presetWrap.dataset.active = hasPreview ? "1" : "";
      presetWrap.hidden = !hasPreview;
    }
    // Preset section header: "✦ In this preset — {name} · N items".
    const presetTitleEl = document.getElementById("rcp-current-preset-title");
    if (presetTitleEl && hasPreview) {
      const nm = state.activePresetName ? ` — ${esc(state.activePresetName)}` : "";
      presetTitleEl.innerHTML =
        `<span class="rcp-current-preset__icon" aria-hidden="true">✦</span>` +
        `<span class="rcp-current-preset__label">In this preset${nm}</span>` +
        `<span class="rcp-current-preset__count" id="rcp-current-preset-count">· ${presetTotal} item${presetTotal === 1 ? "" : "s"}</span>`;
    }

    const tabIcons = new Set([...groups.map((g) => g.icon), ...presetByIcon.keys()]);

    // Active cash funds aren't registry items — they surface as a single
    // navigational "Cash Funds" card/tab (sourced from state, populated by the
    // funds panel's renderFunds). Names only, no $ amounts → nothing to go stale.
    const activeFunds = (state.cashFunds || []).filter((f) => f && f.status === "active");

    // ── Tabs: union of registry + preset macros, in left-nav order. Counts track
    // the registry when it has items, else the preset, so the numbers always mean
    // something. The strip hides entirely when there's nothing to filter. ──
    const tabOrder = [];
    const seenTab = new Set();
    document.querySelectorAll("[data-cat-panel][data-cat-icon]").forEach((panel) => {
      const icon = panel.dataset.catIcon;
      if (!icon || seenTab.has(icon) || !tabIcons.has(icon)) return;
      tabOrder.push({ icon, label: macroLabelForPanel(panel) });
      seenTab.add(icon);
    });
    // Cash Funds tab sits after the macro categories (matches the left-nav order:
    // Birch Card → Funds), before the catch-all "Other".
    if (activeFunds.length && !seenTab.has("funds")) {
      tabOrder.push({ icon: "funds", label: "Cash Funds" });
      seenTab.add("funds");
    }
    if (tabIcons.has("other") && !seenTab.has("other")) tabOrder.push({ icon: "other", label: "Other" });

    // No "All" tab — default the active tab to the first real macro, and re-seat
    // it whenever the previously-active tab no longer exists in either set.
    if (!tabOrder.some((t) => t.icon === _curRegTab)) {
      _curRegTab = tabOrder.length ? tabOrder[0].icon : null;
    }

    // Count-approach A: tab badges ALWAYS reflect the real registry, so their
    // meaning never shifts when a preset is active. The preset's own total lives
    // in the preset section header below — so "5" reads as "5 in my registry",
    // not "5 of the 35 cards on screen". Preset-only macros (a tab that exists
    // only because the preset covers it) show no badge rather than a stale "0".
    const countFor = (icon) => icon === "funds" ? activeFunds.length : ((groups.find((g) => g.icon === icon)?.items.length) || 0);
    const badge = (n) => (n > 0 ? `<span class="rcp-current-tab__count">${n}</span>` : "");

    const tabBtn = (key, label, count, active) =>
      `<button type="button" role="tab" class="rcp-current-tab${active ? " is-active" : ""}" data-current-tab="${esc(key)}" aria-selected="${active ? "true" : "false"}">${esc(label)}${badge(count)}</button>`;
    const tabsEl = document.getElementById("rcp-current-tabs");
    if (tabsEl) {
      tabsEl.innerHTML = tabOrder.length
        ? tabOrder.map((t) => tabBtn(t.icon, t.label, countFor(t.icon), _curRegTab === t.icon)).join("")
        : "";
    }

    // ── Registry grid (or the centered, light placeholder when empty) ──
    const gridEl = document.getElementById("rcp-current-registry-grid");
    if (gridEl) {
      const groupsHtml = groups
        .map((g) => {
          const cells = g.items
            .map((it) => `<div class="rcp-current-cell" data-current-icon="${esc(g.icon)}">${renderItemCard(it, esc)}</div>`)
            .join("");
          const n = g.items.length;
          // Option-2 layout: cards stay neutral; the category signal lives on
          // the surrounding tinted box, so a macro reads as one group at a glance.
          return (
            `<div class="rcp-current-group" data-current-icon="${esc(g.icon)}">` +
              `<div class="rcp-current-group__head"><span class="rcp-current-group__label">${esc(g.label)}</span><span class="rcp-current-group__count">${n}</span></div>` +
              `<div class="rcp-current-group__inner">${cells}</div>` +
            `</div>`
          );
        })
        .join("");
      // Cash funds aren't registry items — append a single navigational card that
      // jumps to the real Cash Funds section (where fresh values load on open).
      const fundsHtml = renderFundsCard(activeFunds, esc);
      gridEl.innerHTML = (total > 0 || fundsHtml)
        ? groupsHtml + fundsHtml
        : `<p class="rcp-current-registry__empty rcp-current-registry__empty--center">No items currently in the registry</p>`;
      applyCurrentRegView();
      setupCurrentRegSpy();
      gridEl.querySelectorAll("[data-remove-item]").forEach((b) => {
        b.addEventListener("click", (e) => {
          e.stopPropagation();
          handleRemoveById(b.dataset.removeItem);
        });
      });
      const gotoFunds = gridEl.querySelector("[data-goto-funds]");
      if (gotoFunds) {
        gotoFunds.addEventListener("click", () => {
          const navBtn = document.querySelector('[data-cat-nav="rcp-cat-funds"]');
          if (navBtn) navBtn.click();
          else document.getElementById("rcp-cat-funds")?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    }
  }

  // The top-block "Cash Funds" card: a navigational tile listing active fund
  // NAMES (titles are reliable and, unlike raised-$ amounts, don't drift between
  // a snapshot and the live total) that takes the registrant to the real Cash
  // Funds section. Empty string when there are no active funds, so the funds
  // tab/group only appear when there's something to show.
  function renderFundsCard(activeFunds, esc) {
    if (!activeFunds || !activeFunds.length) return "";
    const names = activeFunds.map((f) => esc(f.name || "Cash fund"));
    const shown = names.slice(0, 3).join(" · ");
    const extra = names.length > 3 ? ` +${names.length - 3} more` : "";
    return (
      `<div class="rcp-current-group" data-current-icon="funds">` +
        `<div class="rcp-current-group__head"><span class="rcp-current-group__label">Cash Funds</span><span class="rcp-current-group__count">${activeFunds.length}</span></div>` +
        `<div class="rcp-current-group__inner">` +
          `<div class="rcp-current-cell rcp-current-cell--funds" data-current-icon="funds">` +
            `<button type="button" class="rcp-funds-card" data-goto-funds>` +
              `<span class="rcp-funds-card__icon" aria-hidden="true">💰</span>` +
              `<span class="rcp-funds-card__body">` +
                `<span class="rcp-funds-card__title">View Cash Funds</span>` +
                `<span class="rcp-funds-card__names">${shown}${extra}</span>` +
              `</span>` +
              `<span class="rcp-funds-card__arrow" aria-hidden="true">→</span>` +
            `</button>` +
          `</div>` +
        `</div>` +
      `</div>`
    );
  }

  // Scroll-spy for the "Currently in your registry" block (#1): the macro tabs
  // are NOT filters — every item stays in one continuous scroll (horizontal in
  // carousel mode, the page grid otherwise). As the user scrolls from one macro
  // category's items to the next, the matching tab highlights; clicking a tab
  // scrolls that category's items into view. Re-armed after every (re)render and
  // on view-mode change (the scroll root differs between grid and carousel).
  let _curRegSpyIO = null;
  function highlightCurrentTab(icon) {
    if (!icon) return;
    _curRegTab = icon;
    const tabsEl = document.getElementById("rcp-current-tabs");
    if (tabsEl) {
      tabsEl.querySelectorAll(".rcp-current-tab").forEach((t) => {
        const on = t.dataset.currentTab === icon;
        t.classList.toggle("is-active", on);
        t.setAttribute("aria-selected", on ? "true" : "false");
      });
    }
    // Sync the section accent hue to the in-view macro (#3). "all"/"other" and
    // unknown icons fall back to the neutral sage accent.
    const builder = document.querySelector(".rcp-builder");
    if (builder) {
      builder.style.setProperty(
        "--reg-active-hue",
        icon && icon !== "all" && icon !== "other" && icon !== "funds" && icon !== "giftcard" ? `var(--cat-${icon})` : "var(--reg-sage)",
      );
    }
  }
  function setupCurrentRegSpy() {
    if (_curRegSpyIO) { _curRegSpyIO.disconnect(); _curRegSpyIO = null; }
    if (!("IntersectionObserver" in window)) return;
    // Track the registry grid; if it's empty but a preset is being previewed,
    // fall back to the preset grid so the active tab still follows the scroll.
    let grid = document.getElementById("rcp-current-registry-grid");
    if (grid && !grid.querySelector(".rcp-current-cell")) {
      const presetWrap = document.getElementById("rcp-current-preset");
      if (presetWrap && !presetWrap.hidden) {
        grid = document.getElementById("rcp-current-preset-grid") || grid;
      }
    }
    if (!grid) return;
    const cells = [...grid.querySelectorAll(".rcp-current-cell")];
    if (!cells.length) return;
    const isCarousel = grid.classList.contains("is-carousel");
    _curRegSpyIO = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) =>
            isCarousel
              ? a.boundingClientRect.left - b.boundingClientRect.left
              : a.boundingClientRect.top - b.boundingClientRect.top,
          );
        if (vis.length) highlightCurrentTab(vis[0].target.dataset.currentIcon);
      },
      {
        // Carousel scrolls inside the grid; the page grid scrolls in the viewport.
        root: isCarousel ? grid : null,
        threshold: 0.05,
        // Focus the leading edge so the "current" category is the one entering view.
        rootMargin: isCarousel ? "0px -65% 0px 0px" : "-12% 0px -80% 0px",
      },
    );
    cells.forEach((c) => _curRegSpyIO.observe(c));
  }

  // Scroll one grid so a macro category's first cell sits at the start of its
  // scroll (horizontal in carousel mode, the page grid otherwise). No-ops on a
  // missing grid or a macro the grid doesn't contain.
  function scrollGridToIcon(grid, icon) {
    if (!grid) return;
    const isCarousel = grid.classList.contains("is-carousel");
    if (icon === "all") {
      if (isCarousel) grid.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }
    const sel = (window.CSS && CSS.escape) ? CSS.escape(icon) : icon;
    const cell = grid.querySelector(`.rcp-current-cell[data-current-icon="${sel}"]`);
    if (!cell) return;
    cell.scrollIntoView(
      isCarousel
        ? { behavior: "smooth", inline: "start", block: "nearest" }
        : { behavior: "smooth", block: "start", inline: "nearest" },
    );
  }

  // Tab click = nav marker, not a filter. Scroll the chosen macro into view in
  // BOTH the preset preview (when a preset is open) and the registry grid.
  function scrollCurrentToTab(icon) {
    const regGrid = document.getElementById("rcp-current-registry-grid");
    const presetWrap = document.getElementById("rcp-current-preset");
    const presetGrid = presetWrap && !presetWrap.hidden
      ? document.getElementById("rcp-current-preset-grid")
      : null;
    const isCarousel = regGrid && regGrid.classList.contains("is-carousel");

    if (isCarousel) {
      // Two independent horizontal strips → align BOTH to the macro at once.
      scrollGridToIcon(regGrid, icon);
      scrollGridToIcon(presetGrid, icon);
      return;
    }
    if (icon === "all") {
      document.getElementById("rcp-current-registry")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    // Vertical page scroll lands on one region: prefer the preset's same-macro
    // box when a preset is open (the registry's matching box sits below it, in
    // identical order), else the registry.
    scrollGridToIcon(presetGrid || regGrid, icon);
  }

  // Grid (default) ↔ carousel (horizontal list) view for the top block.
  function applyCurrentRegView() {
    ["rcp-current-registry-grid", "rcp-current-preset-grid"].forEach((id) => {
      const gridEl = document.getElementById(id);
      if (!gridEl) return;
      gridEl.classList.toggle("is-carousel", _curRegView === "carousel");
      // Refresh the overlay arrows now that overflow may have changed.
      if (typeof gridEl._arrowUpdate === "function") setTimeout(gridEl._arrowUpdate, 0);
    });
    document.querySelectorAll(".rcp-view-toggle__btn").forEach((b) => {
      const on = b.dataset.view === _curRegView;
      b.classList.toggle("is-active", on);
      b.setAttribute("aria-pressed", on ? "true" : "false");
    });
  }

  function wireCurrentRegistryTabs() {
    const tabsEl = document.getElementById("rcp-current-tabs");
    if (tabsEl) {
      tabsEl.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-current-tab]");
        if (!btn) return;
        // Tabs are scroll-markers now (#1): highlight immediately for feedback,
        // then scroll the category's items into view — the spy keeps the active
        // tab in sync as the scroll settles.
        highlightCurrentTab(btn.dataset.currentTab);
        scrollCurrentToTab(btn.dataset.currentTab);
      });
    }
    // Grid / list (carousel) view toggle.
    document.querySelectorAll(".rcp-view-toggle__btn").forEach((b) => {
      b.addEventListener("click", () => {
        _curRegView = b.dataset.view === "carousel" ? "carousel" : "grid";
        try { localStorage.setItem("birch:reg:curView", _curRegView); } catch (_e) { /* private mode */ }
        applyCurrentRegView();
        // Scroll root changes with the mode → re-arm the scroll-spy.
        setupCurrentRegSpy();
      });
    });
    // Overlay scroll arrows on the grids (auto-hidden unless in carousel mode).
    attachArrows(document.getElementById("rcp-current-registry-grid"));
    attachArrows(document.getElementById("rcp-current-preset-grid"));
  }

  // Scroll-spy: highlight the left-nav entry for whichever stacked section is
  // near the top of the viewport. Replaces the click-driven panel "active"
  // state now that every section is always visible.
  function wireScrollSpy() {
    if (!("IntersectionObserver" in window)) return;
    const panels = [...document.querySelectorAll(".rcp-cat-panel:not(.rcp-cat-panel--all)")];
    if (!panels.length) return;
    const setActive = (panel) => {
      const nav = document.querySelector(`[data-cat-nav="${panel.id}"]`);
      if (!nav) return;
      document.querySelectorAll("[data-cat-nav]").forEach((b) => b.classList.remove("rcp-nav-item--active"));
      nav.classList.add("rcp-nav-item--active");
    };
    const io = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((en) => en.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (vis.length) setActive(vis[0].target);
      },
      { rootMargin: "-15% 0px -75% 0px", threshold: 0 },
    );
    panels.forEach((p) => io.observe(p));
  }

  // Wrap one scrollable strip in a relative container and overlay small prev/
  // next arrows. Arrows auto-hide when there's no horizontal overflow (so it's
  // inert for a grid that isn't in carousel mode). Idempotent per element.
  function attachArrows(scroll) {
    if (!scroll || scroll.dataset.arrowsWired) return;
    scroll.dataset.arrowsWired = "1";
    const ARROW = (dir) =>
      dir === "prev"
        ? `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>`
        : `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>`;
    const wrap = document.createElement("div");
    wrap.className = "rcp-row-scroll-wrap";
    scroll.parentNode.insertBefore(wrap, scroll);
    wrap.appendChild(scroll);
    const mk = (dir) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = `rcp-carousel-arrow rcp-carousel-arrow--${dir}`;
      b.setAttribute("aria-label", dir === "prev" ? "Scroll left" : "Scroll right");
      b.innerHTML = ARROW(dir);
      b.addEventListener("click", () => {
        const amt = Math.max(220, scroll.clientWidth * 0.8);
        scroll.scrollBy({ left: dir === "prev" ? -amt : amt, behavior: "smooth" });
      });
      return b;
    };
    const prev = mk("prev");
    const next = mk("next");
    wrap.appendChild(prev);
    wrap.appendChild(next);
    const update = () => {
      const maxLeft = scroll.scrollWidth - scroll.clientWidth - 2;
      const overflow = scroll.scrollWidth > scroll.clientWidth + 2;
      prev.hidden = !overflow || scroll.scrollLeft <= 2;
      next.hidden = !overflow || scroll.scrollLeft >= maxLeft;
    };
    scroll.addEventListener("scroll", update, { passive: true });
    if ("MutationObserver" in window) {
      new MutationObserver(update).observe(scroll, { childList: true });
    }
    update();
    setTimeout(update, 400);
    scroll._arrowUpdate = update; // callers can force a refresh after toggling
  }

  // Category carousels: hide the scrollbar (CSS) and overlay prev/next arrows.
  function wireCarouselArrows() {
    document.querySelectorAll(".rcp-product-row-scroll").forEach(attachArrows);
  }

  function renderInjectedItems() {
    const esc = window.BirchRegistry.escapeHtml;

    // Wipe previous injections so refresh stays consistent.
    document.querySelectorAll("[data-injected-grid]").forEach((el) => el.remove());

    const isPreview = state.previewItems !== null;

    // Note: we do NOT early-return when there's no registry — the empty
    // "Currently in your registry" box must still render on every browse
    // category (issue 1: always visible, even at 0 items / before a registry
    // exists). The helpers below all tolerate a null registry.

    // Real registry membership — ALWAYS computed so "Currently in your registry"
    // keeps showing even while a preset is previewed (issues 2/11).
    const registryItems = state.registry
      ? (state.registry.items || []).filter((it) => it.status !== "removed")
      : [];
    const { panelMembership, trulyOther } = partitionRegistryIntoPanels(registryItems);

    // Preset preview membership + the identity set used to flag already-added.
    const preview = isPreview ? partitionPreviewIntoPanels(state.previewItems) : null;
    const addedSet = isPreview ? addedIdentitySet() : null;

    // Registry Revamp: the single top "Currently in your registry" block (with
    // macro tabs) owns the in-registry display in the new IA. The per-category
    // injected slots are hidden via CSS (.rcp-builder--stacked [data-injected-slot]).
    // It also surfaces the previewed preset above the registry so the macro tabs
    // filter both — the per-category/all-items preset cards are CSS-hidden here.
    if (NEW_IA) renderCurrentRegistryTabs(panelMembership, trulyOther, esc, preview, addedSet);

    const injectedCounts = {};
    const allPanels = document.querySelectorAll("[data-cat-panel][data-cat-icon]");
    for (const panel of allPanels) {
      const slot = panel.querySelector("[data-injected-slot]");
      if (!slot) continue;
      const catIdx = panel.dataset.catPanel;
      const isBrowseCat = /^\d+$/.test(String(catIdx)); // numeric = a real browse category
      const members = panelMembership.get(panel) || [];
      const previewMembers = preview ? (preview.panelMembership.get(panel) || []) : [];

      const cards = [];
      // Preset card first (the active browse context the user is exploring).
      if (previewMembers.length) cards.push(renderPresetCardHtml(previewMembers, addedSet, esc));
      // Registry card: always on a browse category (issue 1 — visible even at
      // 0 items, as the drop target), plus wherever there are members.
      if (members.length || isBrowseCat) {
        cards.push(renderCurrentItemsCardHtml(members, esc));
      }
      slot.innerHTML = cards.join("");
      wireInjectedSlot(slot);
      injectedCounts[catIdx] = members.length;
    }
    state.aiInjectedCounts = injectedCounts;

    // Aggregate "All items" overview — the REAL registry membership for the
    // "Currently in your registry" boxes, plus the same preset preview overlay
    // ("In this preset" boxes) the per-category panels show, so previewing a
    // preset surfaces here too.
    renderAllItemsView(panelMembership, trulyOther, esc, preview, addedSet);
    setTimeout(revealVariantButtons, 0);
    wireDragAndDrop();

    // Other-items panel — the catch-all at the end of the "More Vetted Picks"
    // stack. Like every other panel in that stack it shows ONLY vetted picks
    // (carousel format): the orphan suggestions that match no category panel (the
    // same set renderAllItemsView buckets into its "Other" group). Active
    // uncategorized registry items are NOT rendered here — they already live in
    // the canonical top "Currently in your registry → Other" tab
    // (renderCurrentRegistryTabs), and rendering them here produced
    // "Currently in registry"-format cards inside a suggestions-only section.
    // Stays hidden when there are no orphan suggestions.
    const grid = document.getElementById("re-other-grid");
    const otherPanel = document.getElementById("rcp-cat-other");
    const navEntry = document.getElementById("rcp-nav-other-entry");
    if (!grid || !otherPanel || !navEntry) return;
    const orphanSugg = (state.suggestions || []).filter((it) => !suggestionPanelFor(it));
    if (orphanSugg.length === 0) {
      hideOtherPanel();
      return;
    }
    navEntry.hidden = false;
    otherPanel.hidden = false;
    // Suggestion-card actions (keep / dismiss / find-similar) are wired by the
    // document-level click delegation in setup — no per-card wiring needed here.
    grid.innerHTML = orphanSugg.map((it) => renderSuggestionCard(it, esc)).join("");
    const countEl = document.getElementById("rcp-count-other");
    if (countEl) countEl.textContent = `${orphanSugg.length}`;
  }

  // ── Drag & drop: drag a browse tile / suggestion / preset card into the
  //    "Currently in your registry" card to add it (issue 11). The drop is just
  //    a convenience gesture for the matching "Add"; re-render then files the
  //    item under its correct category automatically.
  let draggedProductEl = null;
  function wireDragAndDrop() {
    // Registry Revamp: drag-and-drop to add is disabled — adding is via the
    // card "Add" button / popup only. (Drop zones are hidden in the stacked IA.)
    if (NEW_IA) return;
    document
      .querySelectorAll(".rcp-pgrid-card[data-product-id], .rcp-pgrid-card--external, .rcp-pgrid-card--alternate, .re-added-card--suggested, .re-added-card--preview")
      .forEach((el) => {
        if (el.dataset.dragWired) return;
        el.dataset.dragWired = "1";
        el.draggable = true;
        // Inner imgs/links are draggable by default and would hijack the drag —
        // disable them so the card-level drag always wins.
        el.querySelectorAll("img, a").forEach((n) => (n.draggable = false));
        el.addEventListener("dragstart", (e) => {
          draggedProductEl = el;
          el.classList.add("re-dragging");
          try {
            e.dataTransfer.setData("text/plain", "card");
            e.dataTransfer.effectAllowed = "copy";
          } catch (_e) { /* some browsers throw on setData in tests */ }
        });
        el.addEventListener("dragend", () => {
          el.classList.remove("re-dragging");
          draggedProductEl = null;
        });
      });

    document.querySelectorAll("[data-drop-zone]").forEach((zone) => {
      if (zone.dataset.dropWired) return;
      zone.dataset.dropWired = "1";
      zone.addEventListener("dragover", (e) => {
        if (!draggedProductEl) return;
        e.preventDefault();
        zone.classList.add("rcp-current-items--drop");
      });
      zone.addEventListener("dragleave", () => zone.classList.remove("rcp-current-items--drop"));
      zone.addEventListener("drop", (e) => {
        e.preventDefault();
        zone.classList.remove("rcp-current-items--drop");
        const el = draggedProductEl;
        draggedProductEl = null;
        if (el) handleProductDrop(el);
      });
    });
  }

  function handleProductDrop(el) {
    // Browse tile (Birch) → click-equivalent Add.
    if (el.matches(".rcp-pgrid-card[data-product-id]")) {
      const btn = el.querySelector("[data-rcp-add]");
      if (btn && !btn.classList.contains("rcp-pgrid-add-btn--added")) return handleAdd(btn, el);
      return;
    }
    // External (LushLife) feed card → add straight to the registry, same as
    // tapping its "Add" (the card carries its payload in data-ext-*).
    if (el.matches(".rcp-pgrid-card--external")) {
      if (!el.classList.contains("rcp-pgrid-card--added")) return handleAddExternalCard(el);
      return;
    }
    // Personalized alternate (Birch or external) → user-pick add via its id.
    if (el.matches(".rcp-pgrid-card--alternate")) {
      const id = el.getAttribute("data-alt-id");
      if (id && !el.classList.contains("rcp-pgrid-card--added")) return handleAddAlternate(id);
      return;
    }
    // Preset preview card → cherry-pick add (unless already on the registry).
    const previewBtn = el.querySelector("[data-preview-add]");
    if (previewBtn) {
      if (!previewBtn.dataset.alreadyAdded && !previewBtn.classList.contains("re-preview-add--added")) {
        return handleAddPreviewItem(previewBtn);
      }
      return;
    }
    // Suggestion card → keep it (the same as tapping "Add to registry").
    if (el.matches(".re-added-card--suggested")) {
      el.querySelector("[data-sugg-keep]")?.click();
    }
  }

  function hideOtherPanel() {
    const grid = document.getElementById("re-other-grid");
    const otherPanel = document.getElementById("rcp-cat-other");
    const navEntry = document.getElementById("rcp-nav-other-entry");
    if (grid) grid.innerHTML = "";
    if (otherPanel) otherPanel.hidden = true;
    if (navEntry) navEntry.hidden = true;
  }

  // ── Review Box: inline "Suggested for you" sections ──────────────────────
  // AI-suggested items (status "suggested") render as a section UNDER each
  // category's "Currently in your registry" card, and as an aggregate group at
  // the top of "All items". Keep → live (active); Remove → dismissed;
  // Find similar → additively add a vetted alternate via the companion. All
  // persist via the suggestions endpoints (registry-store.js). They never touch
  // the active editor logic.
  function suggestionPanelFor(it) {
    const card = cardSelectorFor(it);
    if (card) {
      const panel = card.closest("[data-cat-panel][data-cat-icon]");
      if (panel) return panel;
    }
    const icon = classifyItemToIcon(it);
    if (icon) {
      const panel = document.querySelector(`[data-cat-panel][data-cat-icon="${CSS.escape(icon)}"]`);
      if (panel) return panel;
    }
    return null; // unclassifiable → aggregate "All items" group only
  }

  function suggestionPriceText(it) {
    const cents = it.priceCents != null ? Number(it.priceCents)
      : it.price != null ? Math.round(Number(it.price) * 100) : null;
    if (cents == null || !Number.isFinite(cents)) return "";
    return "$" + (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
  }

  function renderSuggestionCard(it, esc) {
    // Carousel-style card (.rcp-pgrid-card) like the other sections, plus a
    // top-left dismiss cross and a Why-this / find-similar action row under the
    // price + Add footer. When a reason exists the card keeps its 3D "Why this?"
    // flip (re-flip-card markers preserved for the delegated flip handlers).
    const brandLabel = it.brand || (it.source === "shopify" ? "Birch pick" : "");
    // Always render the vendor line (non-breaking space when empty) so cards in a
    // carousel keep a consistent height.
    const vendorHtml = `<div class="rcp-pgrid-card__vendor">${esc(brandLabel) || "&nbsp;"}</div>`;
    const imgHtml = it.imageUrl
      ? `<img src="${esc(it.imageUrl)}" alt="" class="rcp-pgrid-card__img" loading="lazy">`
      : `<div class="rcp-pgrid-card__img-placeholder" aria-hidden="true">🌿</div>`;
    const priceText = suggestionPriceText(it);
    const hasWhy = !!it.reasonText;
    // "Why this?" flips the card to a back face that explains the pick.
    const whyBtn = hasWhy
      ? `<button type="button" class="rcp-sugg-why-btn" data-flip-open="${esc(it.id)}" aria-label="Why this pick?">
           <span class="rcp-sugg-why-btn__icon" aria-hidden="true">✨</span>Why this?
         </button>`
      : "";
    const findSimilarBtn = `<button type="button" class="rcp-sugg-icon rcp-sugg-findsimilar" data-sugg-findsimilar="${esc(it.id)}" title="Find a similar option to add" aria-label="Find a similar option to add">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                </button>`;
    // Trust score chip on the action line (#5) — PDP-parity value (never "0").
    const trustHtml = trustChipHtml(it.trustScore);
    const backFace = hasWhy
      ? `<div class="re-flip__face re-flip__face--back" aria-hidden="true">
           <div class="rcp-why-back">
             <div class="rcp-why-back__head"><span class="rcp-why-back__spark" aria-hidden="true">✨</span>Why this pick</div>
             <p class="rcp-why-back__text">${esc(it.reasonText)}</p>
             <button type="button" class="rcp-why-back__close" data-flip-close="${esc(it.id)}">
               <span aria-hidden="true">←</span> Back to card
             </button>
           </div>
         </div>`
      : "";
    // Dismiss ✕ sits in the top-left corner of the image (mirrors the
    // "Currently in your registry" cards' remove cross).
    const dismissBtn = `<button type="button" class="rcp-pgrid-card__remove rcp-sugg-remove" data-sugg-remove="${esc(it.id)}" title="Remove suggestion" aria-label="Remove suggestion">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>`;
    const front = `
      <div class="rcp-pgrid-card__img-wrap">
        ${dismissBtn}
        <button type="button" class="rcp-pgrid-card__img-btn" data-open-detail="${esc(it.id)}" aria-label="View details for ${esc(it.title || "")}">
          ${imgHtml}
        </button>
      </div>
      <div class="rcp-pgrid-card__body">
        ${vendorHtml}
        <button type="button" class="rcp-pgrid-card__title" data-open-detail="${esc(it.id)}">${esc(it.title || "")}</button>
        <div class="rcp-pgrid-card__footer">
          <span class="rcp-pgrid-card__price">${esc(priceText)}</span>
          <button type="button" class="rcp-pgrid-add-btn rcp-sugg-keep" data-sugg-keep="${esc(it.id)}">${ADD_PLUS_SVG} Add</button>
        </div>
        <div class="rcp-sugg-actions-row">
          ${findSimilarBtn}
          ${whyBtn}
          ${trustHtml}
        </div>
      </div>`;
    // No reason → a plain carousel card (no flip wrappers, so the card wears its
    // own .rcp-pgrid-card chrome rather than a transparent flip shell).
    if (!hasWhy) {
      return `
        <div class="rcp-pgrid-card rcp-pgrid-card--suggested re-added-card--suggested" data-card-item="${esc(it.id)}">
          ${front}
        </div>`;
    }
    return `
      <div class="rcp-pgrid-card rcp-pgrid-card--suggested re-added-card--suggested re-flip-card" data-card-item="${esc(it.id)}">
        <div class="re-flip">
          <div class="re-flip__face re-flip__face--front">
            ${front}
          </div>
          ${backFace}
        </div>
      </div>`;
  }

  function suggestionSectionHtml(members, esc) {
    const n = members.length;
    // Bulk acts on THIS section's items only (scoped client-side), so it's safe
    // per category. Shown only when there's more than one to act on.
    const bulk = n > 1
      ? `<div class="rcp-sugg-bulk">
           <button type="button" class="rcp-sugg-bulk-btn" data-sugg-keep-all>Add all</button>
           <button type="button" class="rcp-sugg-bulk-btn rcp-sugg-bulk-btn--ghost" data-sugg-dismiss-all>Dismiss all</button>
         </div>` : "";
    return `
      <div class="rcp-suggested" data-suggested-grid="1">
        <div class="rcp-suggested__header">
          <span class="rcp-suggested__spark">✨</span>
          <h3 class="rcp-suggested__title">Suggested for you</h3>
          <span class="rcp-suggested__count">${n} item${n === 1 ? "" : "s"}</span>
          ${bulk}
        </div>
        <p class="rcp-suggested__hint">Keep, remove, or swap each — only added items go on your registry.</p>
        <div class="re-added-cards">
          ${members.map((it) => renderSuggestionCard(it, esc)).join("")}
        </div>
      </div>`;
  }

  // Browse-tab per-category "Suggested for you" sections (the [data-suggested-
  // slot] under each category's research callout). The "All items" tab renders
  // its own per-category suggestions inside renderAllItemsView.
  function renderSuggestions() {
    const esc = window.BirchRegistry.escapeHtml;
    // Clear every browse-tab slot first (so categories that lost all their
    // suggestions go empty), then fill the ones that still have suggestions.
    document.querySelectorAll("[data-suggested-slot]").forEach((s) => (s.innerHTML = ""));

    const suggestions = state.suggestions || [];
    if (suggestions.length) {
      const byPanel = new Map();
      for (const it of suggestions) {
        const panel = suggestionPanelFor(it);
        if (!panel) continue; // unclassifiable items show only in the All-items "Other" group
        const arr = byPanel.get(panel) || [];
        arr.push(it);
        byPanel.set(panel, arr);
      }
      for (const [panel, members] of byPanel.entries()) {
        const slot = panel.querySelector("[data-suggested-slot]");
        if (slot) slot.innerHTML = suggestionSectionHtml(members, esc);
      }
    }
    // Render the build's vetted runner-ups (the RegistrySuggestion candidate
    // ledger) into each category's subcategory slot — re-derived from the live
    // registry each call so the surface evolves as the user keeps/adds/dismisses.
    renderCategoryAlternates();
    // Append the live LushLife browse feed under each still-visible subcat row
    // (Birch products first, then LushLife). Lazy + idempotent.
    setupBrowseFeeds();
    // Make the freshly-rendered suggestion cards draggable into the registry
    // card (idempotent — skips anything already wired).
    wireDragAndDrop();
  }

  // ── Alternates → subcategory slot (the "rcp-subcat-section" surface) ────────
  // The per-category vetted runner-ups the build kept as `candidate` rows,
  // delivered on registry.curatedSuggestions, rendered AS the subcategory browse
  // surface (rcp-pgrid rows) in place of the static collection rows. Re-derived
  // from the live registry every call so it shrinks/reshuffles as the user edits.
  // Backend gates the ledger behind REGISTRY_SUGGESTIONS (empty array when off)
  // → nothing renders and the static rows stay as the fallback.
  function renderCategoryAlternates() {
    const esc = window.BirchRegistry.escapeHtml;
    // #5 sync fix: snapshot each carousel's horizontal scroll position before we
    // rebuild the rows, then restore it — so adding/removing an item never makes
    // the carousels jump back to the start ("carousel stays as is" on edits).
    const _scrollSnap = new Map();
    document.querySelectorAll(".rcp-subcat-row").forEach((row) => {
      const s = row.querySelector(".rcp-product-row-scroll");
      if (row.id && s && s.scrollLeft > 0) _scrollSnap.set(row.id, s.scrollLeft);
    });
    const _restoreScroll = () => {
      _scrollSnap.forEach((left, id) => {
        const s = document.getElementById(id)?.querySelector(".rcp-product-row-scroll");
        if (s) s.scrollLeft = left;
      });
    };
    // FROZEN SNAPSHOT MODEL: the carousel is the build's saved snapshot
    // (curatedSuggestions = picks + AI-appended options + alternates), already
    // ordered by [category, carouselRank] server-side. Membership AND order are
    // fixed — they do NOT change when the registry's active/suggested items change.
    // Only each card's CTA reflects the live registry (on-registry → "Remove";
    // otherwise → "Add"). Always restore any row we previously touched first so a
    // re-render (add/remove/edit) recomputes the CTAs cleanly.
    restoreReplacedSubcatRows();
    document.querySelectorAll("[data-static-subcat]").forEach((s) => (s.hidden = false));

    const snapshot = (state.registry && state.registry.curatedSuggestions) || [];
    if (!snapshot.length) { _restoreScroll(); return; }

    // Index the LIVE registry so a snapshot member can find its current state:
    // an active item (→ render its registry-item card with a working Remove) or a
    // pending Review-Box item (→ "suggested"). Neither → a plain "Add" alternate.
    const live = buildLiveIndex();

    // panel → ordered [{ obj, kind, isPick, band, idx }]. Snapshot order is the
    // server's [category, carouselRank]; the per-row sort below promotes
    // already-added items to right after the Birch picks (#2).
    const byPanel = new Map();
    let _snapIdx = 0;
    const matchedActiveIds = new Set(); // active registry items already shown as a snapshot member
    // Dedupe the snapshot against itself: the same product can be persisted as
    // two carousel rows (a pick AND a later append/alternate row) when the
    // write-side append dedupe missed it. Without this it renders twice — once as
    // a "Birch Pick", once as a regular item. Snapshot order is [category,
    // carouselRank asc], so first-seen-wins keeps the pick (lower rank) and drops
    // the dup. Key off the resolved LIVE item id first so two rows that bind to
    // the same registry item collapse even when their stored ids differ.
    const seenMemberKeys = new Set();
    for (const member of snapshot) {
      const idx = _snapIdx++;
      const liveActive = liveLookup(live.active, member);
      if (liveActive) matchedActiveIds.add(liveActive.id);
      const livePending = liveActive ? null : liveLookup(live.pending, member);
      const dedupKey = (liveActive && "id:" + liveActive.id)
        || (livePending && "id:" + livePending.id)
        || memberIdentityKey(member);
      if (dedupKey) {
        if (seenMemberKeys.has(dedupKey)) continue; // already rendered this product
        seenMemberKeys.add(dedupKey);
      }
      // For an on-registry member render the LIVE item (it carries the real
      // registry-item id the Remove button needs); otherwise render the snapshot
      // row (its suggestion id powers Add).
      // BUT the live registry item comes from the backend and does NOT carry the
      // build-time `reasonText`, so the "Why this?" flip vanished the moment a
      // pick was added (and reappeared once removed, when we fell back to the
      // snapshot member). Graft the snapshot member's reason onto the live item
      // so the explanation survives add/remove. Spread into a fresh object — the
      // live `state` items must not be mutated.
      const liveObj = liveActive || livePending;
      const obj = liveObj
        ? (liveObj.reasonText ? liveObj : { ...liveObj, reasonText: member.reasonText, reasonType: member.reasonType })
        : member;
      const kind = liveActive ? "added" : livePending ? "suggested" : "alternative";
      // The "Birch Pick" highlight is a FROZEN property of the snapshot member —
      // its carouselRank pick band (< 100000), set at build time and never
      // touched by add/remove (the server flips status only). Key off the rank,
      // NOT the live `kind`: deriving it from kind both (a) dropped the badge when
      // a pick was removed and (b) wrongly badged runner-ups / find-similar rows
      // (rank >= 100000) the moment they were added to the registry. Only when the
      // rank is absent (legacy registries built before carouselRank) do we fall
      // back to live status as a best-effort.
      const isPick = member.carouselRank != null
        ? member.carouselRank < 100000
        : (kind === "added" || kind === "suggested");
      // ── Ordering band (#2) ───────────────────────────────────────────────
      // 0 = Birch pick, 1 = already-added (non-pick), 2 = the rest. The band is
      // computed ONCE per member (the first render of the page) and frozen in
      // `_carouselBand`, so adding an item mid-session does NOT reshuffle the row
      // ("stays at its place at that time"). On a fresh page load the map is
      // empty, so a member that's on the registry is recomputed to band 1 and
      // lands right after the picks ("after reload, right after the Birch picks").
      let band = _carouselBand.get(member.id);
      if (band === undefined) {
        band = isPick ? 0 : (kind === "added" ? 1 : 2);
        _carouselBand.set(member.id, band);
      }
      const panel = suggestionPanelFor(obj);
      if (!panel) continue; // unclassifiable → only the "Currently in your registry" card / All-items group
      let list = byPanel.get(panel);
      if (!list) { list = []; byPanel.set(panel, list); }
      // DETERMINISTIC PLACEMENT: resolve the subcategory row from the SNAPSHOT
      // member's precise build `category` (e.g. "Crib mattress"), NOT the live
      // registry item's imprecise productType (often the lumped "Cribs &
      // bassinets" or a bare Shopify type). The build already mapped each pick to
      // its exact subcategory — use that instead of re-guessing from the title.
      const placeSub = subcatForItem(member);
      list.push({ obj, kind, isPick, band, idx, placeSub });
    }
    // Group 2 — "other currently-in-registry items". FROZEN at first load (mirrors
    // the curatedSuggestions snapshot + _carouselBand): captured ONCE so the
    // carousel membership + order never change on add/remove until the next reload.
    // Eligibility excludes paste-a-link / non-LushLife (no shopify/lushlife id) —
    // those are deliberately never carousel members. The frozen panel/placeSub/idx
    // pin each card's position.
    if (_carouselExtras === null && state.registry) {
      const carouselEligible = (it) => !!(it.shopifyProductId || it.shopifyHandle || it.lushlifeProductId);
      _carouselExtras = [];
      const activeAtLoad = ((state.registry.items) || [])
        .filter((it) => it.status !== "removed" && it.status !== "suggested");
      for (const it of activeAtLoad) {
        if (matchedActiveIds.has(it.id)) continue; // already a group-1 snapshot member
        if (!carouselEligible(it)) continue;
        const panel = suggestionPanelFor(it);
        if (!panel) continue;
        _carouselExtras.push({ item: it, panel, placeSub: subcatForItem(it), idx: _snapIdx++ });
      }
    }
    // Re-render the frozen extras each pass with a LIVE CTA — Remove while the item
    // is on the registry, Add (re-addable via handleAddAlternate's _carouselExtras
    // fallback) once the user removes it — without ever changing their order.
    for (const ex of (_carouselExtras || [])) {
      const liveItem = liveLookup(live.active, ex.item);
      let list = byPanel.get(ex.panel);
      if (!list) { list = []; byPanel.set(ex.panel, list); }
      list.push({
        obj: liveItem || ex.item,
        kind: liveItem ? "added" : "alternative",
        isPick: false,
        band: 50,
        idx: ex.idx,
        placeSub: ex.placeSub,
      });
    }
    // Box EVERY category panel's rows — not just those with members — so a panel
    // whose row has only static Birch cards (group 3) still gets the macro box.
    for (const panel of document.querySelectorAll("[data-cat-panel][data-cat-icon]")) {
      const members = byPanel.get(panel) || [];
      // Stable sort by (band, original snapshot index): picks → added → rest →
      // other-registry (band 50). fillSubcatsWithAlternates keeps this order when
      // distributing into subcategory rows.
      members.sort((a, b) => (a.band - b.band) || (a.idx - b.idx));
      fillSubcatsWithAlternates(panel, members, esc);
    }
    // Filled rows cleared their feedLoaded flag — (re)observe so each gets its
    // capped LushLife tail. Idempotent with the caller's own setupBrowseFeeds().
    setupBrowseFeeds();
    // Restore carousel scroll positions captured before the rebuild (#5).
    _restoreScroll();
  }

  // Title/type keyword → subcategory fallback, used ONLY when an item has no
  // canonical category and its raw productType isn't a CATEGORY_TO_SUBCAT key
  // (e.g. a Birch catalog item added straight from a card with a bare Shopify
  // type, or none at all — like a manually-added crib). Ordered MOST-SPECIFIC
  // first so "crib mattress"/"bassinet" win before the bare "crib". Every target
  // is a real subcategory row label (a CATEGORY_TO_SUBCAT value).
  const SUBCAT_KEYWORDS = [
    // Head-noun first: a "Crib & Toddler Mattress" / "Crib Sheet" is a mattress /
    // sheet even though "crib" appears (and the words may not be adjacent), so
    // these must precede the bare crib furniture rule below.
    [/\b(crib\s*sheet|fitted\s*sheet|sheet\s*set)\b/, "Crib Sheets"],
    [/\bmattress\b/, "Crib Mattresses"],
    // Crib BEFORE bassinet: a "Convertible Crib with Bassinet" is a crib (#1). A
    // true bassinet has no crib cue, so it still falls through to the bassinet row.
    [/\b(convertible\s*crib|crib)\b/, "Cribs"],
    [/\b(bassinet|bedside\s*sleeper|co-?sleeper)\b/, "Bassinets"],
    // After crib/bassinet: a "Crib and Dresser Nursery Set" is still a crib.
    [/\b(dressers?|changing\s*tables?|gliders?|nightstands?|bookcases?|bookshel(?:f|ves)|armoires?|chest\s*of\s*drawers)\b/, "Furniture"],
    [/\bcar\s*seat\b/, "Car Seats"],
    [/\bstroller\b/, "Strollers"],
    [/\b(baby\s*carrier|carrier|baby\s*wrap)\b/, "Carriers"],
    [/\b(baby\s*monitor|video\s*monitor|monitor)\b/, "Smart Monitors"],
    [/\b(nasal\s*aspirator|nose\s*sucker|booger|snot|first\s*aid|medicine|grooming\s*kit|health\s*kit)\b/, "Health"],
  ];

  // ── Origin stamping ────────────────────────────────────────────────────────
  // The card a registrant added an item from knows its macro-category (the
  // panel's data-cat-icon) and its subcategory row (data-feed-sub). That's the
  // most reliable placement signal — far better than guessing from a title — so
  // we capture it at add-time and stamp it on the item. partitionRegistryIntoPanels
  // (via classifyItemToIcon) and subcatForItem read it first, so the item lands in
  // the right macro tab + row even after its originating tile is re-rendered away.
  function originHintsFromEl(el) {
    const card = el && el.closest ? (el.closest(".rcp-pgrid-card") || el) : null;
    if (!card || !card.closest) return null;
    const panel = card.closest("[data-cat-panel][data-cat-icon]");
    const row = card.closest(".rcp-subcat-row[data-feed-sub]");
    const icon = panel ? panel.getAttribute("data-cat-icon") : null;
    const sub = row ? (row.getAttribute("data-feed-sub") || "").trim() : null;
    return (icon || sub) ? { icon: icon || null, sub: sub || null } : null;
  }
  function stampOrigin(item, el) {
    if (!item) return;
    const o = originHintsFromEl(el);
    if (!o) return;
    if (o.icon) item.categoryIcon = o.icon;
    if (o.sub) {
      item.subLabel = o.sub;
      // Only fill productType when the item has no canonical type/category, so we
      // never override a real one. The sub label round-trips through the server
      // and re-classifies the same way on reload (see getClassifier).
      if (!item.productType && !item.category) item.productType = o.sub;
    }
  }

  // Normalize a type/label for tolerant matching: lowercase, "&"→"and", collapse
  // punctuation/whitespace, and drop a trailing plural "s" on each word. So a
  // canonical type and a row label that differ only by plural or "&" vs "and"
  // ("nursing pillow" ↔ "Nursing Pillows", "play gyms & mats") still match.
  function _normSub(s) {
    return String(s || "")
      .toLowerCase()
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, " ")
      .trim()
      .replace(/\b(\w+?)s\b/g, "$1");
  }
  // Normalized subcategory-row index → canonical (display-cased) label, built
  // from the DOM. Lets subcatForItem honor an item whose type IS a row label
  // (exact or plural/“&” variant) without depending on CATEGORY_TO_SUBCAT. A
  // miss only means "not placed in a row" — it can never route to another panel,
  // since fillSubcatsWithAlternates only matches rows inside the item's own panel.
  let _subLabelIndex = null;
  function matchSubLabel(type) {
    if (!_subLabelIndex) {
      _subLabelIndex = new Map();
      document.querySelectorAll(".rcp-subcat-row[data-feed-sub]").forEach((row) => {
        const raw = (row.getAttribute("data-feed-sub") || "").trim();
        if (!raw) return;
        _subLabelIndex.set(raw.toLowerCase(), raw); // exact (lower)
        _subLabelIndex.set(_normSub(raw), raw);     // plural/“&”-tolerant
      });
    }
    if (!type) return null;
    return _subLabelIndex.get(String(type).toLowerCase())
      || _subLabelIndex.get(_normSub(type))
      || null;
  }

  // Map an item to its subcategory row label. Resolution is DETERMINISTIC FIRST:
  //   1. an origin-stamped row label (the row the card was added from),
  //   2. the precise build/canonical `category` (e.g. "Crib mattress"), mapped to
  //      its row via the lookup table — the authoritative signal, since the build
  //      already placed every pick into an exact subcategory.
  // The title is consulted ONLY for the one genuinely-ambiguous build category —
  // "Cribs & bassinets" — and for items carrying no usable category at all.
  //
  // WHY "Cribs & bassinets" is ambiguous: it is a crib-OR-bassinet UNION at the
  // source — the toprack search scope splits it into "(crib) OR (bassinet)"
  // (searchTopRackByCategory / productTypeInCategoryUnion in GenAI), so a
  // toprack-sourced BASSINET can be picked and persisted under it. The snapshot
  // carries only this lumped `category`, so the title head noun is the only
  // signal we have to tell a crib from a bassinet. (The real fix is source-side:
  // persist the precise subcategory — the build already knows each pick's
  // product_type. Until then this one bucket cannot be fully deterministic.)
  function subcatForItem(it) {
    // 1. Origin-stamped row label wins (the card the item was added from).
    if (it && it.subLabel) return it.subLabel;
    const type = String((it && (it.category || it.productType)) || "").toLowerCase().trim();
    const title = String((it && it.title) || "").toLowerCase();

    // 1b. Diaper DISPOSAL override. A diaper pail / diaper-genie belongs in the
    //     "Disposal" row, but add_external_to_registry stamps the COARSE canonical
    //     "Diapers" on it (birchCategoryForExternal matches the bare 'diaper'
    //     substring of 'diaper-pails'), so the precise step below would file it
    //     under Diapers. Re-route by title/type before that. Gated on a diaper cue
    //     so a non-diaper "pail" (e.g. a sand pail toy) is never caught.
    const td = `${type} ${title}`;
    if (/diaper/.test(td) && /\b(pails?|diaper\s*genie|genie|disposal)\b/.test(td)) return "Disposal";

    // 2. PRECISE category → its row, deterministically. Every category EXCEPT the
    //    lumped crib/bassinet union maps to exactly one row, so a correctly-typed
    //    item never depends on the title.
    const LUMPED = type === "cribs & bassinets" || type === "crib & bassinet";
    if (type && !LUMPED) {
      const hit = matchSubLabel(type) || CATEGORY_TO_SUBCAT[type] || null;
      if (hit) return hit;
    }

    // 3. Title head-noun split — disambiguates the lumped crib/bassinet union, and
    //    is the only signal for items with no usable category. Scoped to
    //    sleep-furniture titles; mattress / sheet / travel are tested before the
    //    bare crib check (a "Crib & Toddler Mattress" is a mattress, not a crib).
    if (/\b(crib|bassinet|bassinest|cradle|mattress|bedside\s*sleeper|co-?sleeper|moses\s*basket|playard|playpen|pack\s*[’'`]?\s*n\s*play|travel\s*c(?:rib|ot))\b/.test(title)) {
      if (/\b(play\s*-?\s*(?:yard|pen)|playard|playpen|pack\s*[’'`]?\s*n\s*play|travel\s*crib|travel\s*cot)\b/.test(title)) return "Play Yards & Travel Cribs";
      if (/\b(crib\s*sheet|fitted\s*sheet|sheet\s*set)\b/.test(title)) return "Crib Sheets";
      if (/\bmattress\b/.test(title)) return "Crib Mattresses";
      if (/\b(bassinet|bassinest|bedside\s*sleeper|co-?sleeper|cradle|moses\s*basket)\b/.test(title)) return "Bassinets";
      if (/\bcrib\b/.test(title)) return "Cribs";
    }
    if (LUMPED) return "Cribs"; // lumped union with no title cue → default crib row

    // 4. Last-resort keyword fallback for items with no precise category at all.
    const hay = (type + " " + title).trim();
    for (const [re, sub] of SUBCAT_KEYWORDS) { if (re.test(hay)) return sub; }
    return null;
  }

  // Reduce a Shopify product id to its numeric tail so a GID
  // ("gid://shopify/Product/123") and the theme's bare numeric {{ product.id }}
  // ("123") compare equal. Non-numeric ids fall back to a trimmed lowercase form.
  function normProductId(v) {
    const s = String(v == null ? "" : v);
    const m = s.match(/(\d+)\s*$/);
    return m ? m[1] : s.trim().toLowerCase();
  }

  // Identity keys (product id + handle + normalized title) for a set of injected
  // cards, used to hide only the predetermined collection cards that duplicate
  // them. Product id is the strongest signal — a Birch pick and its catalog twin
  // share an id even when the build stored a slightly different title.
  function injectedIdentitySet(items) {
    const set = new Set();
    for (const it of items) {
      if (it.shopifyProductId) set.add("p:" + normProductId(it.shopifyProductId));
      if (it.shopifyHandle) set.add("h:" + String(it.shopifyHandle).toLowerCase().trim());
      if (it.title) set.add("t:" + String(it.title).trim().toLowerCase().replace(/\s+/g, " "));
    }
    return set;
  }

  function staticCardDuplicatesInjected(card, set) {
    if (!card.getAttribute) return false;
    const pid = card.getAttribute("data-product-id");
    const h = card.getAttribute("data-product-handle");
    const t = card.getAttribute("data-product-title");
    if (pid && set.has("p:" + normProductId(pid))) return true;
    if (h && set.has("h:" + String(h).toLowerCase().trim())) return true;
    if (t && set.has("t:" + String(t).trim().toLowerCase().replace(/\s+/g, " "))) return true;
    return false;
  }

  // Exact map: canonical build category (RegistrySuggestion.category, mined from
  // the editorial vocab / essentials floor — a stable, closed set) → the theme
  // subcategory row label (data-feed-sub). This is the PRIMARY, deterministic
  // source of truth for where an alternate belongs; subcatForItem falls back to
  // title keywords only when an item has no canonical type that matches here
  // (e.g. a Birch catalog item added with a bare/absent Shopify type).
  const CATEGORY_TO_SUBCAT = {
    // Nursery
    // NOTE: "cribs & bassinets" is a crib-OR-bassinet UNION at the source, so it
    // is NOT mapped here — subcatForItem disambiguates it by title (a bassinet can
    // be persisted under it). These bare-crib aliases are real cribs only.
    "crib": "Cribs",
    "cribs": "Cribs",
    "crib mattress": "Crib Mattresses",
    "crib mattresses": "Crib Mattresses",
    "crib sheet": "Crib Sheets",
    "crib sheets": "Crib Sheets",
    "bassinet": "Bassinets",
    "bassinets": "Bassinets",
    "travel crib": "Play Yards & Travel Cribs",
    "travel cribs": "Play Yards & Travel Cribs",
    // Raw toprack slugs, in case an item is stamped with the unmapped catalog type
    // ('travel-cribs' contains the 'crib' substring, so the keyword fallback below
    // would otherwise mis-route it to "Cribs"). Mirrors birchCategoryForExternal.
    "travel-cribs": "Play Yards & Travel Cribs",
    "play-yards": "Play Yards & Travel Cribs",
    "playard": "Play Yards & Travel Cribs",
    "playards": "Play Yards & Travel Cribs",
    "pack n play": "Play Yards & Travel Cribs",
    "changing pad": "Changing Pads",
    "changing pads": "Changing Pads",
    "sound machine": "Sound Machines",
    "sound machines": "Sound Machines",
    // Nursery furniture (row added 2026-09-26). The build vocabulary's "Dresser",
    // "Glider" and "Dresser / changing table", the Birch leaves, and the toprack
    // types the row's LushLife feed pulls (dressers / changing-tables / recliners).
    "dresser": "Furniture",
    "dressers": "Furniture",
    "dresser / changing table": "Furniture",
    "changing table": "Furniture",
    "changing tables": "Furniture",
    "changing-tables": "Furniture",
    "glider": "Furniture",
    "gliders": "Furniture",
    "recliners": "Furniture",
    "nursery furniture": "Furniture",
    "nursery storage": "Furniture",
    "nursery-storage": "Furniture",
    // Gear
    "car seats": "Car Seats",
    "car seat": "Car Seats",
    "strollers": "Strollers",
    "stroller": "Strollers",
    "baby carriers": "Carriers",
    "baby carrier": "Carriers",
    // Diaper
    "diapers": "Diapers",
    "baby wipes": "Wipes",
    "diaper pail": "Disposal",
    "diaper pails": "Disposal",
    // Grooming
    "bath tub": "Bath",
    "bath towels": "Bath",
    "baby wash & shampoo": "Bath",
    "baby lotion": "Skin Care",
    "baby skincare": "Skin Care",
    // diaper rash cream is a DIAPER-panel row, not grooming's Skin Care — its
    // macro icon is "diaper", so it must map to the Diaper panel's own row.
    "diaper rash cream": "Diaper Rash Cream",
    // Applicators joined the row 2026-09-26 (leaf diapering-tools).
    "diaper cream applicator": "Diaper Rash Cream",
    "diaper rash treatment": "Diaper Rash Cream",
    "insect repellent": "Skin Care",
    "insect repellent wipes": "Skin Care",
    "thermometer": "Health",
    "nasal aspirator": "Health",
    "first aid kit": "Health",
    "first aid": "Health",
    "medicine": "Health",
    "grooming kit": "Health",
    // Tech
    "baby monitors": "Smart Monitors",
    // Feeding
    "baby formula": "Formula",
    "baby bottles": "Bottles",
    "bibs": "Bibs",
    // Clothes
    "bodysuits & onesies": "Bodysuits",
    "sleep sacks & swaddles": "Sleep Sacks",
    // Toys — extra-word divergences the tolerant row matcher can't reach.
    "baby toys": "Plush Toys",
    "baby toy": "Plush Toys",
    "toys": "Plush Toys",
    "teething toys": "Plush Toys",
    "teething toy": "Plush Toys",
    "play gym & activity mat": "Play Gyms & Mats",
    "play gym": "Play Gyms & Mats",
    "play mat": "Play Gyms & Mats",
    "activity gym": "Play Gyms & Mats",
    "board book": "Books",
    // Parent & Recovery — Nursing Comfort gained covers, nipple shields and
    // nursing bras 2026-09-26 (it held none of them before).
    "nursing cover": "Nursing Comfort",
    "nursing covers": "Nursing Comfort",
    "multi-use cover": "Nursing Comfort",
    "nipple shield": "Nursing Comfort",
    "nipple shields": "Nursing Comfort",
    "nursing bra": "Nursing Comfort",
    "nursing bras": "Nursing Comfort",
  };

  // Dev-only drift guard: a canonical build category with no CATEGORY_TO_SUBCAT
  // entry means its alternates silently won't replace a subcategory row. Warn
  // once per category (so renames/additions to the build taxonomy are caught in
  // testing). Gated behind the theme editor or a `birchDebug` localStorage flag,
  // so it's silent in production. Note: a few categories have no row by design
  // (Breast pumps, Pacifiers, Changing pad …) — those warnings are expected.
  const _unmappedAltCatsWarned = new Set();
  function warnUnmappedAltCategory(category, panel) {
    if (!category || _unmappedAltCatsWarned.has(category)) return;
    let dev = false;
    try {
      dev = !!(window.Shopify && window.Shopify.designMode) || localStorage.getItem("birchDebug") === "1";
    } catch (_e) { /* storage blocked → treat as non-dev */ }
    if (!dev) return;
    _unmappedAltCatsWarned.add(category);
    const label = panel?.querySelector(".rcp-cat-header__title")?.textContent?.trim()
      || panel?.dataset?.catPanel || "?";
    console.warn(
      `[registry-alternates] no CATEGORY_TO_SUBCAT entry for build category "${category}" `
      + `(panel: ${label}) — its alternates won't replace a subcategory row. `
      + `Add a mapping if this category should have one.`,
    );
  }

  // Deterministic, stable brand round-robin for a row's static Birch catalog
  // cards. Birch dropship means a subcategory can be dominated by one vendor
  // (e.g. several "Parasol Co" items in a row); interleaving by brand keeps the
  // strip varied without randomness. STABLE across re-renders: each card's
  // ORIGINAL collection position is stamped once (data-rr-base) and used as the
  // bucketing base, so re-filling an already-interleaved row reproduces the SAME
  // order — round-robin isn't idempotent on its own output, so we always sort
  // back to the base first. Within a brand, base order is preserved; brands
  // rotate in first-appearance order. Brand read from data-detail-brand (set by
  // both the static Liquid card and alternateCardHtml), falling back to
  // data-vendor; blank-brand cards share one bucket and rotate like any brand.
  // No-ops (<2 brands) return the base order unchanged.
  function brandRoundRobin(cards) {
    cards.forEach((c, i) => { if (c.dataset.rrBase === undefined) c.dataset.rrBase = String(i); });
    const base = [...cards].sort((a, b) => Number(a.dataset.rrBase) - Number(b.dataset.rrBase));
    const buckets = new Map();
    const brandOrder = [];
    for (const c of base) {
      const brand = (c.getAttribute("data-detail-brand") || c.getAttribute("data-vendor") || "").trim().toLowerCase();
      if (!buckets.has(brand)) { buckets.set(brand, []); brandOrder.push(brand); }
      buckets.get(brand).push(c);
    }
    if (brandOrder.length < 2) return base; // single brand (or none) → nothing to interleave
    const out = [];
    for (let i = 0; ; i++) {
      let pushed = false;
      for (const brand of brandOrder) {
        const arr = buckets.get(brand);
        if (i < arr.length) { out.push(arr[i]); pushed = true; }
      }
      if (!pushed) break;
    }
    return out;
  }

  // Distribute a panel's FROZEN snapshot members into its subcategory rows by an
  // EXACT category→row lookup (above), preserving the incoming order (= the
  // server's [category, carouselRank], i.e. picks first, then AI-appended, then
  // alternates). Each card's CTA is already baked into `kind` ("added" → Remove,
  // "suggested"/"alternative" → Add) by renderCategoryAlternates from live state;
  // this function never reorders or filters by registry state. The row keeps its
  // predetermined collection cards, minus any that duplicate an injected item.
  // `members` is an ordered array of { obj, kind, isPick }.
  function fillSubcatsWithAlternates(panel, members, esc) {
    const rows = [...panel.querySelectorAll("[data-static-subcat] .rcp-subcat-row[data-feed-sub]")];
    if (!rows.length) return;

    // sub-label → ordered [{ obj, kind }] (snapshot order preserved).
    const bySub = new Map();
    for (const m of members) {
      // Prefer the deterministic placement resolved from the snapshot member's
      // build category (set in renderCategoryAlternates); fall back to deriving
      // from the live obj only for callers that don't pass one.
      const sub = m.placeSub || subcatForItem(m.obj);
      if (!sub) {
        if (m.kind === "alternative") {
          warnUnmappedAltCategory(String(m.obj.category || m.obj.productType || "").toLowerCase().trim(), panel);
        }
        continue; // no row mapping → only the "Currently in your registry" card / Suggested section
      }
      let arr = bySub.get(sub);
      if (!arr) { arr = []; bySub.set(sub, arr); }
      arr.push(m);
    }

    // Every subcategory row gets ONE macro-tinted box holding the VETTED cards —
    // Birch Picks (added or not) + any other on-registry item + the static Birch
    // catalog cards. Non-pick runner-ups that aren't on the registry (curated
    // alternates + AI-appended options) are NOT boxed: they render as plain sibling
    // cards RIGHT AFTER the box, so the row reads [vetted box] → [alternates] →
    // [LushLife/Toprack feed]. The box is a single flex child of the row's scroll,
    // so the strip still scrolls as ONE continuous run. Iterate ALL rows (not just
    // those with members) so a Birch-only row is still boxed.
    for (const row of rows) {
      const sub = row.getAttribute("data-feed-sub");
      const list = bySub.get(sub) || [];
      const scroll = row.querySelector(".rcp-product-row-scroll");
      if (!scroll) continue;
      // Drop any LushLife feed cards from a prior pass so the capped feed recomputes
      // cleanly against the new injected set, and let it re-pull (capped if boxed,
      // full-size otherwise). Done for EVERY row so a feed-only row never goes empty.
      scroll.querySelectorAll(".rcp-pgrid-card--external").forEach((c) => c.remove());
      delete row.dataset.feedLoaded;
      // Split the snapshot members (incoming band order preserved): vetted cards
      // stay in the box; non-pick, not-on-registry runner-ups become loose cards.
      const boxList = list.filter((m) => m.isPick || m.kind === "added");
      const altList = list.filter((m) => !m.isPick && m.kind !== "added");
      const box = document.createElement("div");
      box.className = "rcp-vetted-box";
      // Group 1 (picks/added, snapshot order) + group 2 (other-registry) as injected
      // cards. Loose alternates are appended after the box further down.
      if (boxList.length) box.innerHTML = boxList.map((m) => alternateCardHtml(m.obj, esc, m.kind, m.isPick)).join("");
      // Group 3: move the static Birch catalog cards into the box, AFTER 1+2. Hide
      // ones that duplicate ANY injected item (boxed OR loose alternate) — dedupe
      // against the full `list`, not just the boxed subset.
      const injIds = injectedIdentitySet(list.map((m) => m.obj));
      [...scroll.children].forEach((c) => {
        if (c === box) return;
        if (c.classList && c.classList.contains("rcp-pgrid-card--external")) { c.remove(); return; }
        if (staticCardDuplicatesInjected(c, injIds)) c.classList.add("rcp-alt-hidden");
        box.appendChild(c);
      });
      // Brand-diversify the static Birch catalog cards (group 3) IN PLACE: the
      // AI's Birch Picks (group 1+2 injected, --alternate) keep leading the box;
      // only the catalog cards interleave by brand so one dropship vendor doesn't
      // cluster. Hidden duplicates (rcp-alt-hidden) don't participate and trail.
      // appendChild re-emits them after the picks, so picks order is untouched.
      const staticCards = [...box.children].filter(
        (c) => c.classList && c.classList.contains("rcp-pgrid-card") && !c.classList.contains("rcp-pgrid-card--alternate"),
      );
      // Stamp the stable base (original collection) order on EVERY static card up
      // front — including hidden duplicates — so the base stays correct if a
      // duplicate later toggles visibility when an item is added/removed. On the
      // first fill box order == server/collection order, so the index is exact.
      staticCards.forEach((c, i) => { if (c.dataset.rrBase === undefined) c.dataset.rrBase = String(i); });
      const visibleStatic = staticCards.filter((c) => !c.classList.contains("rcp-alt-hidden"));
      const hiddenStatic = staticCards.filter((c) => c.classList.contains("rcp-alt-hidden"));
      if (visibleStatic.length > 1) {
        [...brandRoundRobin(visibleStatic), ...hiddenStatic].forEach((c) => box.appendChild(c));
      }
      // Insert the box (when it has anything), then the loose alternates right after
      // it — before any LushLife feed the row pulls later.
      let anchor = null;
      if (box.children.length) {
        scroll.insertBefore(box, scroll.firstChild);
        anchor = box;
      }
      if (altList.length) {
        const tpl = document.createElement("template");
        tpl.innerHTML = altList.map((m) => alternateCardHtml(m.obj, esc, m.kind, m.isPick)).join("");
        const ref = anchor ? anchor.nextSibling : scroll.firstChild;
        while (tpl.content.firstChild) scroll.insertBefore(tpl.content.firstChild, ref);
      }
      if (!box.children.length && !altList.length) continue; // nothing injected → leave the row for the feed
      // altReplaced marks a filled row so loadBrowseFeedRow caps its feed tail to 10.
      row.dataset.altReplaced = "1";
    }
  }

  // Undo fillSubcatsWithAlternates: drop injected alternates AND the capped
  // LushLife feed cards, un-hide the predetermined cards, and clear feedLoaded so
  // the next pass recomputes the row (and its capped feed) from scratch.
  function restoreReplacedSubcatRows() {
    document.querySelectorAll(".rcp-subcat-row[data-alt-replaced]").forEach((row) => {
      const scroll = row.querySelector(".rcp-product-row-scroll");
      if (scroll) {
        // Unwrap the box: drop injected (group 1+2) cards, then move the static
        // Birch cards (group 3) back out to the scroll before removing the box.
        const box = scroll.querySelector(".rcp-vetted-box");
        if (box) {
          box.querySelectorAll("[data-alt-injected]").forEach((c) => c.remove());
          while (box.firstChild) scroll.insertBefore(box.firstChild, box);
          box.remove();
        }
        scroll.querySelectorAll("[data-alt-injected]").forEach((c) => c.remove());
        scroll.querySelectorAll(".rcp-pgrid-card--external").forEach((c) => c.remove());
        scroll.querySelectorAll(".rcp-alt-hidden").forEach((c) => c.classList.remove("rcp-alt-hidden"));
      }
      delete row.dataset.altReplaced;
      delete row.dataset.feedLoaded;
    });
  }

  // Live registry index for the frozen carousel: maps a product identity to the
  // CURRENT registry-item (active) / pending-suggestion object, so a snapshot
  // member can render with a live CTA (Remove vs Add) without changing which
  // cards appear or their order. `active` excludes removed + pending; `pending`
  // is the Review-Box set. Keys mirror addedIdentitySet (productId normalized).
  function buildLiveIndex() {
    const active = new Map();
    const pending = new Map();
    const idx = (map, it) => {
      if (!it) return;
      if (it.lushlifeProductId) map.set("l:" + String(it.lushlifeProductId), it);
      if (it.externalUrl) map.set("u:" + String(it.externalUrl).toLowerCase(), it);
      if (it.shopifyHandle) map.set("h:" + String(it.shopifyHandle).toLowerCase(), it);
      if (it.shopifyProductId) map.set("p:" + normProductId(it.shopifyProductId), it);
      if (it.title) map.set("t:" + String(it.title).trim().toLowerCase(), it);
    };
    ((state.registry && state.registry.items) || [])
      .filter((it) => it.status !== "removed" && it.status !== "suggested")
      .forEach((it) => idx(active, it));
    (state.suggestions || []).forEach((it) => idx(pending, it));
    return { active, pending };
  }

  // Find a snapshot member's live counterpart in one of buildLiveIndex's maps,
  // most-specific identity first. Returns the live object or null.
  function liveLookup(map, m) {
    return (m.lushlifeProductId && map.get("l:" + String(m.lushlifeProductId)))
      || (m.externalUrl && map.get("u:" + String(m.externalUrl).toLowerCase()))
      || (m.shopifyHandle && map.get("h:" + String(m.shopifyHandle).toLowerCase()))
      || (m.shopifyProductId && map.get("p:" + normProductId(m.shopifyProductId)))
      || (m.title && map.get("t:" + String(m.title).trim().toLowerCase()))
      || null;
  }

  // Stable identity key for a snapshot member, mirroring liveLookup's priority
  // (and its normalization) so two snapshot rows for the SAME product collapse.
  // Used to dedupe the curatedSuggestions snapshot against itself: a product can
  // appear as both a pick (carouselRank < 100000) and a later append/alternate
  // row (>= 100000) when the write-side append dedupe missed it (their stored
  // identity fields differed). Returns null when the member has no usable id.
  function memberIdentityKey(m) {
    return (m.lushlifeProductId && "l:" + String(m.lushlifeProductId))
      || (m.externalUrl && "u:" + String(m.externalUrl).toLowerCase())
      || (m.shopifyHandle && "h:" + String(m.shopifyHandle).toLowerCase())
      || (m.shopifyProductId && "p:" + normProductId(m.shopifyProductId))
      || (m.title && "t:" + String(m.title).trim().toLowerCase())
      || null;
  }

  // Identity set of everything already on the registry OR pending in the Review
  // Box, so alternates never re-offer an item the user already has.
  function addedIdentitySet() {
    const set = new Set();
    const add = (it) => {
      if (!it) return;
      if (it.shopifyHandle) set.add("h:" + String(it.shopifyHandle).toLowerCase());
      if (it.shopifyProductId) set.add("p:" + String(it.shopifyProductId));
      if (it.externalUrl) set.add("u:" + String(it.externalUrl).toLowerCase());
      if (it.lushlifeProductId) set.add("l:" + String(it.lushlifeProductId));
      if (it.title) set.add("t:" + String(it.title).trim().toLowerCase());
    };
    ((state.registry && state.registry.items) || []).forEach(add);
    (state.suggestions || []).forEach(add);
    return set;
  }

  // Resolve the on-registry item that matches a preview item (same identity keys
  // as isAlreadyAdded), so an already-added preview card can carry a working
  // remove control. Returns null when the match is only a pending Review-Box
  // suggestion (no registry id yet).
  function registryItemFor(it) {
    const items = ((state.registry && state.registry.items) || []).filter((r) => r.status !== "removed");
    const norm = (s) => String(s || "").trim().toLowerCase();
    return items.find((r) =>
      (it.shopifyHandle && norm(r.shopifyHandle) === norm(it.shopifyHandle)) ||
      (it.shopifyProductId && String(r.shopifyProductId || "") === String(it.shopifyProductId)) ||
      (it.externalUrl && norm(r.externalUrl) === norm(it.externalUrl)) ||
      (it.lushlifeProductId && String(r.lushlifeProductId || "") === String(it.lushlifeProductId)) ||
      (it.title && norm(r.title) === norm(it.title)),
    ) || null;
  }

  // Pending Review-Box suggestion that matches a preview item (same identity keys
  // as registryItemFor). Lets a preset card represented only as a suggestion carry
  // a working "✕ Remove" that dismisses the suggestion instead of a dead "Added ✓".
  function suggestionIdFor(it) {
    const norm = (s) => String(s || "").trim().toLowerCase();
    const s = (state.suggestions || []).find((r) =>
      (it.shopifyHandle && norm(r.shopifyHandle) === norm(it.shopifyHandle)) ||
      (it.shopifyProductId && String(r.shopifyProductId || "") === String(it.shopifyProductId)) ||
      (it.externalUrl && norm(r.externalUrl) === norm(it.externalUrl)) ||
      (it.lushlifeProductId && String(r.lushlifeProductId || "") === String(it.lushlifeProductId)) ||
      (it.title && norm(r.title) === norm(it.title)),
    );
    return s ? s.id : null;
  }

  function isAlreadyAdded(it, set) {
    if (it.shopifyHandle && set.has("h:" + String(it.shopifyHandle).toLowerCase())) return true;
    if (it.shopifyProductId && set.has("p:" + String(it.shopifyProductId))) return true;
    if (it.externalUrl && set.has("u:" + String(it.externalUrl).toLowerCase())) return true;
    if (it.lushlifeProductId && set.has("l:" + String(it.lushlifeProductId))) return true;
    if (it.title && set.has("t:" + String(it.title).trim().toLowerCase())) return true;
    return false;
  }

  function alternatesSectionHtml(members, esc) {
    // No "More options we considered" header (issue 9, Case 2): the vetted
    // alternatives ARE the browse surface for a built registry, so they render
    // as a plain product row — not a separately-labeled section.
    return `
      <div class="rcp-subcat-row rcp-subcat-row--alternates" data-alternates-row="1">
        <div class="rcp-product-row-scroll">
          ${members.map((it) => alternateCardHtml(it, esc)).join("")}
        </div>
      </div>`;
  }

  // Highlighted "Birch Pick" card for the AI-builder picks injected at the front
  // of each subcategory row. Carries a sage "Birch Pick" badge + accent border so
  // it reads as a curated pick ahead of the regular catalog cards, an action row
  // (find-similar + "Why this?" + trust ring) matching the Suggested section, and
  // — when the build attached a reason — a 3D "Why this?" flip whose back face
  // explains the pick (reusing the same delegated flip handlers).
  //   kind: "added"       → on-registry pick, footer shows a working "✕ Remove"
  //         "suggested"   → pending Review-Box pick, footer Add = keep (data-alt-add → keep)
  //         "alternative" → curated runner-up, footer Add = add (data-alt-add → add)
  function alternateCardHtml(it, esc, kind, isPickOverride) {
    const cents = it.priceCents != null ? Number(it.priceCents)
      : it.price != null ? Math.round(Number(it.price) * 100) : null;
    const priceStr = (cents != null && Number.isFinite(cents))
      ? "$" + (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2) : "";
    const vendor = it.brand || (it.source === "shopify" ? "Birch pick" : (it.externalRetailer || ""));
    // "Birch Pick" treatment (badge + accent border) marks the AI's OWN picks —
    // the on-registry items ("added") and the pending Review-Box items
    // ("suggested"). Curated runner-ups ("alternative") are NOT Birch Picks: they
    // still lead the row but wear plain card chrome with no badge/accent.
    // isPick is normally the FROZEN snapshot property (carouselRank pick band),
    // passed in by renderCategoryAlternates so a removed pick keeps its Birch
    // Pick highlight. Fall back to live `kind` for callers that don't pass it.
    const isPick = isPickOverride !== undefined
      ? isPickOverride
      : (kind === "added" || kind === "suggested");
    const pickBadge = isPick
      ? `<span class="rcp-pick-badge"><span class="rcp-pick-badge__spark" aria-hidden="true">✨</span>Birch Pick</span>`
      : "";
    // Birch reward badge — top-right shaded logo ("Earn N% Back" on hover).
    // Birch (Shopify-catalog) cards only; external retailer cards don't qualify.
    const rewardBadge = it.source === "shopify"
      ? `<span class="rcp-birch-reward" data-tip="Earn ${REWARD_PCT}% Back" tabindex="0" role="img" aria-label="Earn ${REWARD_PCT}% Back"><img src="https://cdn.shopify.com/s/files/1/0675/0761/7858/files/logo_shaded.webp" alt="" loading="lazy" width="30" height="30"></span>`
      : "";
    const img = it.imageUrl
      ? `<img src="${esc(it.imageUrl)}" alt="${esc(it.title || "")}" loading="lazy" width="400" height="400" class="rcp-pgrid-card__img">`
      : `<div class="rcp-pgrid-card__img-placeholder" aria-hidden="true">🌿</div>`;
    const ext = it.source !== "shopify";
    const hasWhy = !!it.reasonText;
    const isAdded = kind === "added";
    // Image + title open the in-page detail popup (data-card-detail), not a
    // navigation — consistent with every other card on the page.
    const titleEl = `<button type="button" class="rcp-pgrid-card__title" data-card-detail>${esc(it.title || "")}</button>`;
    const imgLinked = `<button type="button" class="rcp-pgrid-card__img-btn" data-card-detail tabindex="-1" aria-hidden="true">${img}</button>`;
    // Footer (#3): on-registry picks show a WORKING "✕ Remove" pill that pulls the
    // item back off the registry — the same affordance the browse tiles get
    // (setAdded), not a dead "Added ✓". The only exception is a gifted/locked item
    // (purchases or unresolved contributions): it stays a non-interactive "Gifted"
    // chip since the server would reject the removal anyway. The rest show an Add
    // pill (data-alt-add — handleAddAlternate routes suggestion ids to keep).
    const altBlocked = isAdded && !!itemRemovalBlockReason(it);
    const footerBtn = isAdded
      ? (altBlocked
          ? `<span class="rcp-pgrid-add-btn rcp-pgrid-add-btn--added rcp-pgrid-add-btn--locked" aria-label="${esc(it.title || "")} — already gifted, cannot remove">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
              Gifted
            </span>`
          : `<button type="button" class="rcp-pgrid-add-btn rcp-pgrid-add-btn--added" data-alt-remove="${esc(it.id)}" aria-label="Remove ${esc(it.title || "")} from registry">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              Remove
            </button>`)
      : `<button type="button" class="rcp-pgrid-add-btn" data-alt-add="${esc(it.id)}" aria-label="Add ${esc(it.title || "")} to registry">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add
            </button>`;
    // Action row: find-similar (title-driven AI query) + "Why this?" flip (when a
    // reason exists) + trust ring (when the item carries a score). Mirrors the
    // Suggested-section card so every Birch Pick reads the same.
    const findSimilarBtn = `<button type="button" class="rcp-sugg-icon rcp-sugg-findsimilar" data-find-similar="${esc(it.title || "")}"${findSimilarAttrs(it, esc)} title="Find a similar option" aria-label="Find a similar option to ${esc(it.title || "this")}">${FIND_SIMILAR_SVG}</button>`;
    const whyBtn = hasWhy
      ? `<button type="button" class="rcp-sugg-why-btn" data-flip-open="${esc(it.id)}" aria-label="Why this pick?">
             <span class="rcp-sugg-why-btn__icon" aria-hidden="true">✨</span>Why this?
           </button>`
      : "";
    const trustHtml = trustChipHtml(it.trustScore);
    const actionsRow = `<div class="rcp-sugg-actions-row">${findSimilarBtn}${whyBtn}${trustHtml}</div>`;
    const front = `
        <div class="rcp-pgrid-card__img-wrap">
          ${pickBadge}
          ${rewardBadge}
          ${imgLinked}
        </div>
        <div class="rcp-pgrid-card__body">
          <div class="rcp-pgrid-card__vendor">${esc(vendor) || "&nbsp;"}</div>
          ${titleEl}
          <div class="rcp-pgrid-card__footer">
            <span class="rcp-pgrid-card__price">${esc(priceStr)}</span>
            ${footerBtn}
          </div>
          ${actionsRow}
        </div>`;
    const cardAttrs = `data-alt-injected="1" data-alt-id="${esc(it.id)}"
        data-detail-source="${ext ? "external" : "shopify"}"
        data-detail-title="${esc(it.title || "")}"
        data-detail-image="${esc(it.imageUrl || "")}"
        data-detail-price-cents="${cents != null ? cents : ""}"
        data-detail-currency="${esc(it.currency || "USD")}"
        data-detail-brand="${esc(it.brand || "")}"
        data-detail-handle="${ext ? "" : esc(it.shopifyHandle || "")}"
        data-detail-url="${ext ? esc(it.externalUrl || "") : ""}"
        data-detail-llid="${ext ? esc(it.lushlifeProductId || "") : ""}"
        ${it.trustScore != null ? `data-detail-trust="${esc(String(it.trustScore))}"` : ""}`;
    // On-registry picks carry rcp-pgrid-card--added so drop/click delegation
    // treats them as non-addable (and they wear the dimmed "added" chrome).
    const addedCls = isAdded ? " rcp-pgrid-card--added" : "";
    // --pick adds the sage accent border; only the AI's own picks get it.
    const pickCls = isPick ? " rcp-pgrid-card--pick" : "";
    // No reason → a plain pick card, no flip shell.
    if (!hasWhy) {
      return `
      <div class="rcp-pgrid-card rcp-pgrid-card--alternate${pickCls}${addedCls}" ${cardAttrs}>
        ${front}
      </div>`;
    }
    const backFace = `<div class="re-flip__face re-flip__face--back" aria-hidden="true">
           <div class="rcp-why-back">
             <div class="rcp-why-back__head"><span class="rcp-why-back__spark" aria-hidden="true">✨</span>Why this pick</div>
             <p class="rcp-why-back__text">${esc(it.reasonText)}</p>
             <button type="button" class="rcp-why-back__close" data-flip-close="${esc(it.id)}">
               <span aria-hidden="true">←</span> Back to card
             </button>
           </div>
         </div>`;
    return `
      <div class="rcp-pgrid-card rcp-pgrid-card--alternate${pickCls} re-flip-card${addedCls}" ${cardAttrs}>
        <div class="re-flip">
          <div class="re-flip__face re-flip__face--front">
            ${front}
          </div>
          ${backFace}
        </div>
      </div>`;
  }

  // Add an alternate to the registry directly (active) — a deliberate USER pick,
  // distinct from the AI's "suggested" (Review Box) inserts. Works for Birch and
  // external rows; both go through the same /items endpoint via API.addItem.
  // An add for this card is already in flight. Until the first add commits the
  // card still reads "Add", so a fast second tap would fire a second addItem that
  // races the server-side dedupe and creates a DUPLICATE row. Guard against it.
  const altAddInFlight = new Set();

  async function handleAddAlternate(id) {
    const { API, showToast } = window.BirchRegistry;
    if (altAddInFlight.has(id)) return;
    const curated = (state.registry && state.registry.curatedSuggestions) || [];
    let it = curated.find((c) => c.id === id);
    if (!it) {
      // A frozen group-2 extra the user removed mid-session reads "Add" again — its
      // id isn't in curatedSuggestions, so re-add from the captured item payload.
      const ex = (_carouselExtras || []).find((e) => e.item && e.item.id === id);
      if (ex) it = ex.item;
    }
    if (!it) {
      // A Review-Box "suggested" card mirrored into the alternates row — its
      // "Add" means Keep (suggested → active), so route to the keep flow.
      if ((state.suggestions || []).some((s) => s.id === id)) return handleKeepSuggestion(id);
      return;
    }
    // Lock the clicked control immediately so a second tap can't double-submit
    // (a disabled button also suppresses the delegated click).
    const btn = document.querySelector(`[data-alt-add="${(window.CSS && CSS.escape) ? CSS.escape(id) : id}"]`);
    if (btn) { btn.setAttribute("disabled", ""); btn.classList.add("rcp-pgrid-add-btn--pending"); }
    // Capture the card's origin before the await (the alternates row re-renders on
    // add) so we can keep the item's macro tab + subcategory placement.
    const altOrigin = originHintsFromEl(btn);
    altAddInFlight.add(id);
    const cents = it.priceCents != null ? Number(it.priceCents)
      : it.price != null ? Math.round(Number(it.price) * 100) : null;
    const payload = it.source === "shopify"
      ? {
          source: "shopify",
          shopifyProductId: it.shopifyProductId,
          shopifyVariantId: it.shopifyVariantId,
          shopifyHandle: it.shopifyHandle,
          title: it.title,
          imageUrl: it.imageUrl,
          priceCents: cents,
          currency: it.currency || "USD",
          priority: "nice_to_have",
          quantityWanted: 1,
        }
      : {
          source: "external",
          externalUrl: it.externalUrl,
          externalRetailer: it.externalRetailer || it.brand || null,
          // Carry the durable LushLife id + trust so the added row keeps its
          // brand/trust enrichment AND matches this snapshot member by identity.
          lushlifeProductId: it.lushlifeProductId,
          trustScore: it.trustScore,
          title: it.title,
          imageUrl: it.imageUrl,
          priceCents: cents,
          currency: it.currency || "USD",
          priority: "nice_to_have",
          quantityWanted: 1,
        };
    try {
      const registryId = await getOrCreateRegistry();
      const { item } = await API.addItem(registryId, payload);
      if (!state.registry.items) state.registry.items = [];
      // Preserve the curated canonical category + the card's origin so the added
      // item keeps its macro tab + subcategory row (the server item may omit them).
      if (it.category && !item.category) item.category = it.category;
      // Stamp the snapshot member's durable identity onto the stored item so
      // buildLiveIndex/liveLookup can match it back to this card and flip the CTA
      // to "Remove" IN PLACE. The server response may omit these fields (or
      // normalise the external URL), which left non-Birch cards stuck on "Add"
      // after a successful add — the flip then fired only intermittently.
      if (it.lushlifeProductId && !item.lushlifeProductId) item.lushlifeProductId = it.lushlifeProductId;
      if (it.externalUrl && !item.externalUrl) item.externalUrl = it.externalUrl;
      if (it.shopifyProductId && !item.shopifyProductId) item.shopifyProductId = it.shopifyProductId;
      if (it.shopifyHandle && !item.shopifyHandle) item.shopifyHandle = it.shopifyHandle;
      if (it.title && !item.title) item.title = it.title;
      if (altOrigin) {
        if (altOrigin.icon) item.categoryIcon = altOrigin.icon;
        if (altOrigin.sub) { item.subLabel = altOrigin.sub; if (!item.productType && !item.category) item.productType = altOrigin.sub; }
      }
      state.registry.items.push(item);
      if (item.shopifyProductId) state.itemByProductId[String(item.shopifyProductId)] = item;
      if (item.shopifyHandle) state.itemByHandle[String(item.shopifyHandle)] = item;
      // Frozen carousel: KEEP the member in curatedSuggestions so its card stays
      // in place — the re-render flips its CTA to Remove via the live index (and a
      // later remove flips it back to Add, in place). Do NOT drop it from the
      // snapshot (that made the card vanish/jump and skipped the Remove flip).
      renderInjectedItems();
      updateCategoryCounts();
      renderCategoryAlternates();
      renderRightSidebar(state.registry);
      showToast && showToast("Added to registry");
    } catch (_e) {
      if (btn) { btn.removeAttribute("disabled"); btn.classList.remove("rcp-pgrid-add-btn--pending"); }
      showToast && showToast("Could not add item");
    } finally {
      altAddInFlight.delete(id);
    }
  }

  // Re-render BOTH suggestion surfaces (browse-tab sections + the All-items
  // per-category groups) after an optimistic mutation. renderInjectedItems
  // rebuilds the active panels + All-items view (which now embeds suggestions).
  function rerenderSuggestionSurfaces() {
    renderInjectedItems();
    renderSuggestions();
  }

  // Suggestion actions — optimistic + persistent, mirroring the tray's logic
  // but inline. Wired once via delegation (wireSuggestionActions).
  async function handleKeepSuggestion(itemId) {
    const { API, showToast } = window.BirchRegistry;
    if (!state.registry) return;
    const idx = (state.suggestions || []).findIndex((s) => s.id === itemId);
    if (idx < 0) return;
    const snap = state.suggestions[idx];
    state.suggestions.splice(idx, 1);

    // Optimistically promote the suggestion into the active set so it appears in
    // "Currently in your registry" instantly instead of waiting on the
    // scheduleRefresh round-trip. keepSuggestion preserves the item id, so the
    // refresh below just reconciles this same row — no duplicate.
    promoteSuggestionToActive(snap);
    rerenderSuggestionSurfaces();
    markAddedProducts();
    updateCategoryCounts();
    renderRightSidebar(state.registry);
    try {
      await API.keepSuggestion(state.registry.id, itemId);
      scheduleRefresh(); // reconcile fields/counts with the server
    } catch (e) {
      // Revert: pull the optimistic active row back out, restore the suggestion.
      demoteActiveItem(itemId);
      state.suggestions.splice(Math.min(idx, state.suggestions.length), 0, snap);
      rerenderSuggestionSurfaces();
      markAddedProducts();
      updateCategoryCounts();
      renderRightSidebar(state.registry);
      showToast && showToast("Couldn't add that item");
    }
  }

  // Move a suggestion's snapshot into the active working set (state.registry.items
  // + the product/handle lookup maps) so "Currently in your registry" reflects it
  // immediately. The item keeps its real id; a later scheduleRefresh reconciles.
  function promoteSuggestionToActive(snap) {
    if (!state.registry.items) state.registry.items = [];
    const activeItem = { ...snap, status: "active" };
    if (state.registry.items.some((it) => it.id === activeItem.id)) return;
    state.registry.items.push(activeItem);
    if (activeItem.source === "shopify") {
      if (activeItem.shopifyProductId) state.itemByProductId[String(activeItem.shopifyProductId)] = activeItem;
      if (activeItem.shopifyHandle) state.itemByHandle[String(activeItem.shopifyHandle)] = activeItem;
    }
  }

  // Inverse of promoteSuggestionToActive — used to roll back a failed keep.
  function demoteActiveItem(itemId) {
    if (state.registry?.items) {
      state.registry.items = state.registry.items.filter((it) => it.id !== itemId);
    }
    for (const map of [state.itemByProductId, state.itemByHandle]) {
      for (const k of Object.keys(map)) if (map[k]?.id === itemId) delete map[k];
    }
  }

  async function handleDismissSuggestion(itemId) {
    const { API, showToast } = window.BirchRegistry;
    if (!state.registry) return;
    const idx = (state.suggestions || []).findIndex((s) => s.id === itemId);
    if (idx < 0) return;
    const snap = state.suggestions[idx];
    state.suggestions.splice(idx, 1);
    rerenderSuggestionSurfaces();
    try {
      await API.dismissSuggestion(state.registry.id, itemId);
    } catch (e) {
      state.suggestions.splice(Math.min(idx, state.suggestions.length), 0, snap);
      rerenderSuggestionSurfaces();
      showToast && showToast("Couldn't remove that suggestion");
    }
  }

  // Find-similar is ADDITIVE (idea 8 / Phase 9): it asks the AI for a vetted
  // option like this AI-suggested pick and adds it — it does NOT swap out the
  // current suggestion (which stays for the registrant to Keep or Remove). The
  // companion routes this to find_similar_options + add.
  function handleFindSimilarSuggestion(itemId) {
    const it = (state.suggestions || []).find((s) => s.id === itemId);
    if (!it) return;
    // Structured identity of the suggestion card, so the AI resolves the seed
    // deterministically (external LushLife suggestions have no Shopify handle).
    const findSimilar = {
      title: it.title || undefined,
      source: it.source || (it.lushlifeProductId ? "external" : it.shopifyHandle ? "shopify" : undefined),
      lushlifeProductId: it.lushlifeProductId || undefined,
      handle: it.shopifyHandle || undefined,
      category: it.productType || it.category || it.stage || undefined,
    };
    window.dispatchEvent(new CustomEvent("birch:open-with-query", {
      detail: { query: `Find a similar option to the suggested "${it.title}" and add it to my registry.`, findSimilar },
    }));
    const { showToast } = window.BirchRegistry;
    showToast && showToast("Asking Birch AI to find a similar option…");
  }

  // Scoped bulk: act on the suggestions in the CLICKED section only (one
  // category), so "Add all" in Nursery never touches Gear. Sends the section's
  // item ids to ONE atomic bulk endpoint (approve/reject) rather than fanning
  // out a PATCH per item — a per-item Promise.all caused concurrent
  // registry-wide completion-discount refreshes to contend on D1 (intermittent
  // 500s → a partial keep → 409 illegal_status_transition on retry). The bulk
  // updateMany is idempotent, so a retry is always safe.
  async function handleScopedBulk(btn, kind) {
    const { API, showToast } = window.BirchRegistry;
    if (!state.registry) return;
    const section = btn.closest(".rcp-suggested");
    const ids = section
      ? [...section.querySelectorAll("[data-card-item]")].map((c) => c.getAttribute("data-card-item"))
      : (state.suggestions || []).map((s) => s.id);
    const idSet = new Set(ids);
    const targets = (state.suggestions || []).filter((s) => idSet.has(s.id));
    if (!targets.length) return;
    const itemIds = targets.map((t) => t.id);
    const snap = state.suggestions.slice();
    state.suggestions = state.suggestions.filter((s) => !idSet.has(s.id));
    // For a bulk keep, promote every target into the active set so they all show
    // in "Currently in your registry" instantly (mirrors handleKeepSuggestion).
    if (kind === "keep") targets.forEach(promoteSuggestionToActive);
    rerenderSuggestionSurfaces();
    if (kind === "keep") { markAddedProducts(); updateCategoryCounts(); renderRightSidebar(state.registry); }
    try {
      if (kind === "keep") {
        await API.approveSuggestions(state.registry.id, { itemIds });
        scheduleRefresh();
      } else {
        await API.rejectSuggestions(state.registry.id, { itemIds });
      }
    } catch (e) {
      state.suggestions = snap;
      if (kind === "keep") itemIds.forEach(demoteActiveItem);
      rerenderSuggestionSurfaces();
      if (kind === "keep") { markAddedProducts(); updateCategoryCounts(); renderRightSidebar(state.registry); }
      showToast && showToast("Bulk action failed — please try again");
    }
  }

  // #5 — bulk-remove ALL pending AI suggestions from the registry. Uses the same
  // atomic reject endpoint as the per-section "Dismiss all" so a retry is safe.
  async function handleRemoveAllSuggestions() {
    const { API, showToast } = window.BirchRegistry;
    if (!state.registry) return;
    const targets = (state.suggestions || []).slice();
    if (!targets.length) return;
    if (typeof window.confirm === "function" &&
        !window.confirm(`Remove all ${targets.length} AI suggestion${targets.length === 1 ? "" : "s"}? You can always ask Birch AI for more.`)) {
      return;
    }
    const itemIds = targets.map((t) => t.id);
    const snap = state.suggestions.slice();
    state.suggestions = [];
    rerenderSuggestionSurfaces();
    renderRightSidebar(state.registry);
    try {
      await API.rejectSuggestions(state.registry.id, { itemIds });
      showToast && showToast("Suggestions removed");
    } catch (e) {
      state.suggestions = snap;
      rerenderSuggestionSurfaces();
      showToast && showToast("Couldn't remove suggestions — please try again");
    }
  }

  let suggestionActionsWired = false;
  function wireSuggestionActions() {
    if (suggestionActionsWired) return;
    suggestionActionsWired = true;
    document.addEventListener("click", (e) => {
      const keep = e.target.closest("[data-sugg-keep]");
      if (keep) { e.preventDefault(); e.stopPropagation(); return handleKeepSuggestion(keep.getAttribute("data-sugg-keep")); }
      const rem = e.target.closest("[data-sugg-remove]");
      if (rem) { e.preventDefault(); e.stopPropagation(); return handleDismissSuggestion(rem.getAttribute("data-sugg-remove")); }
      const fs = e.target.closest("[data-sugg-findsimilar]");
      if (fs) { e.preventDefault(); e.stopPropagation(); return handleFindSimilarSuggestion(fs.getAttribute("data-sugg-findsimilar")); }
      // Generic "find similar" on any browse/feed/static card. The query stays
      // human-readable (title); the data-fs-* attrs ride along as structured
      // `findSimilar` so the AI resolves the exact card (source / lushlife id /
      // handle / category) instead of re-deriving it from the title.
      const fsGen = e.target.closest("[data-find-similar]");
      if (fsGen) {
        e.preventDefault(); e.stopPropagation();
        const title = fsGen.getAttribute("data-find-similar") || "";
        const findSimilar = {
          title,
          source: fsGen.getAttribute("data-fs-source") || undefined,
          lushlifeProductId: fsGen.getAttribute("data-fs-llid") || undefined,
          handle: fsGen.getAttribute("data-fs-handle") || undefined,
          category: fsGen.getAttribute("data-fs-category") || undefined,
        };
        window.dispatchEvent(new CustomEvent("birch:open-with-query", {
          detail: { query: `Find a similar option to "${title}" and add it to my registry.`, findSimilar },
        }));
        window.BirchRegistry.showToast && window.BirchRegistry.showToast("Asking Birch AI to find a similar option…");
        return;
      }
      const ka = e.target.closest("[data-sugg-keep-all]");
      if (ka) { e.preventDefault(); e.stopPropagation(); return handleScopedBulk(ka, "keep"); }
      const da = e.target.closest("[data-sugg-dismiss-all]");
      if (da) { e.preventDefault(); e.stopPropagation(); return handleScopedBulk(da, "dismiss"); }
      const aadd = e.target.closest("[data-alt-add]");
      if (aadd) { e.preventDefault(); e.stopPropagation(); return handleAddAlternate(aadd.getAttribute("data-alt-add")); }
      const arem = e.target.closest("[data-alt-remove]");
      if (arem) { e.preventDefault(); e.stopPropagation(); return handleRemoveById(arem.getAttribute("data-alt-remove")); }
      const eadd = e.target.closest("[data-ext-add]");
      if (eadd) { e.preventDefault(); e.stopPropagation(); return handleAddExternalCard(eadd.closest(".rcp-pgrid-card")); }
      const flipOpen = e.target.closest("[data-flip-open]");
      if (flipOpen) {
        e.preventDefault(); e.stopPropagation();
        const card = flipOpen.closest(".re-flip-card");
        if (card) {
          card.classList.add("is-flipped");
          card.querySelector(".rcp-why-back__close")?.focus();
        }
        return;
      }
      const flipClose = e.target.closest("[data-flip-close]");
      if (flipClose) {
        e.preventDefault(); e.stopPropagation();
        const card = flipClose.closest(".re-flip-card");
        if (card) {
          card.classList.remove("is-flipped");
          card.querySelector(".rcp-sugg-why-btn")?.focus();
        }
        return;
      }
    });
  }

  // ── Browse feed (Surface 2): LushLife products appended per subcat row ──────
  // Each static subcat row (rcp-subcat-row[data-feed-cat][data-feed-sub]) shows
  // its Birch collection products (server-rendered) followed by the top-ranked
  // LushLife products for that subcategory, fetched LIVE from the edge worker so
  // price/stock are always current. Lazy per-row via IntersectionObserver, and
  // only for rows that are actually visible (rows hidden because AI alternates
  // took over the slot never fetch). Idempotent — each row loads once.
  let browseFeedObserver = null;
  function setupBrowseFeeds() {
    if (!("IntersectionObserver" in window)) return;
    if (!browseFeedObserver) {
      browseFeedObserver = new IntersectionObserver((entries) => {
        for (const en of entries) {
          if (!en.isIntersecting) continue;
          const row = en.target;
          browseFeedObserver.unobserve(row);
          loadBrowseFeedRow(row);
        }
      }, { rootMargin: "200px" });
    }
    document.querySelectorAll(".rcp-subcat-row[data-feed-cat][data-feed-sub]").forEach((row) => {
      if (row.dataset.feedLoaded) return;
      // A row hidden because an earlier pass left it empty can never intersect,
      // so the observer would never re-try it. Re-load it directly (it may now
      // hold injected alternates, or the feed may have filled in) and let
      // syncFeedRowVisibility decide again.
      if (row.hasAttribute("data-feed-empty")) { loadBrowseFeedRow(row); return; }
      browseFeedObserver.observe(row);
    });
  }

  // A row whose Birch collection is empty is drawn for the feed alone (see
  // sections/registry-edit.liquid). When the feed brings nothing either, the row
  // is a heading over a blank strip — hide it, and its sidebar subcategory link,
  // until a later pass gives it cards.
  function syncFeedRowVisibility(row) {
    const scroll = row.querySelector(".rcp-product-row-scroll");
    const empty = !scroll || !scroll.querySelector(".rcp-pgrid-card:not(.rcp-alt-hidden)");
    if (empty) row.setAttribute("data-feed-empty", "");
    else if (!row.hasAttribute("data-feed-empty")) return;
    else row.removeAttribute("data-feed-empty");
    row.hidden = empty;
    if (row.id) {
      const nav = document.querySelector(`[data-subcat-target="${row.id}"]`);
      const li = nav && nav.closest("li");
      if (li) li.hidden = empty;
    }
  }

  // Normalize an external buy link for dedup: origin+path only, lowercased, no
  // trailing slash. Duplicate catalog listings point at the SAME retailer page
  // under different titles (and per-fetch tracking params fork the raw URLs), so
  // this — mirroring the server's normBuyUrl in browse-feed.ts — is the stable
  // identity for "same product".
  function normFeedUrl(u) {
    if (!u) return "";
    try {
      const p = new URL(u);
      return (p.origin + p.pathname).toLowerCase().replace(/\/+$/, "");
    } catch (_e) {
      return String(u).trim().toLowerCase();
    }
  }

  async function loadBrowseFeedRow(row) {
    if (row.dataset.feedLoaded) return;
    try {
      await fillBrowseFeedRow(row);
    } finally {
      syncFeedRowVisibility(row);
    }
  }

  async function fillBrowseFeedRow(row) {
    row.dataset.feedLoaded = "1";
    const cat = row.getAttribute("data-feed-cat");
    const sub = row.getAttribute("data-feed-sub");
    const scroll = row.querySelector(".rcp-product-row-scroll");
    if (!cat || !sub || !scroll) return;
    // On a row we've filled with suggested/personalized cards, cap the WHOLE row
    // at 10: pull only (10 − currently-visible cards) LushLife items so the strip
    // tops up to 10 without overflowing. Untouched rows keep the standard size.
    const altFilled = row.dataset.altReplaced === "1";
    // Count actual cards. The vetted cards live INSIDE the .rcp-vetted-box and the
    // loose alternates trail it as siblings, so a descendant query on the scroll —
    // not scroll.children — is what reflects the visible count.
    const visibleNow = scroll.querySelectorAll(".rcp-pgrid-card:not(.rcp-alt-hidden)").length;
    const wantN = altFilled ? Math.max(0, 10 - visibleNow) : 12;
    // A capped personalized row that's already at its 10-card limit still needs the
    // continuous horizontal scroll armed — otherwise it can never pull more feed
    // pages (the no-registry rows wire this below via the wantN = 12 path, which is
    // why infinite scroll only ever worked there). Arm load-more from the start of
    // the feed (offset 0): loadMoreFeedRow dedupes page 1 against the curated cards
    // already in the row, so nothing shows twice and no feed items are skipped.
    // feedMore is optimistic — the first load-more corrects it from the server's
    // hasMore if the feed turns out empty.
    if (wantN <= 0) {
      row.dataset.feedOffset = "0";
      row.dataset.feedMore = "1";
      wireCarouselScroll(row, cat, sub);
      return;
    }
    const PROXY = (window.BirchAI?.routes?.aiProxy) || "/apps/ai";
    // Titles already in this row — Birch static cards plus any injected suggested/
    // personalized cards — so the feed only adds genuinely new options. Sent as the
    // server `exclude` and re-applied client-side as a backstop.
    const normTitle = (t) => String(t || "").trim().toLowerCase().replace(/\s+/g, " ");
    const birchTitleList = [...scroll.querySelectorAll(".rcp-pgrid-card[data-product-title]")]
      .map((c) => normTitle(c.getAttribute("data-product-title")))
      .filter(Boolean);
    const injectedCards = altFilled ? [...row.querySelectorAll("[data-alt-injected]")] : [];
    const injectedTitleList = injectedCards
      .map((c) => normTitle(c.getAttribute("data-detail-title")))
      .filter(Boolean);
    const excludeTitleList = [...birchTitleList, ...injectedTitleList];
    const birchTitles = new Set(excludeTitleList);
    try {
      // Always fetch the canonical page size (12) regardless of how many cards
      // are already in the row. A request-specific limit (the old altFilled
      // `wantN + 8`) forked the edge cache key — cat/sub/limit/offset/sort/exclude
      // — into a dozen variants per row, tanking the hit rate and defeating any
      // warm of the canonical key. 12 covers wantN (rows cap at 10) and the
      // slice below still trims alt-filled rows to wantN client-side.
      const fetchLimit = 12;
      const excludeParam = excludeTitleList.length
        ? `&exclude=${encodeURIComponent(excludeTitleList.join("\n"))}`
        : "";
      const resp = await fetch(
        `${PROXY}/browse-feed?cat=${encodeURIComponent(cat)}&sub=${encodeURIComponent(sub)}&limit=${fetchLimit}${excludeParam}`,
        { credentials: "same-origin" },
      );
      if (!resp.ok) return;
      const data = await resp.json();
      const items = (data && data.products) || [];
      if (!items.length) return;
      const esc = window.BirchRegistry.escapeHtml;
      const added = addedIdentitySet();
      // Also exclude the injected cards by external identity (url / lushlife id /
      // handle), which a title match alone can miss.
      for (const c of injectedCards) {
        const u = c.getAttribute("data-detail-url");
        const l = c.getAttribute("data-detail-llid");
        const h = c.getAttribute("data-detail-handle");
        if (u) added.add("u:" + String(u).toLowerCase());
        if (l) added.add("l:" + String(l));
        if (h) added.add("h:" + String(h).toLowerCase());
      }
      let passed = items
        .filter((it) => !isAlreadyAdded({ externalUrl: it.externalUrl, lushlifeProductId: it.lushlifeProductId, title: it.title }, added))
        .filter((it) => !birchTitles.has(normTitle(it.title)));
      if (altFilled) passed = passed.slice(0, wantN);
      const html = passed.map((it) => feedCardHtml(it, esc)).join("");
      if (html) {
        scroll.insertAdjacentHTML("beforeend", html);
        // The feed lands asynchronously, after renderSuggestions()'s
        // wireDragAndDrop() already ran — re-wire so these external cards are
        // draggable into the registry too (idempotent; skips wired cards).
        wireDragAndDrop();
      }
      // #5 — continuous scroll: remember the server cursor + wire horizontal
      // load-more so reaching the end of a carousel pulls the next page (same
      // browse-feed source the See-all page paginates). On a capped personalized
      // row we only displayed the first `wantN` of page 1, so re-arm from offset 0
      // (not nextOffset) — loadMoreFeedRow dedupes what's already shown and surfaces
      // the items the cap held back, instead of jumping past them to page 2.
      if (altFilled) {
        row.dataset.feedOffset = "0";
        row.dataset.feedMore = "1";
      } else {
        row.dataset.feedOffset = String(data.nextOffset ?? "");
        row.dataset.feedMore = data.hasMore ? "1" : "";
      }
      wireCarouselScroll(row, cat, sub);
    } catch (_e) { /* feed is best-effort — Birch rows still render */ }
  }

  // #5 — load the next browse-feed page into a carousel row when the user
  // scrolls near its right edge. Mirrors the See-all page's infinite scroll.
  async function loadMoreFeedRow(row, cat, sub) {
    if (row.dataset.feedLoadingMore === "1" || row.dataset.feedMore !== "1") return;
    const scroll = row.querySelector(".rcp-product-row-scroll");
    if (!scroll) return;
    row.dataset.feedLoadingMore = "1";
    const offset = parseInt(row.dataset.feedOffset || "0", 10) || 0;
    const PROXY = (window.BirchAI?.routes?.aiProxy) || "/apps/ai";
    const normTitle = (t) => String(t || "").trim().toLowerCase().replace(/\s+/g, " ");
    const present = new Set(
      [...scroll.querySelectorAll(".rcp-pgrid-card[data-product-title], .rcp-pgrid-card[data-detail-title]")]
        .map((c) => normTitle(c.getAttribute("data-product-title") || c.getAttribute("data-detail-title")))
        .filter(Boolean),
    );
    // Cross-batch URL guard (mirrors the server's per-batch normBuyUrl dedup):
    // duplicate catalog listings share one retailer page under different titles,
    // so a title-only check lets page 2 re-show a product page 1 already has.
    // Compare on origin+path — query strings are per-fetch tracking noise.
    const presentUrls = new Set(
      [...scroll.querySelectorAll(".rcp-pgrid-card[data-detail-url], .rcp-pgrid-card[data-ext-url]")]
        .map((c) => normFeedUrl(c.getAttribute("data-detail-url") || c.getAttribute("data-ext-url")))
        .filter(Boolean),
    );
    try {
      const resp = await fetch(
        `${PROXY}/browse-feed?cat=${encodeURIComponent(cat)}&sub=${encodeURIComponent(sub)}&limit=12&offset=${offset}`,
        { credentials: "same-origin" },
      );
      if (resp.ok) {
        const data = await resp.json();
        const items = (data && data.products) || [];
        row.dataset.feedOffset = String(data.nextOffset ?? offset);
        row.dataset.feedMore = data.hasMore ? "1" : "";
        const esc = window.BirchRegistry.escapeHtml;
        const added = addedIdentitySet();
        const passed = items
          .filter((it) => !isAlreadyAdded({ externalUrl: it.externalUrl, lushlifeProductId: it.lushlifeProductId, title: it.title }, added))
          .filter((it) => !present.has(normTitle(it.title)))
          .filter((it) => { const u = normFeedUrl(it.externalUrl); return !u || !presentUrls.has(u); });
        const html = passed.map((it) => feedCardHtml(it, esc)).join("");
        if (html) { scroll.insertAdjacentHTML("beforeend", html); wireDragAndDrop(); }
      }
    } catch (_e) { /* best-effort */ }
    row.dataset.feedLoadingMore = "";
  }

  function wireCarouselScroll(row, cat, sub) {
    if (row.dataset.feedScrollWired === "1") return;
    const scroll = row.querySelector(".rcp-product-row-scroll");
    if (!scroll) return;
    row.dataset.feedScrollWired = "1";
    scroll.addEventListener(
      "scroll",
      () => {
        if (row.dataset.feedMore !== "1" || row.dataset.feedLoadingMore === "1") return;
        if (scroll.scrollLeft + scroll.clientWidth >= scroll.scrollWidth - 320) {
          loadMoreFeedRow(row, cat, sub);
        }
      },
      { passive: true },
    );
  }

  // External (LushLife) browse card — same rcp-pgrid shell as the Birch cards,
  // carrying its add payload in data-ext-* so handleAddExternalCard can add it
  // straight to the registry without a state lookup.
  function feedCardHtml(it, esc) {
    const cents = it.priceCents != null ? Number(it.priceCents)
      : it.price != null ? Math.round(Number(it.price) * 100) : null;
    const priceStr = (cents != null && Number.isFinite(cents))
      ? "$" + (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2) : "";
    const vendor = it.brand || it.externalRetailer || "LushLife";
    const img = it.image
      ? `<img src="${esc(it.image)}" alt="${esc(it.title || "")}" loading="lazy" width="400" height="400" class="rcp-pgrid-card__img">`
      : `<div class="rcp-pgrid-card__img-placeholder" aria-hidden="true">🌿</div>`;
    const href = esc(it.externalUrl || "");
    // Clicking the image/title opens the in-page detail popup (data-card-detail
    // + the normalized data-detail-* descriptor), not a navigation — same UX as
    // the "Suggested for you" cards. The data-ext-* attrs still feed the Add btn.
    return `
      <div class="rcp-pgrid-card rcp-pgrid-card--external"
        data-ext-url="${href}"
        data-ext-title="${esc(it.title || "")}"
        data-ext-image="${esc(it.image || "")}"
        data-ext-price-cents="${cents != null ? cents : ""}"
        data-ext-retailer="${esc(it.externalRetailer || it.brand || "")}"
        data-ext-llid="${esc(it.lushlifeProductId || "")}"
        ${'' /* The exact catalog variant this tile shows — its photo, price and
             buy link all come from it, so the add must record it rather than
             let the server re-resolve some other variant of the product. */}
        data-ext-variant-id="${esc(it.externalVariantId || "")}"
        data-ext-color="${esc(it.color || "")}"
        data-ext-size="${esc(it.size || "")}"
        data-detail-retailer="${esc(it.externalRetailer || "")}"
        data-detail-source="external"
        data-detail-title="${esc(it.title || "")}"
        data-detail-image="${esc(it.image || "")}"
        data-detail-price-cents="${cents != null ? cents : ""}"
        data-detail-currency="USD"
        data-detail-brand="${esc(vendor)}"
        data-detail-url="${href}"
        data-detail-llid="${esc(it.lushlifeProductId || "")}"
        ${it.trustScore != null ? `data-detail-trust="${esc(String(it.trustScore))}"` : ""}>
        <div class="rcp-pgrid-card__img-wrap">
          <button type="button" class="rcp-pgrid-card__img-btn" data-card-detail tabindex="-1" aria-hidden="true">${img}</button>
        </div>
        <div class="rcp-pgrid-card__body">
          <div class="rcp-pgrid-card__vendor">${esc(vendor) || "&nbsp;"}</div>
          <button type="button" class="rcp-pgrid-card__title" data-card-detail>${esc(it.title || "")}</button>
          <div class="rcp-pgrid-card__footer">
            <span class="rcp-pgrid-card__price">${esc(priceStr)}</span>
            <button type="button" class="rcp-pgrid-add-btn" data-ext-add aria-label="Add ${esc(it.title || "")} to registry">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add
            </button>
          </div>
          ${browseActionRow(it, esc)}
        </div>
      </div>`;
  }

  async function handleAddExternalCard(card) {
    if (!card) return;
    const { API, showToast } = window.BirchRegistry;
    const d = card.dataset;
    const cents = d.extPriceCents ? parseInt(d.extPriceCents, 10) : null;
    // Capture the origin (macro + subcategory) BEFORE the await — external feed
    // tiles are removed from the DOM on the next alternates re-render, so this is
    // our only chance to learn where the item came from (#1).
    const origin = originHintsFromEl(card);
    const payload = {
      source: "external",
      externalUrl: d.extUrl,
      externalRetailer: d.extRetailer || null,
      // Persist the durable LushLife/Toprack id (+ trust) the feed card carries.
      // Without it the saved item has only an externalUrl, so on reload it reads as
      // a paste-a-link/non-LushLife item and is dropped from the carousel.
      lushlifeProductId: d.extLlid || undefined,
      // The tile is bound to ONE catalog variant (see feedCardHtml) — record it
      // so the registry row's photo, price and buy link stay the offer the
      // registrant actually looked at.
      externalVariantId: d.extVariantId || undefined,
      externalColor: d.extColor || undefined,
      externalSize: d.extSize || undefined,
      trustScore: d.detailTrust ? Number(d.detailTrust) : undefined,
      title: d.extTitle,
      imageUrl: d.extImage || null,
      priceCents: Number.isFinite(cents) ? cents : null,
      currency: "USD",
      priority: "nice_to_have",
      quantityWanted: 1,
    };
    if (origin && origin.sub) payload.productType = origin.sub;
    const btn = card.querySelector("[data-ext-add]");
    if (btn) btn.disabled = true;
    try {
      const registryId = await getOrCreateRegistry();
      const { item } = await API.addItem(registryId, payload);
      if (!state.registry.items) state.registry.items = [];
      // Stamp the captured origin onto the stored item so it classifies into the
      // right macro tab + subcategory row instead of falling through to "Other".
      if (origin) {
        if (origin.icon) item.categoryIcon = origin.icon;
        if (origin.sub) { item.subLabel = origin.sub; if (!item.productType && !item.category) item.productType = origin.sub; }
      }
      state.registry.items.push(item);
      markAddedProducts();
      renderInjectedItems();
      updateCategoryCounts();
      renderRightSidebar(state.registry);
      // Keep the feed tile in its place — don't yank it out. Flip it to a working
      // "✕ Remove" IN PLACE (reusing the data-alt-remove delegated handler), and
      // mark the card added so the card-body re-add guard leaves it alone. It reads
      // as on-registry there until the next page refresh.
      card.classList.add("rcp-pgrid-card--added");
      if (btn) {
        btn.disabled = false;
        btn.classList.add("rcp-pgrid-add-btn--added");
        btn.innerHTML = `${REMOVE_X_SVG} Remove`;
        btn.removeAttribute("data-ext-add");
        btn.setAttribute("data-alt-remove", item.id);
        btn.setAttribute("aria-label", `Remove ${d.extTitle || ""} from registry`);
      }
      showToast && showToast("Added to registry");
    } catch (_e) {
      if (btn) btn.disabled = false;
      showToast && showToast("Could not add item");
    }
  }

  // ── Aggregate "All items" view ───────────────────────────────────────────
  // One labeled group per category (reusing the category's own icon + label)
  // plus an Other group, so the registrant can see everything in one place
  // instead of clicking category by category. Built from the same membership
  // data as the per-category cards, so the two views can never disagree.
  function renderAllItemsView(panelMembership, trulyOther, esc, preview, addedSet) {
    const navEntry = document.getElementById("rcp-nav-all-entry");
    const groupsHost = document.getElementById("re-all-groups");
    const totalEl = document.getElementById("rcp-all-total");
    const countEl = document.getElementById("rcp-count-all");
    if (!navEntry || !groupsHost) return;

    // Classify suggestions into the SAME panels as active items, so the All
    // view shows each category's active items AND its suggestions together —
    // categorized on one page rather than one big suggestions box.
    const suggByPanel = new Map();
    const orphanSugg = [];
    for (const it of state.suggestions || []) {
      const panel = suggestionPanelFor(it);
      if (panel) {
        const arr = suggByPanel.get(panel) || [];
        arr.push(it);
        suggByPanel.set(panel, arr);
      } else {
        orphanSugg.push(it);
      }
    }

    // One group per BROWSE category (numeric panel), always — so "All items"
    // shows the full registry structure even when empty (issue 2a), mirroring
    // the per-category always-visible boxes (issue 1). Plus an "Other" group
    // when there are unclassifiable items.
    const browsePanels = [...document.querySelectorAll("[data-cat-panel][data-cat-icon]")]
      .filter((p) => /^\d+$/.test(String(p.dataset.catPanel)));
    const groups = [];
    let activeTotal = 0;
    let suggTotal = 0;
    for (const panel of browsePanels) {
      const members = panelMembership.get(panel) || [];
      const sugg = suggByPanel.get(panel) || [];
      const previewMembers = preview ? (preview.panelMembership.get(panel) || []) : [];
      const iconEl = panel.querySelector(".rcp-cat-header__icon");
      const titleEl = panel.querySelector(".rcp-cat-header__title");
      groups.push({
        icon: iconEl ? iconEl.innerHTML : "",
        label: titleEl ? titleEl.textContent.trim() : "",
        members, // [{ item }] — passed straight to the shared component
        suggested: sugg,
        previewMembers, // preset preview items for this category (overlay)
        // Numeric browse-panel index → links the All-items group to its category
        // tab's "Explore Vetted Picks" header (#rcp-more-<idx>) via data-more-nav.
        catIdx: String(panel.dataset.catPanel || ""),
      });
      activeTotal += members.length;
      suggTotal += sugg.length;
    }
    if (trulyOther.length || orphanSugg.length) {
      const otherIcon = document.querySelector("#rcp-cat-other .rcp-cat-header__icon");
      groups.push({
        icon: otherIcon ? otherIcon.innerHTML : "",
        label: "Other Items",
        members: trulyOther.map((it) => ({ item: it })),
        suggested: orphanSugg,
        previewMembers: [], // preset preview items are always classified into a category
      });
      activeTotal += trulyOther.length;
      suggTotal += orphanSugg.length;
    }

    const total = activeTotal + suggTotal;
    state.allHasItems = total > 0; // gates the one-time default-to-All (see below)

    // "All items" is ALWAYS shown (issue 2a) — never hidden at 0 items.
    navEntry.hidden = false;
    const allPanel = document.getElementById("rcp-cat-all");
    if (allPanel) allPanel.hidden = false;
    if (totalEl) totalEl.textContent = total
      ? ` - ${activeTotal} item${activeTotal === 1 ? "" : "s"}` + (suggTotal ? ` · ${suggTotal} suggested` : "")
      : "";
    if (countEl) countEl.textContent = total ? String(activeTotal || suggTotal) : "";

    groupsHost.innerHTML = groups
      .map((g) => {
        // Reuse the EXACT components from the individual category tabs so the two
        // views can't diverge: the category name is the group heading (outside),
        // then the shared "Currently in your registry" box (which carries its own
        // heading + count), then the separate "Suggested for you" box.
        // Preset preview ("In this preset") box first — same order as the
        // per-category panels — then the registry box, then suggestions.
        const presetBox = g.previewMembers && g.previewMembers.length
          ? renderPresetCardHtml(g.previewMembers, addedSet, esc)
          : "";
        // Hide the "tap Add… or drag…" hint in the All-items tab when there are
        // no AI suggestions and no preset preview — there's nothing below to act on.
        const hideHint = suggTotal === 0 && !preview;
        const currentBox = renderCurrentItemsCardHtml(g.members, esc, { hideHint });
        const suggBlock = g.suggested.length ? suggestionSectionHtml(g.suggested, esc) : "";
        // Per-category jump link: takes the user straight to this category's
        // "Explore Vetted Picks" surface to discover more options. Only on real
        // browse categories (the "Other" group has no catIdx / vetted picks tab).
        const moreLink = g.catIdx
          ? `<div class="rcp-all-group__more">
               <button type="button" class="rcp-all-group__more-link" data-more-nav="${esc(g.catIdx)}">More ${esc(g.label)} options &rarr;</button>
             </div>`
          : "";
        return `
        <div class="rcp-all-group">
          <div class="rcp-all-group__head">
            <span class="rcp-all-group__icon" aria-hidden="true">${g.icon}</span>
            <h3 class="rcp-all-group__title">${esc(g.label)}</h3>
          </div>
          ${presetBox}
          ${currentBox}
          ${suggBlock}
          ${moreLink}
        </div>`;
      })
      .join("");

    // Wire both the registry remove controls and the preset preview "Add"
    // controls (wireInjectedSlot handles [data-remove-item] + [data-preview-add]).
    wireInjectedSlot(groupsHost);
  }

  // Remember the last-opened tab per registry so a reload lands the user back on
  // it (rather than always snapping to "All items"). Keyed by registry id so two
  // registries don't clobber each other's last tab.
  const ACTIVE_PANEL_KEY = "birch:reg:lastPanel";
  function persistActivePanel(panelId) {
    const rid = state.registry && state.registry.id;
    if (!rid || !panelId) return;
    try { localStorage.setItem(ACTIVE_PANEL_KEY, JSON.stringify({ rid, panelId })); } catch (_e) { /* private mode */ }
  }
  function readPersistedPanel() {
    const rid = state.registry && state.registry.id;
    if (!rid) return null;
    try {
      const o = JSON.parse(localStorage.getItem(ACTIVE_PANEL_KEY) || "null");
      return o && o.rid === rid ? o.panelId : null;
    } catch (_e) { return null; }
  }

  // Activate a category/aggregate panel + its nav entry (shared by the nav
  // wiring, the default-tab logic, and the "Explore recommended picks" button).
  function activatePanel(panelId) {
    document.querySelectorAll("[data-cat-nav]").forEach((b) => b.classList.remove("rcp-nav-item--active"));
    const navBtn = document.querySelector(`[data-cat-nav="${panelId}"]`);
    if (navBtn) navBtn.classList.add("rcp-nav-item--active");
    document.querySelectorAll("[data-cat-panel]").forEach((p) => p.classList.remove("rcp-cat-panel--active"));
    const panel = document.getElementById(panelId);
    if (panel) panel.classList.add("rcp-cat-panel--active");
    persistActivePanel(panelId);
  }

  // On the first edit-mode render, land the user on the aggregate "All items"
  // tab when the registry has any active items OR pending suggestions — so they
  // see everything at once rather than starting on an arbitrary category.
  // One-time: never yanks the user back after they navigate away.
  function maybeDefaultToAllItems() {
    if (state.didDefaultTab) return;
    // First, restore the tab the user last had open for this registry (survives
    // reloads). Only if the saved panel is present in the DOM — categories can
    // change between sessions; fall through to the "All items" default if not.
    const saved = readPersistedPanel();
    if (saved && document.getElementById(saved)) {
      state.didDefaultTab = true;
      activatePanel(saved);
      return;
    }
    // "All items" is always visible now, so gate on actual content — don't land
    // the user on an empty All view.
    if (!state.allHasItems) return;
    const allPanel = document.getElementById("rcp-cat-all");
    if (!allPanel) return;
    state.didDefaultTab = true;
    activatePanel("rcp-cat-all");
  }

  // If the aggregate panel is the active one but has just emptied out, return
  // the user to the first category panel so they're never stuck on a blank view.
  function deactivateAllPanelIfActive() {
    const allPanel = document.getElementById("rcp-cat-all");
    if (!allPanel || !allPanel.classList.contains("rcp-cat-panel--active")) return;
    allPanel.classList.remove("rcp-cat-panel--active");
    document.querySelectorAll("[data-cat-nav]").forEach((b) => b.classList.remove("rcp-nav-item--active"));
    const firstCatBtn = document.querySelector('[data-cat-nav="rcp-cat-1"]');
    const firstPanel = document.getElementById("rcp-cat-1");
    if (firstCatBtn) firstCatBtn.classList.add("rcp-nav-item--active");
    if (firstPanel) firstPanel.classList.add("rcp-cat-panel--active");
    persistActivePanel("rcp-cat-1");
  }

  // ── Per-category counts: match exactly the "Currently in your registry"
  // card. renderInjectedItems builds that card from BOTH catalog-added rows
  // and classified-unmatched rows, and stashes the total per panel in
  // state.aiInjectedCounts. We use that single number so the nav can't drift
  // from the visible card (previously: added the catalog tally on top of an
  // already-comprehensive injected count → double-counted).
  function updateCategoryCounts() {
    // Sidebar split (Registry Revamp): the "Suggested for You" category nav
    // items (Nursery … Cash Funds) badge their pending AI-suggestion count, while
    // the "Currently Added" entry up top badges the registry's added-item count.
    // Suggestions are classified into the same panels as the stacked
    // "Suggested for you" carousels via suggestionPanelFor, so the nav can't
    // drift from what's shown.
    // The badge counts every item that wears the "Birch Pick" badge — i.e. the
    // picks, NOT the curated alternatives. That's everything ON the registry
    // (active) PLUS the pending Review-Box picks. In a freshly-built registry
    // these are often the same set (suggested == in-registry), so the badge
    // matches what's shown in the rows.
    const suggByPanel = {};
    const bump = (panel) => {
      const idx = panel && panel.dataset ? panel.dataset.catPanel : null;
      if (idx && /^\d+$/.test(String(idx))) suggByPanel[idx] = (suggByPanel[idx] || 0) + 1;
    };
    for (const it of (state.registry && state.registry.items) || []) {
      if (it.status === "removed" || it.status === "suggested") continue; // suggested counted via state.suggestions below
      bump(suggestionPanelFor(it)); // every on-registry pick wears the badge
    }
    for (const it of state.suggestions || []) bump(suggestionPanelFor(it));
    document.querySelectorAll("[data-cat-panel]").forEach((panel) => {
      const catIdx = panel.dataset.catPanel;
      if (catIdx === "other") return;
      const total = suggByPanel[catIdx] || 0;
      const countEl = document.getElementById("rcp-count-" + catIdx);
      if (countEl) countEl.textContent = total > 0 ? String(total) : "";
    });

    // "Currently Added" nav entry: badge = items currently in the registry;
    // reveal it whenever a registry exists (the #rcp-current-registry block it
    // links to is shown for any active registry, even at 0 items).
    const addedTotal = (state.registry?.items || [])
      .filter((it) => it.status !== "removed" && it.status !== "suggested").length;
    const curCountEl = document.getElementById("rcp-count-current");
    if (curCountEl) curCountEl.textContent = addedTotal > 0 ? String(addedTotal) : "";
    const curEntry = document.getElementById("rcp-nav-current-entry");
    if (curEntry) curEntry.hidden = !state.registry;
    // The divider under "Currently Added" only makes sense when the entry shows.
    const curDivider = document.getElementById("rcp-nav-divider");
    if (curDivider) curDivider.hidden = !state.registry;

    // Guest reminder bar rides on the same count. This function is called after
    // every mutation that changes the item set, so piggybacking here keeps the
    // bar in sync without sprinkling calls through each handler.
    renderGuestBar();
  }

  // ── Item action delegation ────────────────────────────────────────────────
  function lookupItemById(itemId) {
    return (state.registry?.items || []).find((it) => it.id === itemId)
      || (state.suggestions || []).find((it) => it.id === itemId)
      || null;
  }

  function refreshControlsFor(itemId) {
    const esc = window.BirchRegistry.escapeHtml;
    const item = lookupItemById(itemId);
    if (!item) return;
    document.querySelectorAll(`[data-controls-for="${CSS.escape(itemId)}"]`).forEach((node) => {
      const wrap = document.createElement("div");
      wrap.innerHTML = renderCardControls(item, esc).trim();
      const fresh = wrap.firstElementChild;
      if (fresh) node.replaceWith(fresh);
    });
  }

  // Re-render the whole "Currently in your registry" card for one item (image +
  // title + price + controls all change when the variant changes). Falls back to
  // refreshControlsFor for the injected Birch-tile controls, which have no
  // re-added-card wrapper (only a [data-controls-for] strip under the tile).
  function refreshAddedCard(itemId) {
    const esc = window.BirchRegistry.escapeHtml;
    const item = lookupItemById(itemId);
    if (!item) return;
    let replacedCard = false;
    document.querySelectorAll(`[data-card-item="${CSS.escape(itemId)}"]`).forEach((node) => {
      const wrap = document.createElement("div");
      wrap.innerHTML = renderItemCard(item, esc, { isAi: item.reasonType === "why_we_suggested_it" }).trim();
      const fresh = wrap.firstElementChild;
      if (fresh) {
        node.replaceWith(fresh);
        replacedCard = true;
        // Re-wire the remove button on the freshly-rendered card.
        fresh.querySelectorAll("[data-remove-item]").forEach((b) => {
          b.addEventListener("click", (e) => {
            e.stopPropagation();
            handleRemoveById(b.dataset.removeItem);
          });
        });
      }
    });
    if (!replacedCard) refreshControlsFor(itemId);
    setTimeout(revealVariantButtons, 0);
  }

  // The "Change variant" button is rendered hidden (data-variant-probe). This
  // reveals it only for products with >1 variant. Variant counts are fetched
  // lazily after paint, cached per Shopify product, and throttled so a large
  // registry never fires a burst of Shopify calls at once.
  const _variantCountCache = new Map(); // product key → variant count
  async function revealVariantButtons() {
    if (!state.registry) return;
    const { API } = window.BirchRegistry;
    const pending = [...document.querySelectorAll("[data-variant-probe]")]
      .filter((b) => !b.dataset.variantProbed);
    if (!pending.length) return;

    // Group buttons by product so identical products are only fetched once.
    const byKey = new Map();
    for (const btn of pending) {
      btn.dataset.variantProbed = "1";
      const item = lookupItemById(btn.dataset.itemId);
      const key = item && (item.shopifyProductId || item.shopifyHandle);
      if (!key) { btn.remove(); continue; } // unfetchable → don't offer it
      let entry = byKey.get(key);
      if (!entry) { entry = { item, btns: [] }; byKey.set(key, entry); }
      entry.btns.push(btn);
    }

    const applyCount = (count, btns) => {
      for (const btn of btns) {
        if (count > 1) btn.hidden = false;
        else btn.remove();
      }
    };

    const keys = [...byKey.keys()];
    let cursor = 0;
    const worker = async () => {
      while (cursor < keys.length) {
        const key = keys[cursor++];
        const { item, btns } = byKey.get(key);
        let count = _variantCountCache.get(key);
        if (count == null) {
          try {
            const data = await API.itemVariants(state.registry.id, item.id);
            count = ((data && data.variants) || []).length;
          } catch (_) { count = 0; }
          _variantCountCache.set(key, count);
        }
        applyCount(count, btns);
      }
    };
    // Bounded concurrency keeps the probe from hammering Shopify on big lists.
    await Promise.all(Array.from({ length: Math.min(5, keys.length) }, worker));
  }

  // Change size/color of a Birch item. Fetches the product's variants on demand
  // (avoids N Shopify calls on page load); if there's only one, says so. On pick,
  // PATCHes the item — the server validates the variant + syncs price and image —
  // then re-renders the card.
  async function handleChangeVariant(itemId) {
    const item = lookupItemById(itemId);
    if (!item) return;
    const { API, showToast } = window.BirchRegistry;
    try {
      const data = await API.itemVariants(state.registry.id, itemId);
      const variants = (data && data.variants) || [];
      if (variants.length <= 1) {
        showToast?.("This product has just one option.");
        return;
      }
      const chosenId = await openVariantPicker(item, data);
      if (!chosenId || chosenId === item.shopifyVariantId) return;
      const { item: updated } = await API.updateItem(state.registry.id, itemId, { shopifyVariantId: chosenId });
      const idx = state.registry.items.findIndex((it) => it.id === itemId);
      if (idx >= 0) {
        state.registry.items[idx] = {
          ...state.registry.items[idx],
          ...updated,
          // The card reads priceCents (number); the PATCH returns price (dollars).
          priceCents: updated.price != null ? Math.round(Number(updated.price) * 100) : state.registry.items[idx].priceCents,
        };
      }
      refreshAddedCard(itemId);
      showToast?.("Option updated ✦");
    } catch (e) {
      showToast?.(e?.body?.message || "Could not change the option");
    }
  }

  // Modal radio-list of variants. Resolves to the chosen variant id (or null).
  function openVariantPicker(item, data) {
    const esc = window.BirchRegistry.escapeHtml;
    const variants = (data && data.variants) || [];
    const current = (data && data.selectedVariantId) || item.shopifyVariantId;
    return new Promise((resolve) => {
      const backdrop = document.createElement("div");
      backdrop.className = "registry-modal-backdrop";
      const rows = variants.map((v) => {
        const label = (v.selectedOptions && v.selectedOptions.length)
          ? v.selectedOptions.map((o) => o.value).join(" / ")
          : v.title;
        const price = v.price != null && v.price !== "" ? ` — $${Number(v.price).toFixed(2)}` : "";
        const oos = v.availableForSale ? "" : " (sold out)";
        return `
          <label class="re-variant-row ${v.availableForSale ? "" : "re-variant-row--oos"}">
            <input type="radio" name="re-variant" value="${esc(v.id)}" ${v.id === current ? "checked" : ""} ${v.availableForSale ? "" : "disabled"}>
            <span>${esc(label)}${price}${oos}</span>
          </label>`;
      }).join("");
      backdrop.innerHTML = `
        <form class="registry-modal" novalidate style="max-width:420px">
          <h2>Choose an option</h2>
          <p style="color:var(--reg-muted);font-size:0.875rem;margin:0 0 14px">${esc(item.title || "")}</p>
          <div class="re-variant-list">${rows}</div>
          <div class="registry-modal__actions">
            <button type="button" class="registry-btn registry-btn--secondary" data-action="cancel">Cancel</button>
            <button type="submit" class="registry-btn registry-btn--primary">Save</button>
          </div>
        </form>`;
      document.body.appendChild(backdrop);
      const close = (val) => { backdrop.remove(); resolve(val); };
      backdrop.querySelector("[data-action=cancel]").addEventListener("click", () => close(null));
      backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(null); });
      backdrop.querySelector("form").addEventListener("submit", (e) => {
        e.preventDefault();
        const sel = backdrop.querySelector("input[name=re-variant]:checked");
        close(sel ? sel.value : null);
      });
    });
  }

  async function handleQuantityDelta(itemId, delta) {
    const item = lookupItemById(itemId);
    if (!item) return;
    const min = Math.max(1, Number(item.quantityPurchased || 0));
    const next = Math.max(min, Number(item.quantityWanted || 1) + delta);
    if (next === item.quantityWanted) return;
    const { API, showToast } = window.BirchRegistry;
    try {
      const { item: updated } = await API.updateItem(state.registry.id, itemId, { quantityWanted: next });
      const idx = state.registry.items.findIndex((it) => it.id === itemId);
      if (idx >= 0) state.registry.items[idx] = { ...state.registry.items[idx], ...updated };
      refreshControlsFor(itemId);
    } catch (e) {
      showToast?.(e?.body?.message || "Could not update quantity");
    }
  }

  // Contribution toggle, goal editing, and mark-as-purchased handlers were
  // removed from the edit page — those actions now live on registry-progress.

  let controlsListenerAttached = false;
  function ensureControlsListener() {
    if (controlsListenerAttached) return;
    controlsListenerAttached = true;
    document.addEventListener("click", (e) => {
      // Product-detail popup: clicking a card's image or title. Handled here
      // (same delegated listener) so it works in every grid without re-wiring.
      // Registry / suggestion cards carry the item id (data-open-detail); browse
      // cards (static Birch tiles, alternates, LushLife feed) carry an inline
      // descriptor on the .rcp-pgrid-card root (data-detail-*) and a
      // data-card-detail marker on their clickable image/title.
      const detailBtn = e.target.closest("[data-open-detail]");
      if (detailBtn) {
        e.preventDefault();
        e.stopPropagation();
        openProductDetail(detailBtn.dataset.openDetail, detailBtn.closest(".rcp-pgrid-card"));
        return;
      }
      const cardDetail = e.target.closest("[data-card-detail]");
      if (cardDetail) {
        const descriptor = descriptorFromCard(cardDetail.closest(".rcp-pgrid-card"));
        if (descriptor) {
          e.preventDefault();
          e.stopPropagation();
          openProductDetail(descriptor, cardDetail.closest(".rcp-pgrid-card"));
          return;
        }
      }
      const btn = e.target.closest("[data-action][data-item-id]");
      if (!btn) return;
      const itemId = btn.dataset.itemId;
      const action = btn.dataset.action;
      e.stopPropagation();
      if (action === "qty-dec") handleQuantityDelta(itemId, -1);
      else if (action === "qty-inc") handleQuantityDelta(itemId, +1);
      else if (action === "change-variant") handleChangeVariant(itemId);
    });
  }

  // ── Product-detail popup ──────────────────────────────────────────────────
  // Two-column on desktop, full-height bottom sheet on mobile. Lazy-loads
  // /apps/registry/api/items/:id/details (the same endpoint the React app uses)
  // which normalizes Birch (Shopify, R2 catalog) and LushLife (external, D1)
  // into one shape: { brand, image, price, currency, trustScore, keyFeatures[],
  // pros[], cons[], specifications[[label,value]], certifications[], materials[],
  // description, externalUrl }. Sections with no data are hidden so a sparser
  // source still looks intentional.
  // Read the normalized data-detail-* descriptor a browse card carries on its
  // .rcp-pgrid-card root. Returns null when the card has no usable source so the
  // caller falls through (e.g. a malformed tile never opens an empty popup).
  function descriptorFromCard(card) {
    if (!card) return null;
    const d = card.dataset;
    const source = d.detailSource;
    if (source !== "shopify" && source !== "external") return null;
    const cents = d.detailPriceCents ? parseInt(d.detailPriceCents, 10) : null;
    return {
      source,
      title: d.detailTitle || "",
      imageUrl: d.detailImage || "",
      brand: d.detailBrand || "",
      priceCents: Number.isFinite(cents) ? cents : null,
      currency: d.detailCurrency || "USD",
      shopifyHandle: d.detailHandle || "",
      externalUrl: d.detailUrl || "",
      lushlifeProductId: d.detailLlid || "",
      trustScore: d.detailTrust ? Number(d.detailTrust) : null,
      // Not rendered by the popup body — these are what the variant picker needs
      // to key the catalog lookup and to build a faithful add payload.
      shopifyProductId: d.productId || "",
      externalRetailer: d.extRetailer || d.detailRetailer || "",
      productType: d.productType || "",
    };
  }

  // The /product-variants descriptor for a browse card. Returns null when the
  // card carries nothing the catalog can be keyed on (→ no picker).
  function descriptorVariantQuery(item) {
    if (item.source === "shopify") {
      if (!item.shopifyHandle && !item.shopifyProductId) return null;
      return { source: "shopify", handle: item.shopifyHandle, productId: item.shopifyProductId };
    }
    if (!item.lushlifeProductId && !item.externalUrl) return null;
    return {
      source: "external",
      lushlifeId: item.lushlifeProductId,
      externalUrl: item.externalUrl,
      retailer: item.externalRetailer,
    };
  }

  // Build the descriptor query string for a browse card (a product that isn't a
  // registry item, so the items/:id/details endpoint can't resolve it). Mirrors
  // proxy.api.product-detail.jsx's params.
  function descriptorDetailUrl(item) {
    const params = new URLSearchParams();
    params.set("source", item.source);
    if (item.shopifyHandle) params.set("handle", item.shopifyHandle);
    if (item.lushlifeProductId) params.set("lushlifeId", item.lushlifeProductId);
    if (item.externalUrl) params.set("externalUrl", item.externalUrl);
    if (item.title) params.set("title", item.title);
    if (item.imageUrl) params.set("image", item.imageUrl);
    if (item.brand) params.set("brand", item.brand);
    const cents = typeof item.priceCents === "number"
      ? item.priceCents
      : (item.price != null ? Math.round(Number(item.price) * 100) : null);
    if (cents != null && Number.isFinite(cents)) params.set("price", String(cents / 100));
    if (item.currency) params.set("currency", item.currency);
    if (item.trustScore != null) params.set("trustScore", String(item.trustScore));
    return `/apps/registry/api/product-detail?${params.toString()}`;
  }

  // Accepts EITHER a registry/suggestion item id (string) → fetch by id, OR an
  // inline descriptor object for a browse card (static Birch tile, alternate, or
  // LushLife feed) → enrich via the descriptor endpoint. Both render the same
  // popup, so every card on the page opens details instead of navigating away.
  function openProductDetail(arg, card) {
    const esc = window.BirchRegistry.escapeHtml;
    let item;
    let detailUrl;
    if (typeof arg === "string") {
      if (!arg) return;
      item = lookupItemById(arg) || {};
      detailUrl = `/apps/registry/api/items/${encodeURIComponent(arg)}/details`;
    } else if (arg && typeof arg === "object" && (arg.source === "shopify" || arg.source === "external")) {
      item = arg;
      detailUrl = descriptorDetailUrl(item);
    } else {
      return;
    }

    const backdrop = document.createElement("div");
    backdrop.className = "registry-modal-backdrop re-detail-backdrop";
    backdrop.innerHTML = `
      <div class="re-detail" role="dialog" aria-modal="true" aria-label="${esc(item.title || "Product")} details">
        <button type="button" class="re-detail__close" data-detail-close aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <div class="re-detail__grip" aria-hidden="true"></div>
        <div class="re-detail__scroll" data-detail-scroll></div>
      </div>`;
    document.body.appendChild(backdrop);
    document.body.style.overflow = "hidden";

    const scrollHost = backdrop.querySelector("[data-detail-scroll]");

    const close = () => {
      backdrop.remove();
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
    backdrop.querySelector("[data-detail-close]").addEventListener("click", close);

    const cardFallback = {
      source: item.source,
      title: item.title,
      brand: item.brand || null,
      image: item.imageUrl || null,
      price: item.price != null ? Number(item.price) : (typeof item.priceCents === "number" ? item.priceCents / 100 : null),
      currency: item.currency || "USD",
      trustScore: item.trustScore != null ? Number(item.trustScore) : null,
      keyFeatures: [], pros: [], cons: [], specifications: [],
      certifications: [], materials: [], description: null,
      externalUrl: item.externalUrl || null,
    };

    // Re-paintable state: the detail fetch and the (registry-item-only) variant
    // fetch resolve independently and each triggers a repaint from the latest of
    // both. Popup is never empty — it starts from what the card already knows.
    let lastDetail = cardFallback;
    let lastState = "loading";
    let variantData = null;
    // Selected option combo for the variant grid (option name → value). Seeded
    // from the item's current variant once variantData resolves; updated in place
    // by option-pill clicks so the choice survives repaints.
    let variantSelection = null;
    // CUR-213 — whether the option lookup has answered, and the sheet's own
    // Add for a product the option picker doesn't cover (see editCtaFor).
    let variantsSettled = false;
    let adding = false;
    const cardRef = card ? cardRefFor(card) : null;
    const liveCard = () => (cardRef ? findCardIn(document.querySelectorAll(".rcp-pgrid-card"), cardRef) : null);
    const onRegistryNow = () => (typeof arg === "string" && (state.registry?.items || []).some((it) => it.id === arg))
      || registryHasProduct(state.registry?.items, item);
    const priceNow = () => (typeof item.priceCents === "number" ? item.priceCents / 100 : Number(lastDetail && lastDetail.price));
    const plainCta = () => editCtaHtml(editCtaFor({
      hasAddPath: cardHasAddPath(liveCard()), onRegistry: onRegistryNow(), adding, variantData, variantsSettled, price: priceNow(),
    }));
    // Review M9 — the option lookup has no timeout of its own; don't leave Add
    // waiting on it forever.
    setTimeout(() => { if (!variantsSettled) { variantsSettled = true; repaint(); } }, 6000);
    const repaint = () => {
      if (!backdrop.isConnected) return;
      scrollHost.innerHTML = renderDetailBody(lastDetail, lastState, esc, item, variantData, variantSelection, plainCta());
    };
    repaint();

    fetch(detailUrl, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status))))
      .then((data) => {
        lastDetail = data.detail || cardFallback;
        lastState = data.detail ? "ready" : "error";
        repaint();
      })
      .catch(() => { lastState = "error"; repaint(); });

    // Variant picker. A registry item (arg is a string id) lists through the
    // item-keyed route, whose selectedVariantId is what distinguishes "this
    // exact option is yours" (Remove) from "you have this product but we don't
    // know which option" (Confirm). A BROWSE card isn't on the registry yet, so
    // it lists through the descriptor route instead and every option reads as
    // Add — same picker, same CTA rules, so the tiles behave identically here
    // and in the intake walkthrough.
    const registryItemId = typeof arg === "string" ? arg : null;
    const variantQuery = registryItemId ? null : descriptorVariantQuery(item);
    const loadVariants = registryItemId
      ? (state.registry?.id && window.BirchRegistry?.API?.itemVariants
          ? window.BirchRegistry.API.itemVariants(state.registry.id, registryItemId) : null)
      : (variantQuery && window.BirchRegistry?.API?.productVariants
          ? window.BirchRegistry.API.productVariants(variantQuery) : null);
    if (loadVariants) {
      loadVariants
        .then((v) => {
          variantData = v || null;
          variantsSettled = true;
          if (variantData) {
            const groups = variantOptionGroups(variantData);
            if (groups.length) variantSelection = defaultVariantSelection(variantData, groups);
          }
          repaint();
        })
        .catch(() => { variantsSettled = true; repaint(); });
    } else {
      variantsSettled = true;
    }

    // Per-variant Add/Remove (#5, delegated so it survives repaints). Adding a
    // variant creates a NEW registry item (two variants coexist); removing pulls
    // that variant's item off the registry. The list repaints from live
    // state.registry.items, so the row flips Add⇄Remove without a refetch.
    scrollHost.addEventListener("click", async (e) => {
      // Option-pill pick: update the selection, re-resolve to a real variant, and
      // repaint the grid + CTA in place (no server call).
      const optBtn = e.target.closest("[data-variant-opt]");
      if (optBtn) {
        e.preventDefault();
        if (!variantData) return;
        const name = optBtn.getAttribute("data-variant-opt");
        const value = optBtn.getAttribute("data-variant-opt-val");
        const groups = variantOptionGroups(variantData);
        const next = { ...(variantSelection || defaultVariantSelection(variantData, groups)), [name]: value };
        variantSelection = repairVariantSelection(variantData, groups, next, name);
        repaint();
        return;
      }
      // CUR-213 — the sheet's Add for a product with one option (or none on
      // record): the card's own add, exactly as if it had been tapped.
      const plainAddBtn = e.target.closest("[data-detail-add]");
      if (plainAddBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (adding) return;
        const target = liveCard();
        if (!target || !cardHasAddPath(target)) { repaint(); return; }
        adding = true;
        repaint();
        try { await addCard(target); } catch (_e) { /* the card's own handler toasts */ }
        adding = false;
        repaint();
        return;
      }
      const addBtn = e.target.closest("[data-variant-add]");
      const remBtn = e.target.closest("[data-variant-remove]");
      const confirmBtn = e.target.closest("[data-variant-confirm]");
      if (!addBtn && !remBtn && !confirmBtn) return;
      e.preventDefault();
      e.stopPropagation();
      const { API, showToast } = window.BirchRegistry;
      if (!state.registry?.id) return;
      const btn = addBtn || remBtn || confirmBtn;
      btn.disabled = true;
      try {
        if (remBtn) {
          const rid = remBtn.getAttribute("data-variant-remove");
          const target = lookupItemById(rid);
          const blockReason = itemRemovalBlockReason(target);
          if (blockReason) { btn.disabled = false; showToast?.(blockReason); return; }
          await API.removeItem(state.registry.id, rid);
          state.registry.items = (state.registry.items || []).filter((it) => it.id !== rid);
          showToast?.("Removed from registry");
        } else if (confirmBtn) {
          // This item is already on the registry but its stored variant is
          // unresolved (see renderVariantList's `unresolved` branch) — the
          // registrant is confirming WHICH option they already have, so PATCH
          // the existing item's variant rather than adding a new row. Uses the
          // same server-side variant-switch validation (and purchase lock) as
          // the manual variant-switch flow.
          const rid = confirmBtn.getAttribute("data-variant-confirm");
          const vid = confirmBtn.getAttribute("data-variant-confirm-vid");
          const kind = variantData?.kind;
          const patch = kind === "external" ? { externalVariantId: vid } : { shopifyVariantId: vid };
          const { item: updated } = await API.updateItem(state.registry.id, rid, patch);
          const idx = (state.registry.items || []).findIndex((it) => it.id === rid);
          if (idx >= 0) {
            const merged = {
              ...state.registry.items[idx],
              ...updated,
              // Cards read priceCents (number); the PATCH returns price (dollars).
              priceCents: updated?.price != null
                ? Math.round(Number(updated.price) * 100)
                : state.registry.items[idx].priceCents,
            };
            state.registry.items[idx] = merged;
            // The popup's `item` closure still points at the pre-merge object;
            // keep it coherent so a repaint/re-open reads the confirmed variant.
            if (item && item.id === rid) Object.assign(item, merged);
          }
          // Mark the popup's variant snapshot resolved: `unresolved` (and the
          // "Confirm this is your option" CTA) derive from selectedVariantId,
          // which the fetch-time snapshot left null. Without this, repaint()
          // re-renders the confirm CTA and the popup looks like nothing
          // happened until a full page reload.
          if (variantData) variantData.selectedVariantId = vid;
          showToast?.("Got it — option confirmed");
        } else {
          const vid = addBtn.getAttribute("data-variant-add");
          const payload = buildVariantAddPayload(vid, variantData, item, lastDetail);
          if (!payload) { btn.disabled = false; return; }
          const { item: created } = await API.addItem(state.registry.id, payload);
          if (created && !(state.registry.items || []).some((it) => it.id === created.id)) {
            (state.registry.items = state.registry.items || []).push({
              ...created,
              priceCents: created.price != null ? Math.round(Number(created.price) * 100) : (payload.priceCents ?? null),
            });
          }
          showToast?.("Added to registry");
        }
        repaint();
        renderInjectedItems();
        renderCategoryAlternates();
        updateCategoryCounts();
        renderRightSidebar(state.registry);
      } catch (err) {
        btn.disabled = false;
        showToast?.(err?.body?.message || "Something went wrong");
      }
    });
  }

  // ── CUR-213: an Add on every product in the sheet ─────────────────────────
  // The option picker only appears for 2+ options, so a single-option product
  // (most Birch items, nearly every partner one) had no Add in the sheet. The
  // sheet now adds THROUGH the product's card — the page's own add for that
  // card type, awaited — so the add is exactly the card's. Review C1/I4: the
  // card is re-found by its product at action time (live refreshes replace
  // card nodes), and success is read from the registry itself, never from a
  // card leaving the page or showing an optimistic "Added". No Remove here.
  const ADDABLE_CARD = ".rcp-pgrid-card--external, .rcp-pgrid-card--alternate, .re-added-card--suggested";
  function cardHasAddPath(card) {
    if (!card) return false;
    if (card.matches(ADDABLE_CARD)) return true;
    return !!(card.querySelector("[data-rcp-add]") || card.querySelector("[data-preview-add]"));
  }
  /** The keys a card is known by, most specific first. Pure; exported. */
  function cardRefFor(card) {
    const d = (card && card.dataset) || {};
    return { cardItem: d.cardItem || "", altId: d.altId || "", productId: d.productId || "", llid: d.detailLlid || "", url: d.detailUrl || "" };
  }
  /** The card for `ref` among `cards` (a repaint's replacement), or null. Pure; exported. */
  function findCardIn(cards, ref) {
    const keys = [["cardItem", "cardItem"], ["altId", "altId"], ["productId", "productId"], ["llid", "detailLlid"], ["url", "detailUrl"]];
    for (const [rk, dk] of keys) {
      if (!ref[rk]) continue;
      for (const c of cards) if (c && c.dataset && c.dataset[dk] === ref[rk]) return c;
      return null;
    }
    return null;
  }
  /** Is this product a live row on the registry? Pure; exported. */
  function registryHasProduct(items, p) {
    const tail = (v) => String(v == null ? "" : v).replace(/^gid:\/\/shopify\/Product\//, "");
    return (items || []).some((it) => {
      if (!it || it.status === "removed") return false;
      if (p.source === "shopify") return !!p.shopifyProductId && it.shopifyProductId != null && tail(it.shopifyProductId) === tail(p.shopifyProductId);
      return (!!p.lushlifeProductId && String(it.lushlifeProductId || "") === String(p.lushlifeProductId))
        || (!!p.externalUrl && it.externalUrl === p.externalUrl);
    });
  }
  function editHasPicker(variantData) {
    if (!variantData) return false;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    return variants.length > 1 && variantOptionGroups(variantData).length > 0;
  }
  /** none | picker | added | adding | loading | add. Pure; exported. */
  function editCtaFor(s) {
    if (editHasPicker(s.variantData)) return { kind: "picker" };
    if (s.onRegistry) return { kind: "added" };
    if (s.adding) return { kind: "adding" };
    if (!s.hasAddPath) return { kind: "none" };
    if (!s.variantsSettled) return { kind: "loading" };
    return { kind: "add", price: Number.isFinite(s.price) && s.price > 0 ? s.price : null };
  }
  function editCtaHtml(c) {
    const CHECK = '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
    const PLUS = '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
    let btn = "";
    if (c.kind === "add") btn = `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--add" data-detail-add>${PLUS} Add to registry${c.price != null ? ` · $${c.price.toFixed(2)}` : ""}</button>`;
    else if (c.kind === "loading") btn = `<button type="button" class="re-detail__variant-cta" disabled>Add to registry</button>`;
    else if (c.kind === "adding") btn = `<button type="button" class="re-detail__variant-cta" disabled>Adding…</button>`;
    else if (c.kind === "added") btn = `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--remove" disabled>${CHECK} In your registry</button>`;
    return btn ? `<div class="re-detail__variant-action re-detail__variant-action--plain">${btn}</div>` : "";
  }
  /** The card's own add for its type, awaited (handleProductDrop's dispatch,
   *  which was fire-and-forget for drag-and-drop). */
  function addCard(card) {
    if (card.matches(".re-added-card--suggested")) {
      const id = card.dataset.cardItem || card.querySelector("[data-sugg-keep]")?.getAttribute("data-sugg-keep");
      return id ? handleKeepSuggestion(id) : Promise.resolve();
    }
    return Promise.resolve(handleProductDrop(card));
  }
  // Exported for unit/detail-popup-add.test.cjs.
  window.BirchRegistryEdit = {
    _editCtaFor: editCtaFor, _editCtaHtml: editCtaHtml,
    _registryHasProduct: registryHasProduct, _cardRefFor: cardRefFor, _findCardIn: findCardIn,
  };

  // ── Variant option grid (#5, replaces the old flat row list) ───────────────
  // `variantData` is the response from API.itemVariants ({ kind, variants[,
  // options, selectedVariantId] }). Instead of one Add/Remove row per variant,
  // we render one swatch/pill ROW PER OPTION (Color, Size, …) — like the PDP
  // picker — and a single Add/Remove CTA for whatever combination is selected.
  // Selecting a different combo adds a SECOND registry item (the backend dedupes
  // by exact variant id, so two variants coexist); a combo already on the
  // registry shows Remove. Returns "" when there is nothing to choose.

  // The variant's option name→value map. External rows carry color/size; Shopify
  // rows carry selectedOptions [{name,value}].
  function variantOptionMap(v, kind) {
    if (kind === "external") {
      const m = {};
      if (v.color) m.Color = v.color;
      if (v.size) m.Size = v.size;
      return m;
    }
    const m = {};
    for (const o of (v.selectedOptions || [])) if (o && o.name) m[o.name] = o.value;
    return m;
  }
  const variantIdOf = (v, kind) => (kind === "external" ? String(v.variantId) : String(v.id));
  const variantIsAvailable = (v, kind) => (kind === "external" ? v.isAvailable !== false : v.availableForSale !== false);

  // The option groups to render: explicit Shopify `options`, or Color/Size
  // synthesized from the external variants. "Title"/"Default Title" is dropped so
  // single-option products don't render a meaningless one-pill row.
  function variantOptionGroups(variantData) {
    const kind = variantData.kind;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    if (kind === "external") {
      const groups = [];
      for (const name of ["Color", "Size"]) {
        const values = [...new Set(variants.map((v) => variantOptionMap(v, kind)[name]).filter(Boolean))];
        if (values.length) groups.push({ name, values });
      }
      return groups;
    }
    const opts = Array.isArray(variantData.options) ? variantData.options : [];
    return opts
      .filter((o) => o && o.name && o.name.toLowerCase() !== "title" && Array.isArray(o.values) && o.values.length)
      .map((o) => ({ name: o.name, values: o.values }));
  }

  // Find the variant whose options exactly match `sel` across every group.
  function variantForSelection(variantData, groups, sel) {
    const kind = variantData.kind;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    return variants.find((v) => {
      const m = variantOptionMap(v, kind);
      return groups.every((g) => m[g.name] === sel[g.name]);
    }) || null;
  }

  // Initial selection: the item's current variant, else the first available, else
  // the first variant — expanded to a full option map.
  function defaultVariantSelection(variantData, groups) {
    const kind = variantData.kind;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    const sid = variantData.selectedVariantId != null ? String(variantData.selectedVariantId) : null;
    let base = sid ? variants.find((v) => variantIdOf(v, kind) === sid) : null;
    if (!base) base = variants.find((v) => variantIsAvailable(v, kind)) || variants[0];
    return base ? variantOptionMap(base, kind) : {};
  }

  // After a pill click, keep the changed option and re-resolve the others so the
  // selection always points at a REAL variant (prefer an available one). Avoids
  // dead "no such combination" states the way the PDP picker does.
  function repairVariantSelection(variantData, groups, sel, changedName) {
    const kind = variantData.kind;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    if (variantForSelection(variantData, groups, sel)) return sel;
    const honorsChanged = (v) => variantOptionMap(v, kind)[changedName] === sel[changedName];
    const pick = variants.find((v) => variantIsAvailable(v, kind) && honorsChanged(v))
      || variants.find((v) => honorsChanged(v));
    return pick ? variantOptionMap(pick, kind) : sel;
  }

  function renderVariantList(variantData, esc, selection, anchorItem) {
    if (!variantData) return "";
    const kind = variantData.kind;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    if (variants.length <= 1) return "";
    const groups = variantOptionGroups(variantData);
    if (!groups.length) return "";
    const sel = selection || defaultVariantSelection(variantData, groups);

    // This popup's own item has no specific variant on record yet — it was
    // added via a path that couldn't identify (or skipped identifying) which
    // option it is: an ambiguous handle auto-resolve, a carousel/alternates/
    // preset quick-add, etc. Guessing "first available variant is the one
    // that's already on the registry" is exactly the bug this UI must not
    // repeat — every pill would otherwise show a false "Add" for an item the
    // registrant already has, and clicking it would silently duplicate/merge
    // into the wrong row server-side. Surface it honestly instead: the
    // registrant must confirm which option they already have via the picker
    // (the click resolves it in place through the same PATCH the manual
    // variant-switch uses — see the data-variant-confirm branch in the
    // delegated click handler below, in openProductDetail).
    const unresolved = anchorItem && anchorItem.id && variantData.selectedVariantId == null;

    // A value is selectable when some AVAILABLE variant carries it alongside the
    // other currently-selected options (greys out true dead-ends only).
    const valueSelectable = (group, value) => variants.some((v) => {
      if (!variantIsAvailable(v, kind)) return false;
      const m = variantOptionMap(v, kind);
      if (m[group.name] !== value) return false;
      return groups.every((g) => g.name === group.name || !sel[g.name] || m[g.name] === sel[g.name]);
    });

    const groupsHtml = groups.map((g) => {
      const pills = g.values.map((value) => {
        const isSel = sel[g.name] === value;
        const oos = !valueSelectable(g, value);
        return `<button type="button" class="re-detail__opt${isSel ? " re-detail__opt--sel" : ""}${oos ? " re-detail__opt--oos" : ""}" data-variant-opt="${esc(g.name)}" data-variant-opt-val="${esc(value)}" aria-pressed="${isSel ? "true" : "false"}">${esc(value)}</button>`;
      }).join("");
      return `<div class="re-detail__opt-group">
        <div class="re-detail__opt-lbl">${esc(g.name)}</div>
        <div class="re-detail__opt-row">${pills}</div>
      </div>`;
    }).join("");

    // Resolve the current selection → one variant → a single Add/Remove CTA.
    const resolved = variantForSelection(variantData, groups, sel);
    let ctaHtml;
    let noteHtml = "";
    if (!resolved) {
      ctaHtml = `<button type="button" class="re-detail__variant-cta" disabled>Select options</button>`;
    } else {
      const vid = variantIdOf(resolved, kind);
      const added = unresolved ? null : registryItemForVariant(vid, kind);
      const available = variantIsAvailable(resolved, kind);
      const priceTxt = resolved.price != null && resolved.price !== "" ? ` · $${Number(resolved.price).toFixed(2)}` : "";
      if (added) {
        ctaHtml = `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--remove" data-variant-remove="${esc(added.id)}"><svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> In your registry · Remove</button>`;
      } else if (unresolved) {
        // Already on the registry, but as SOME unconfirmed option — the pill
        // grid lets the registrant pick which one; that pick PATCHes this
        // same item's variant rather than adding a new row (see the delegated
        // click handler's data-variant-confirm branch).
        ctaHtml = `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--confirm" data-variant-confirm="${esc(anchorItem.id)}" data-variant-confirm-vid="${esc(vid)}"${available ? "" : " disabled"}>${available ? `Confirm this is your option${esc(priceTxt)}` : "Sold out"}</button>`;
        noteHtml = `<p class="re-detail__variant-note">This is already in your registry, but we're not sure which option — pick the one you have.</p>`;
      } else if (available) {
        ctaHtml = `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--add" data-variant-add="${esc(vid)}"><svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Add to registry${esc(priceTxt)}</button>`;
      } else {
        ctaHtml = `<button type="button" class="re-detail__variant-cta" disabled>Sold out</button>`;
      }
    }

    return `
      <div class="re-detail__variants">
        <div class="re-detail__variants-lbl">Options</div>
        ${groupsHtml}
        <div class="re-detail__variant-action">${ctaHtml}</div>
        ${noteHtml}
      </div>`;
  }

  // Find the registry item (if any) that represents a given variant id, so the
  // list can render Remove + mark it selected. Variant ids are unique per product,
  // so an id match is a safe identity check across both sources.
  function registryItemForVariant(variantId, kind) {
    const items = (state.registry && state.registry.items) || [];
    if (kind === "external") {
      const want = String(variantId);
      return items.find((it) => it.externalVariantId != null && String(it.externalVariantId) === want) || null;
    }
    // Shopify variant ids arrive in two shapes: the GID form from the picker
    // (getProductVariants) and the bare-numeric form from optimistic carousel
    // adds / legacy rows. Compare on the numeric tail so both forms match.
    const tail = (v) => String(v == null ? "" : v).replace(/^gid:\/\/shopify\/ProductVariant\//, "");
    const want = tail(variantId);
    return items.find((it) => it.shopifyVariantId != null && tail(it.shopifyVariantId) === want) || null;
  }

  // Build the addItem payload for a chosen variant. Pulls product-level context
  // (handle, productType, lushlife id, retailer) from the popup's product `item`
  // and the variant-level fields (id, price, color/size, image) from `variantData`.
  // The variant label is appended to the title so two variants of the same product
  // read as distinct rows on the registry.
  function buildVariantAddPayload(variantId, variantData, product, detail) {
    if (!variantData) return null;
    const kind = variantData.kind;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    const baseTitle = String((product && product.title) || (detail && detail.title) || "");
    const productType = (product && product.productType) || undefined;
    const currency = (product && product.currency) || (detail && detail.currency) || "USD";
    const cents = (v) => (v != null && v !== "" && Number.isFinite(Number(v)) ? Math.round(Number(v) * 100) : undefined);
    const withLabel = (label) => (label && !baseTitle.toLowerCase().includes(label.toLowerCase()) ? `${baseTitle} — ${label}` : baseTitle);
    if (kind === "external") {
      const v = variants.find((x) => String(x.variantId) === String(variantId));
      if (!v) return null;
      const label = [v.color, v.size].filter(Boolean).join(" · ");
      return {
        source: "external",
        title: withLabel(label),
        imageUrl: v.image || (product && product.imageUrl) || (detail && detail.image) || undefined,
        priceCents: cents(v.price),
        currency,
        productType,
        externalUrl: v.buyLink || (product && product.externalUrl) || undefined,
        externalRetailer: (product && product.externalRetailer) || variantData.retailer || undefined,
        externalVariantId: String(v.variantId),
        externalColor: v.color || undefined,
        externalSize: v.size || undefined,
        lushlifeProductId: (product && product.lushlifeProductId) || undefined,
        priority: "nice_to_have",
        quantityWanted: 1,
      };
    }
    const v = variants.find((x) => String(x.id) === String(variantId));
    if (!v) return null;
    const label = v.title && v.title !== "Default Title" ? v.title : "";
    return {
      source: "shopify",
      title: withLabel(label),
      imageUrl: v.image || (product && product.imageUrl) || (detail && detail.image) || undefined,
      priceCents: cents(v.price),
      currency,
      productType,
      shopifyProductId: (product && product.shopifyProductId) || variantData.productId || undefined,
      shopifyVariantId: String(v.id),
      shopifyHandle: (product && product.shopifyHandle) || undefined,
      priority: "nice_to_have",
      quantityWanted: 1,
    };
  }

  // TrustScore block for the popup (#6) — mirrors the PDP "CurateIQ Score" card:
  // the overall number plus a per-criterion breakdown (Safety / Ease / Comfort /
  // Longevity / Value) with bars, and an optional "View details" disclosure that
  // reveals each criterion's reasoning. Scores are 0–100. Renders nothing when no
  // score AND no breakdown are available (only shown when available).
  function renderTrustBlock(d, esc) {
    const raw = d.trustScore;
    const overall = raw != null && Number.isFinite(Number(raw)) ? Math.round(Math.max(0, Math.min(100, Number(raw)))) : null;
    const bd = Array.isArray(d.trustBreakdown)
      ? d.trustBreakdown.filter((c) => c && c.criteria != null && Number.isFinite(Number(c.score)))
      : [];
    if (overall == null && !bd.length) return "";
    const head = overall != null
      ? `<div class="re-detail__trust-head"><span class="re-detail__trust-num">${overall}</span><span class="re-detail__trust-lbl">TrustScore</span></div>`
      : "";
    if (!bd.length) {
      return `<div class="re-detail__trust">${head}</div>`;
    }
    const clamp = (n) => Math.round(Math.max(0, Math.min(100, Number(n))));
    const rows = bd.map((c) => {
      const s = clamp(c.score);
      return `<li class="re-detail__bd-item">
        <div class="re-detail__bd-row"><span class="re-detail__bd-name">${esc(String(c.criteria))}</span><span class="re-detail__bd-score">${s}</span></div>
        <div class="re-detail__bd-track" role="progressbar" aria-valuenow="${s}" aria-valuemin="0" aria-valuemax="100" aria-label="${esc(String(c.criteria))} score ${s} out of 100"><span class="re-detail__bd-fill" style="width:${s}%"></span></div>
      </li>`;
    }).join("");
    const withWhy = bd.filter((c) => c.reasoning);
    const details = withWhy.length
      ? `<details class="re-detail__bd-details"><summary class="re-detail__bd-toggle"><span class="re-detail__bd-toggle-show">View details</span><span class="re-detail__bd-toggle-hide">Hide details</span></summary>
          <div class="re-detail__bd-why">${withWhy.map((c) => {
            const wpct = Number.isFinite(Number(c.weight)) ? ` <span class="re-detail__bd-weight">(${Math.round(Number(c.weight) * 100)}% of score)</span>` : "";
            return `<p><strong>${esc(String(c.criteria))}.</strong> ${esc(String(c.reasoning))}${wpct}</p>`;
          }).join("")}</div>
        </details>`
      : "";
    return `<div class="re-detail__trust re-detail__trust--card">
      ${head}
      <div class="re-detail__bd"><ul class="re-detail__bd-list">${rows}</ul>${details}</div>
    </div>`;
  }

  function renderDetailBody(d, state, esc, item, variantData, variantSelection, plainCtaHtml) {
    const money = (v, c) => (v == null ? "" : new Intl.NumberFormat("en-US", { style: "currency", currency: c || "USD" }).format(Number(v)));
    // Show the brand only. No fallback label — when the brand is unknown the
    // line renders blank (a non-breaking space below) so the card keeps its
    // height and the layout stays consistent.
    const subtitle = d.brand || "";
    const list = (arr) => Array.isArray(arr) ? arr.filter(Boolean) : [];
    const features = list(d.keyFeatures), pros = list(d.pros), cons = list(d.cons);
    const specs = Array.isArray(d.specifications) ? d.specifications : [];
    const certs = list(d.certifications), materials = list(d.materials);
    const description = typeof d.description === "string" ? d.description.trim() : "";
    const hasAny = description || features.length || pros.length || cons.length || specs.length || certs.length || materials.length;

    const birchPdp = d.source === "shopify" && item.shopifyHandle
      ? `/products/${encodeURIComponent(item.shopifyHandle)}` : null;
    const productUrl = d.externalUrl || birchPdp;

    const trustHtml = renderTrustBlock(d, esc);

    const descHtml = description ? `
      <section class="re-detail__section">
        <h3>Description</h3>
        <p class="re-detail__desc">${esc(description)}</p>
      </section>` : "";

    const featuresHtml = features.length ? `
      <section class="re-detail__section">
        <h3>Key features</h3>
        <ul class="re-detail__feats">${features.map((f) => `<li>${esc(f)}</li>`).join("")}</ul>
      </section>` : "";

    const prosConsHtml = (pros.length || cons.length) ? `
      <section class="re-detail__section">
        <h3>Pros &amp; cons</h3>
        <div class="re-detail__pc">
          ${pros.length ? `<div class="re-detail__pc-col"><div class="re-detail__pc-head re-detail__pc-head--pro">Pros</div><ul>${pros.map((p) => `<li>${esc(p)}</li>`).join("")}</ul></div>` : ""}
          ${cons.length ? `<div class="re-detail__pc-col"><div class="re-detail__pc-head re-detail__pc-head--con">Cons</div><ul>${cons.map((c) => `<li>${esc(c)}</li>`).join("")}</ul></div>` : ""}
        </div>
      </section>` : "";

    const specsHtml = specs.length ? `
      <section class="re-detail__section">
        <h3>Specifications</h3>
        <dl class="re-detail__specs">${specs.map((row) => {
          const label = Array.isArray(row) ? row[0] : row && row.label;
          const value = Array.isArray(row) ? row[1] : row && row.value;
          return `<div class="re-detail__spec-row"><dt>${esc(String(label ?? ""))}</dt><dd>${esc(String(value ?? ""))}</dd></div>`;
        }).join("")}</dl>
      </section>` : "";

    const chips = (label, items) => items.length ? `
      <div class="re-detail__chips-block"><div class="re-detail__chips-lbl">${label}</div><div class="re-detail__chips">${items.map((x) => `<span class="re-detail__chip">${esc(x)}</span>`).join("")}</div></div>` : "";
    const moreHtml = (certs.length || materials.length) ? `
      <section class="re-detail__section"><h3>More</h3>${chips("Certifications", certs)}${chips("Materials", materials)}</section>` : "";

    const skeleton = `<div class="re-detail__skeleton"><span></span><span></span><span></span></div>`;
    const emptyMsg = state === "error"
      ? `<p class="re-detail__empty">We couldn’t load extra details right now.</p>`
      : (state === "ready" && !hasAny ? `<p class="re-detail__empty">No additional details available yet.</p>` : "");

    // Reflect the picker's currently-selected variant in the header image + price.
    // `d` (the product-level detail/fallback) never changes as pills are clicked,
    // so without this the top stays on the default variant while the options and
    // CTA below update — reads as a bug. Fall back to `d`'s values when the
    // selected variant carries no image/price (e.g. some scraped externals).
    let displayImage = d.image;
    let displayPrice = d.price;
    if (variantData) {
      const groups = variantOptionGroups(variantData);
      if (groups.length) {
        const sel = variantSelection || defaultVariantSelection(variantData, groups);
        const resolved = variantForSelection(variantData, groups, sel);
        if (resolved) {
          if (resolved.image) displayImage = resolved.image;
          if (resolved.price != null && resolved.price !== "") displayPrice = Number(resolved.price);
        }
      }
    }

    return `
      <div class="re-detail__cols">
        <div class="re-detail__media">
          ${displayImage ? `<img src="${esc(displayImage)}" alt="" class="re-detail__img">` : `<div class="re-detail__img-ph">🌿</div>`}
          <div class="re-detail__price">${money(displayPrice, d.currency)}</div>
          ${renderVariantList(variantData, esc, variantSelection, item) || plainCtaHtml || ""}
          ${trustHtml}
        </div>
        <div class="re-detail__main">
          <h2 class="re-detail__title">${esc(d.title || "")}</h2>
          <div class="re-detail__sub">${esc(subtitle) || "&nbsp;"}</div>
          ${state === "loading" ? skeleton : `${descHtml}${featuresHtml}${prosConsHtml}${specsHtml}${moreHtml}${emptyMsg}`}
        </div>
      </div>
      ${productUrl ? `<div class="re-detail__foot"><a class="re-detail__buylink" href="${esc(productUrl)}" target="_blank" rel="noopener">${d.source === "shopify" ? "View product" : `View at ${esc(d.brand || "retailer")}`}<span class="re-detail__buylink-ext" aria-hidden="true">↗</span></a></div>` : ""}`;
  }

  // Category panels above the target can still be lazy-rendering (product
  // cards, real product images) when a nav click fires — scrolling on a fixed
  // delay races that content and lands short once it finishes shifting layout
  // (CUR-44; same root cause the Add-from-Link popup below was changed to
  // sidestep). A single category panel can hold 80+ images, and on a fresh,
  // uncached load a large chunk of those are still mid-load a full second or
  // more after the click — verified directly (44 of 82 incomplete 1.5s in on
  // one test run) — so this needs real headroom, not a couple of frames.
  //
  // A single "wait for stable, then scroll once" isn't enough on its own: a
  // *smooth* scrollIntoView takes several hundred ms to animate to the offset
  // it computed at the moment it was called, and more images can finish
  // loading — shifting the target further — during that animation itself, so
  // the finished scroll still lands short (measured directly: it does).
  // Instead this keeps watching for up to TOTAL_WATCH_MS: it scrolls smoothly
  // the first time the target holds still for STABLE_MS (normal UX for the
  // common already-cached case), then instantly snap-corrects any further
  // shift it detects afterward — instant, not smooth, so a second shift
  // doesn't cost another few hundred ms chasing a target that might move yet
  // again.
  function scrollToStablePosition(el, opts) {
    if (!el) return;
    const behavior = (opts && opts.behavior) || "smooth";
    const block = (opts && opts.block) || "start";
    const TOTAL_WATCH_MS = 3000;
    const STABLE_MS = 120;
    const start = performance.now();
    let lastTop = el.getBoundingClientRect().top;
    let stableSince = start;
    let scrolled = false;
    function tick(now) {
      const top = el.getBoundingClientRect().top;
      if (Math.abs(top - lastTop) >= 1) {
        lastTop = top;
        stableSince = now;
        if (scrolled && Math.abs(top) > 4) el.scrollIntoView({ behavior: "instant", block });
      }
      if (!scrolled && now - stableSince >= STABLE_MS) {
        el.scrollIntoView({ behavior, block });
        scrolled = true;
      }
      if (now - start < TOTAL_WATCH_MS) {
        requestAnimationFrame(tick);
      } else if (!scrolled) {
        el.scrollIntoView({ behavior, block });
      }
    }
    requestAnimationFrame(tick);
  }

  // ── Mobile hamburger toggle for the left rail (CUR-44) ─────────────────────
  // No-op ≥861px — the toggle button and .rcp-builder-nav--open class are both
  // inert there (registry.css only reads them inside the 860px media query).
  //
  // closeMobileNav() is called directly (not via a separate delegated click
  // listener) from wireCategoryNav's handlers below, synchronously before
  // scrollToStablePosition — collapsing the open drawer shifts everything
  // below it up the page, and scrollToStablePosition needs to measure the
  // target's *post-collapse* position, not race a second listener to find out
  // whether the collapse happened first. Relying on event-bubble order between
  // two independently-wired listeners was the original version of this bug.
  function closeMobileNav() {
    const nav = document.querySelector(".rcp-builder-nav");
    if (!nav) return;
    nav.classList.remove("rcp-builder-nav--open");
    document.getElementById("rcp-nav-toggle")?.setAttribute("aria-expanded", "false");
    document.querySelector(".rcp-nav-sheet-backdrop")?.remove();
    document.body.classList.remove("rcp-nav-sheet-open");
  }

  function wireNavToggle() {
    const nav = document.querySelector(".rcp-builder-nav");
    const toggle = document.getElementById("rcp-nav-toggle");
    if (!nav || !toggle) return;
    toggle.addEventListener("click", () => {
      if (nav.classList.contains("rcp-builder-nav--open")) {
        closeMobileNav();
        return;
      }
      nav.classList.add("rcp-builder-nav--open");
      toggle.setAttribute("aria-expanded", "true");
      // The sheet floats fixed above the page — a backdrop makes that legible
      // (dims the content behind it) and gives a click target to dismiss,
      // matching the existing tracker sheets (.ft-sheet-backdrop and co).
      const backdrop = document.createElement("div");
      backdrop.className = "rcp-nav-sheet-backdrop";
      backdrop.addEventListener("click", closeMobileNav);
      document.body.appendChild(backdrop);
      document.body.classList.add("rcp-nav-sheet-open");
    });
  }

  // ── Category nav: identical UX to original create / edit pages ────────────
  function wireCategoryNav() {
    document.querySelectorAll("[data-cat-nav]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const panelId = btn.dataset.catNav;
        document.querySelectorAll("[data-cat-nav]").forEach((b) => b.classList.remove("rcp-nav-item--active"));
        btn.classList.add("rcp-nav-item--active");
        document.querySelectorAll("[data-cat-panel]").forEach((p) => p.classList.remove("rcp-cat-panel--active"));
        const panel = document.getElementById(panelId);
        if (panel) panel.classList.add("rcp-cat-panel--active");
        persistActivePanel(panelId);
        // Land at the top of the freshly-shown panel — otherwise the scroll
        // position is held over from the previous (often taller) panel, leaving
        // the user stranded near the page bottom. Mirrors the subcat handler.
        // Close the mobile drawer first — see closeMobileNav's comment above.
        closeMobileNav();
        scrollToStablePosition(panel);
      });
    });
    document.querySelectorAll("[data-expand-nav]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const idx = btn.dataset.expandNav;
        const sublist = document.getElementById("rcp-nav-subcats-" + idx);
        const entry = btn.closest(".rcp-nav-entry");
        if (!sublist) return;
        const isOpen = !sublist.hidden;
        sublist.hidden = isOpen;
        btn.setAttribute("aria-expanded", String(!isOpen));
        btn.classList.toggle("rcp-nav-item__expand--open", !isOpen);
        if (entry) entry.classList.toggle("rcp-nav-entry--open", !isOpen);
      });
    });
    document.querySelectorAll("[data-subcat-nav]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const catIdx = btn.dataset.subcatNav;
        const panelId = "rcp-cat-" + catIdx;
        const targetId = btn.dataset.subcatTarget;
        document.querySelectorAll("[data-cat-nav]").forEach((b) => b.classList.remove("rcp-nav-item--active"));
        const catBtn = document.querySelector(`[data-cat-nav="${panelId}"]`);
        if (catBtn) catBtn.classList.add("rcp-nav-item--active");
        document.querySelectorAll("[data-cat-panel]").forEach((p) => p.classList.remove("rcp-cat-panel--active"));
        const panel = document.getElementById(panelId);
        if (panel) panel.classList.add("rcp-cat-panel--active");
        persistActivePanel(panelId);
        const target = document.getElementById(targetId);
        closeMobileNav();
        scrollToStablePosition(target);
      });
    });
    // "More … options" links live inside the dynamically-rendered All-items
    // groups, so they're wired by delegation (one listener, survives re-renders).
    // Each jumps to its category panel and scrolls to the "Explore Vetted Picks"
    // header (#rcp-more-<idx>) added in registry-edit.liquid.
    if (!document.body.dataset.rcpMoreNavWired) {
      document.body.dataset.rcpMoreNavWired = "1";
      document.addEventListener("click", (e) => {
        const link = e.target.closest("[data-more-nav]");
        if (!link) return;
        const idx = link.dataset.moreNav;
        activatePanel("rcp-cat-" + idx);
        const target = document.getElementById("rcp-more-" + idx);
        closeMobileNav();
        scrollToStablePosition(target);
      });
    }
  }

  // ── Hero top-action row (Registry Revamp #7): Share registry + Add by Link. ──
  // "All registries" + "Settings" chips are revealed by their own helpers
  // (revealAllRegistriesChip / setSettingsButtonVisibility).
  function renderTopActions(r) {
    // CUR-151 — reopen the guided walkthrough for THIS registry (any registry,
    // finished or not; registry-start.js honours `reopen=1`).
    const guided = document.getElementById("rcp-guided-chip");
    if (guided && r?.id && r.status !== "ended") {
      guided.href = `/pages/registry-start?id=${encodeURIComponent(r.id)}&reopen=1`;
      // CUR-157 — once it's live, going back in is about changing preferences
      // (the walkthrough then finishes with "Done", not "Publish").
      const guidedLabel = guided.querySelector("[data-rcp-guided-label]");
      if (guidedLabel) guidedLabel.textContent = r.status === "published" ? "Update preferences" : "Guided setup";
      guided.hidden = false;
    } else if (guided) {
      guided.hidden = true;
    }
    const share = document.getElementById("rcp-share-chip");
    if (share && r?.id) {
      share.href = `/pages/registry-share?id=${encodeURIComponent(r.id)}`;
      share.hidden = false;
      // The share chip is a persistent Liquid element and renderTopActions runs
      // on every sidebar render, so bind exactly once — otherwise each re-render
      // stacks another handler and one tap opens N modals.
      if (isGuest() && !share.dataset.guestBound) {
        // Keep it visible — a guest should SEE that sharing exists — but route
        // the click into the sign-in prompt instead of a page that would 401.
        share.dataset.guestBound = "1";
        share.addEventListener("click", (e) => {
          e.preventDefault();
          openSignInPrompt(
            "share your registry",
            "Sharing gives your registry its own link so friends and family can see it and send gifts.",
          );
        });
      }
    }
    // CUR-167 — "+ New registry". Signed in: the link goes to the build choice
    // with a "new" intent. A guest holds one registry, so the click asks them
    // to sign in (keeping this one) or clear it and start over.
    const newChip = document.getElementById("rcp-new-registry-chip");
    if (newChip && r?.id) {
      newChip.hidden = false;
      if (isGuest() && !newChip.dataset.guestBound) {
        newChip.dataset.guestBound = "1";
        newChip.addEventListener("click", (e) => {
          e.preventDefault();
          openStartOverPrompt();
        });
      }
    }
  }

  // CUR-167 — a guest's "+ New registry". Sign in keeps this registry (it is
  // claimed into the account) and lands on a fresh one; "Clear and start over"
  // ends this one for good and starts a fresh guest registry.
  function openStartOverPrompt() {
    const { escapeHtml: esc, API } = window.BirchRegistry;
    const r = state.registry;
    if (!r?.id) return;
    const count = (r.items || []).filter((i) => i && i.status !== "removed").length;
    const lost = count > 0
      ? `This clears your current registry and its ${count} item${count === 1 ? "" : "s"}. It can't be undone.`
      : "This clears your current registry. It can't be undone.";
    const backdrop = document.createElement("div");
    backdrop.className = "registry-modal-backdrop";
    backdrop.innerHTML = `
      <div class="registry-modal rcp-start-over" role="dialog" aria-modal="true" aria-labelledby="rcp-start-over-title" style="max-width:420px">
        <h2 id="rcp-start-over-title" style="margin:0 0 10px;font-size:1.2rem">Start a new registry?</h2>
        <p style="font-size:0.9rem;color:var(--reg-muted);margin:0 0 18px;line-height:1.55">
          Without an account you can keep one registry at a time. Sign in to keep this one and start another.
        </p>
        <a class="registry-btn registry-btn--primary registry-btn--block" href="${esc(window.BirchRegistry.signInUrl?.("/pages/registry-create?new=1") || signInHref())}">Sign in to keep this registry</a>
        <div class="rcp-start-over__clear">
          <p class="rcp-start-over__warn" id="rcp-start-over-warn">${esc(lost)}</p>
          <button type="button" class="registry-btn registry-btn--secondary registry-btn--block rcp-start-over__btn" data-action="clear" aria-describedby="rcp-start-over-warn">Clear and start over</button>
          <p class="rcp-start-over__error" data-start-over-error role="alert" hidden></p>
        </div>
        <button type="button" class="rcp-start-over__keep" data-action="close">Keep building this one</button>
      </div>`;
    document.body.appendChild(backdrop);
    const close = () => backdrop.remove();
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
    backdrop.querySelector("[data-action=close]").addEventListener("click", close);
    const btn = backdrop.querySelector("[data-action=clear]");
    const error = backdrop.querySelector("[data-start-over-error]");
    btn.addEventListener("click", async () => {
      btn.disabled = true;
      btn.textContent = "Clearing…";
      error.hidden = true;
      try {
        const res = await API.startOver(r.id);
        const fresh = res?.registry;
        if (!fresh?.id) throw new Error("no_registry");
        // The cleared id must never be reopened from memory.
        try { localStorage.removeItem(LS_KEY); } catch (_) {}
        try { localStorage.removeItem(SESSION_CHOICE_KEY); } catch (_) {}
        location.href = `/pages/registry-start?id=${encodeURIComponent(fresh.id)}`;
      } catch (e) {
        error.textContent = e?.body?.message || "Couldn't clear your registry. Try again.";
        error.hidden = false;
        btn.disabled = false;
        btn.textContent = "Clear and start over";
      }
    });
  }

  // ── Top "Review & Publish" bar (relocated from the left rail, #6) ──
  // Rendered above "Currently in your registry", under the rail title, with a
  // divider. Shares the publishOrShare handler with the (now removed) sidebar row.
  function renderPublishBar(registry) {
    const bar = document.getElementById("re-publish-bar");
    if (!bar) return;
    if (!registry || registry.status === "ended") {
      bar.innerHTML = "";
      bar.hidden = true;
      return;
    }
    const r = registry;
    const isPub = r.status === "published";
    if (isGuest()) {
      // Publishing needs a Shopify customer (shareable page, gift tracking,
      // completion discount), so a guest gets the same prominent bar with
      // honest copy and a sign-in destination rather than a hidden feature.
      bar.innerHTML = `
        <button type="button" id="re-publish-btn" class="rcp-publish-bar__btn">
          <span class="rcp-publish-bar__icon"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>
          <span class="rcp-publish-bar__text">
            <span class="rcp-publish-bar__label">Sign in to save &amp; publish</span>
            <span class="rcp-publish-bar__sub">Your registry is saved — sign in to keep it and share it</span>
          </span>
          <svg class="rcp-publish-bar__chev" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
        </button>`;
      bar.hidden = false;
      document.getElementById("re-publish-btn")?.addEventListener("click", () =>
        openSignInPrompt(
          "publish your registry",
          "Publishing gives your registry a shareable link, tracks gifts as they arrive, and lets you choose when they ship.",
        ));
      return;
    }
    bar.innerHTML = `
      <button type="button" id="re-publish-btn" class="rcp-publish-bar__btn">
        <span class="rcp-publish-bar__icon">${isPub
          ? `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-6"/></svg>`
          : `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>`}</span>
        <span class="rcp-publish-bar__text">
          <span class="rcp-publish-bar__label">${isPub ? "View progress" : "Review & publish"}</span>
          <span class="rcp-publish-bar__sub">${isPub ? "See gift activity" : "Check your gifts, then publish"}</span>
        </span>
        <svg class="rcp-publish-bar__chev" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
      </button>`;
    bar.hidden = false;
    document.getElementById("re-publish-btn")?.addEventListener("click", () => publishOrShare());
  }

  // ── Left-rail actions: delivery + publish (only when registry exists) ─
  function renderRightSidebar(registry) {
    // Review & Publish now lives in the top bar above the registry (#6).
    renderPublishBar(registry);
    const slot = document.getElementById("re-nav-actions");
    if (!slot) return;
    if (!registry) {
      slot.innerHTML = "";
      slot.hidden = true;
      return;
    }
    const r = registry;

    if (r.status === "ended") {
      const endedNote = r.endedAt
        ? `Ended ${new Date(r.endedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`
        : "Registry ended";
      slot.innerHTML = `
        <div class="rcp-nav-actions__note">
          <div class="rcp-nav-actions__note-title">Registry complete 🎉</div>
          <p>${endedNote}. Held shipments have been released. You can still view items and gift activity any time.</p>
        </div>`;
      slot.hidden = false;
      return;
    }

    const esc = window.BirchRegistry.escapeHtml;
    // Right-pointing chevron, matching the rail's nav-item visual weight. Only
    // for rows that leave the builder for another page — see popupHint below
    // for rows that open something in place instead (CUR-44: these used to
    // share one icon, which read as "these two rows behave the same").
    const chev = `<svg class="rcp-nav-action__chev" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>`;
    // Box-with-arrow glyph — for rows that open a popup over the current page
    // rather than navigating away from it. Same 14px box and stroke weight as
    // the chevron above, so the two read as a pair at the right edge of a row.
    const popupHint = `<svg class="rcp-nav-action__chev" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`;
    const rows = [];

    // ── Add by Link row (#7 — moved here from the hero top, above delivery) ──
    // Opens openAddLinkModal() in place (see the click handler below) — a
    // popup, not a page — so it gets popupHint rather than the page chevron
    // it used to share with the Delivery row just below.
    rows.push(`
      <button type="button" id="re-addlink-action" class="rcp-nav-action">
        <span class="rcp-nav-action__icon"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg></span>
        <span class="rcp-nav-action__info">
          <span class="rcp-nav-action__label">Add from Link</span>
        </span>
        ${popupHint}
      </button>`);

    // ── Delivery row ──
    // CUR-158 — "set" only once the parent has really chosen. The untouched
    // default (ship_on_date with no week) is not a choice; same rule as
    // registry-delivery-choice.js hasChoice() and the publish gate.
    const hasDelivery = r.deliverySetting === "manual_release"
      || r.deliverySetting === "ship_as_gifted"
      || (r.deliverySetting === "ship_on_date" && !!r.deliveryDate);
    let deliveryValue = "Choose when gifts ship to you";
    if (hasDelivery) {
      deliveryValue = deliveryLabel(r.deliverySetting);
      if (r.deliverySetting === "ship_on_date" && r.deliveryDate) {
        const d = new Date(r.deliveryDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        deliveryValue = `${deliveryLabel(r.deliverySetting)} · ${d}`;
      }
    }
    // Delivery timing + shipping address are sign-in-only (they're customer PII
    // and drive real fulfilment), so for a guest the row becomes a prompt.
    rows.push(`
      <a href="${isGuest() ? "#" : `/pages/registry-delivery?id=${encodeURIComponent(r.id)}`}"${isGuest() ? ' data-guest-gate="delivery"' : ""} class="rcp-nav-action${hasDelivery ? " rcp-nav-action--set" : ""}">
        <span class="rcp-nav-action__icon"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="1" y="3" width="15" height="13" rx="1.5"/><path d="M16 8h4l3 3v5h-7z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg></span>
        <span class="rcp-nav-action__info">
          <span class="rcp-nav-action__label">${hasDelivery ? "Shipping" : "Set shipping"}</span>
          <span class="rcp-nav-action__value">${esc(isGuest() ? "Sign in to choose when gifts ship" : deliveryValue)}</span>
        </span>
        ${chev}
      </a>`);

    // ── Shipping address row REMOVED (Registry Revamp #6) ──
    // Setting the shipping address now lives on the registry-delivery page (a
    // clearer home alongside delivery timing). The edit-page sidebar no longer
    // carries it. showAddressNudge() is retained for the delivery page's use.

    // ── Review & Publish row RELOCATED (#6) ──
    // The primary "Review & publish" CTA now lives in the top #re-publish-bar
    // (renderPublishBar), above "Currently in your registry" — no longer here.

    // ── Share row REMOVED from sidebar (Registry Revamp #7) ──
    // Share registry moved to the hero top-action row (renderTopActions).

    slot.innerHTML = rows.join("");
    slot.hidden = false;
    slot.querySelector('[data-guest-gate="delivery"]')?.addEventListener("click", (e) => {
      e.preventDefault();
      openSignInPrompt(
        "set delivery",
        "Delivery timing and your shipping address are tied to your account, so gifts reach the right place at the right time.",
      );
    });
    document.getElementById("re-addlink-action")?.addEventListener("click", () => {
      // Open the focused paste-a-link popup (openAddLinkModal) rather than
      // scrolling to the inline bar — the async-rendered cards above it made the
      // smooth-scroll target shift and land short. Both routes share the same
      // add pipeline, so behavior is identical once a link is pasted.
      openAddLinkModal();
    });
    // Top-action row reflects the current registry (share visibility, links).
    renderTopActions(r);
  }

  // CUR-45: hold_until_date/decide_later/ship_by_stage/ship_now removed —
  // validated against live data first (birch-registry-staging Registry
  // table: only ship_on_date and ship_as_gifted are actually in use, zero
  // rows on any legacy value), so this is a straight cleanup, not a
  // behavior change for any real registry. Canonical 3 match the backend's
  // exact DELIVERY_SETTINGS (Workers/birch-registry app/lib/constants.js).
  function deliveryLabel(setting) {
    return ({
      // CUR-158: the same three titles as registry-delivery-choice.js OPTIONS.
      ship_on_date: "Arrive in a week you choose",
      manual_release: "I\u2019ll release them",
      ship_as_gifted: "Ship right away",
    })[setting] || "Ship right away";
  }

  // ── Publish / share / due-date / address ──────────────────────────────────
  async function publishOrShare() {
    const r = state.registry;
    if (!r) return;
    if (guestGate(
      "publish your registry",
      "Publishing gives your registry a shareable link, tracks gifts as they arrive, and lets you choose when they ship.",
    )) return;
    // Published registries jump to the progress page ("View Progress"); the
    // separate "Share Registry" link below the button covers sharing.
    if (r.status === "published") {
      location.href = `/pages/registry-progress?id=${encodeURIComponent(r.id)}`;
      return;
    }
    // For an unpublished registry the primary button is now "Review & Publish".
    // Publishing itself moved to the progress page (review your gifts first),
    // so this just routes there — the due-date/address gate + publish action
    // live on registry-progress now.
    location.href = `/pages/registry-progress?id=${encodeURIComponent(r.id)}`;
  }

  async function doPublish(r) {
    const { API, showToast } = window.BirchRegistry;
    const btn = document.getElementById("re-publish-btn");
    if (btn) { btn.disabled = true; btn.textContent = "Publishing…"; }
    try {
      await API.publish(r.id);
      r.status = "published";
      showToast("Registry published!");
      location.href = `/pages/registry-share?id=${encodeURIComponent(r.id)}`;
    } catch (e) {
      if (e?.body?.error === "missing_due_date") {
        if (btn) { btn.disabled = false; btn.textContent = "Publish Registry ✦"; }
        showDueDateNudge(r);
        return;
      }
      showToast(e?.body?.message || "Could not publish — please try again.");
      if (btn) { btn.disabled = false; btn.textContent = "Publish Registry ✦"; }
    }
  }

  function showDueDateNudge(r, onSaved) {
    const { escapeHtml, showToast, API } = window.BirchRegistry;
    const existingIso = r.dueDate ? new Date(r.dueDate).toISOString().slice(0, 10) : "";
    const isoToday = new Date().toISOString().slice(0, 10);
    const backdrop = document.createElement("div");
    backdrop.className = "registry-modal-backdrop";
    backdrop.innerHTML = `
      <div class="registry-modal" style="max-width:440px" role="dialog" aria-modal="true">
        <h2 style="margin:0 0 8px;font-size:1.125rem">Pick a due date</h2>
        <p style="font-size:0.875rem;color:var(--reg-muted);margin:0 0 20px;line-height:1.55">
          Your due date powers gift-timing nudges, the auto-end of the registry, and your 15% completion discount window.
        </p>
        <form id="re-due-form" novalidate>
          <div class="registry-modal__field">
            <label for="re-due-input">Due date</label>
            <input id="re-due-input" name="dueDate" type="date" required min="${isoToday}" value="${escapeHtml(existingIso)}">
          </div>
          <div class="registry-modal__actions" style="margin-top:4px">
            <button type="button" class="registry-btn registry-btn--secondary" data-action="cancel">Cancel</button>
            <button type="submit" class="registry-btn registry-btn--primary">Save</button>
          </div>
        </form>
      </div>`;
    document.body.appendChild(backdrop);
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) backdrop.remove(); });
    backdrop.querySelector("[data-action=cancel]")?.addEventListener("click", () => backdrop.remove());
    backdrop.querySelector("#re-due-form")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const val = backdrop.querySelector("#re-due-input").value;
      if (!val) { showToast?.("Pick a date first."); return; }
      try {
        const { registry: updated } = await API.update(r.id, { dueDate: val });
        state.registry = { ...state.registry, ...updated };
        backdrop.remove();
        showToast?.("Due date saved");
        if (onSaved) onSaved(); else publishOrShare();
      } catch (_) { showToast?.("Could not save due date."); }
    });
  }

  function showAddressNudge(r, onSaved) {
    const { escapeHtml, showToast, API } = window.BirchRegistry;
    const saved = r.shippingAddress || {};
    const hasSaved = !!(saved.line1 && saved.city);
    const profile = window.BirchProfileAddress || {};
    // Prefer the address already on the registry. Otherwise, if the customer
    // has a saved profile address, auto-populate it and just ask them to
    // confirm — no retyping an address Shopify already has on file.
    const fromProfile = !hasSaved && !!(profile.line1 && profile.city);
    const addr = hasSaved ? saved : (fromProfile ? profile : saved);
    const heading = fromProfile ? "Confirm your shipping address" : "Add your shipping address";
    const intro = fromProfile
      ? "We pulled this from your Birch account — confirm it's where gifts should ship, or edit it below."
      : "Before publishing, we need your shipping address so gifts can be delivered to you.";
    const backdrop = document.createElement("div");
    backdrop.className = "registry-modal-backdrop";
    backdrop.innerHTML = `
      <div class="registry-modal" style="max-width:480px" role="dialog" aria-modal="true">
        <h2 style="margin:0 0 8px;font-size:1.125rem">${heading}</h2>
        <p style="font-size:0.875rem;color:var(--reg-muted);margin:0 0 20px;line-height:1.55">
          ${intro}
        </p>
        <form id="re-addr-form" novalidate>
          <div class="registry-modal__field">
            <label for="re-addr-line1">Street address</label>
            <input id="re-addr-line1" name="line1" required placeholder="123 Main St" autocomplete="street-address" value="${escapeHtml(addr.line1 || "")}">
          </div>
          <div class="registry-modal__field">
            <label for="re-addr-line2">Apt / suite (optional)</label>
            <input id="re-addr-line2" name="line2" placeholder="Apt 4B" autocomplete="address-line2" value="${escapeHtml(addr.line2 || "")}">
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
            <div class="registry-modal__field">
              <label for="re-addr-city">City</label>
              <input id="re-addr-city" name="city" required placeholder="Springfield" autocomplete="address-level2" value="${escapeHtml(addr.city || "")}">
            </div>
            <div class="registry-modal__field">
              <label for="re-addr-zip">ZIP</label>
              <input id="re-addr-zip" name="zip" required placeholder="12345" autocomplete="postal-code" value="${escapeHtml(addr.zip || addr.postalCode || "")}">
            </div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px">
            <div class="registry-modal__field">
              <label for="re-addr-state">State</label>
              <input id="re-addr-state" name="state" placeholder="IL" autocomplete="address-level1" value="${escapeHtml(addr.state || addr.province || "")}">
            </div>
            <div class="registry-modal__field">
              <label for="re-addr-country">Country</label>
              <input id="re-addr-country" name="country" placeholder="US" autocomplete="country" value="${escapeHtml(addr.country || "US")}">
            </div>
          </div>
          <div class="registry-modal__actions" style="margin-top:4px">
            <button type="button" class="registry-btn registry-btn--secondary" data-action="cancel">Cancel</button>
            <button type="submit" class="registry-btn registry-btn--primary">Save</button>
          </div>
        </form>
      </div>`;
    document.body.appendChild(backdrop);
    const close = () => backdrop.remove();
    backdrop.querySelector('[data-action="cancel"]').addEventListener("click", close);
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
    backdrop.querySelector("#re-addr-form").addEventListener("submit", async (e) => {
      e.preventDefault();
      const form = e.target;
      const line1 = form.querySelector("#re-addr-line1").value.trim();
      const line2 = form.querySelector("#re-addr-line2").value.trim();
      const city = form.querySelector("#re-addr-city").value.trim();
      const zip = form.querySelector("#re-addr-zip").value.trim();
      const stateVal = form.querySelector("#re-addr-state").value.trim();
      const country = form.querySelector("#re-addr-country").value.trim() || "US";
      if (!line1 || !city || !zip) { showToast?.("Please fill in street, city, and ZIP."); return; }
      try {
        await API.update(r.id, { shippingAddress: { line1, line2: line2 || undefined, city, zip, state: stateVal || undefined, country } });
        r.shippingAddress = { line1, line2, city, zip, state: stateVal, country };
        close();
        if (onSaved) await onSaved(); else await doPublish(r);
      } catch (_) { showToast?.("Could not save address."); }
    });
  }

  // ── Registry name / owner / co-registrant (shared by the Settings dialog
  //    and the nav-header pencil "Edit details" dialog) ──────────────────────

  // Common-standard registry name when the owner hasn't set one:
  //   "Ana & Sam's Registry" → "Baby Smith's Registry" → "Ana's Baby Registry".
  function autoRegistryName(first, last, pFirst) {
    first = (first || "").trim(); last = (last || "").trim(); pFirst = (pFirst || "").trim();
    if (first && pFirst) return `${first} & ${pFirst}'s Registry`;
    if (last) return `Baby ${last}'s Registry`;
    if (first) return `${first}'s Baby Registry`;
    return "Baby Registry";
  }

  // Owner defaults to the signed-in customer (exposed on the create form's
  // data-attributes) when the registry has no stored owner yet. Co-registrant
  // starts empty. These power the share-page display name and the
  // auto-generated registry name.
  function getOwnerDefaults(r) {
    const form = document.getElementById("registry-create-form");
    const customerFirst = (form?.dataset.customerFirst || "").trim();
    const customerLast = (form?.dataset.customerLast || "").trim();
    const ownerFirst = r.registrantFirst != null && r.registrantFirst !== "" ? r.registrantFirst : customerFirst;
    const ownerLast = r.registrantLast != null && r.registrantLast !== "" ? r.registrantLast : customerLast;
    return {
      customerFirst, customerLast, ownerFirst, ownerLast,
      partnerFirst: r.partnerFirst || "", partnerLast: r.partnerLast || "",
    };
  }

  // The three editable fields (name, owner, co-registrant) as modal markup.
  // Shared so the full Settings dialog and the focused "Edit details" dialog
  // render — and wire (wireRegistryNameFields) — exactly the same controls.
  function registryNameFieldsHtml(r, escapeHtml) {
    const { ownerFirst, ownerLast, partnerFirst, partnerLast } = getOwnerDefaults(r);
    return `
        <div class="registry-modal__field">
          <label for="rcp-settings-name">Registry name</label>
          <input id="rcp-settings-name" name="name" type="text" maxlength="120" autocomplete="off" placeholder="${escapeHtml(autoRegistryName(ownerFirst, ownerLast, partnerFirst))}" value="${escapeHtml(r.name || "")}">
          <span class="rcp-settings__status" id="rcp-settings-name-status" aria-live="polite"></span>
        </div>
        <div class="registry-modal__field">
          <label>Registry owner</label>
          <div class="rcp-settings__name-grid">
            <input id="rcp-settings-owner-first" name="registrantFirst" type="text" placeholder="First name" autocomplete="given-name" value="${escapeHtml(ownerFirst)}" aria-label="Owner first name">
            <input id="rcp-settings-owner-last" name="registrantLast" type="text" placeholder="Last name" autocomplete="family-name" value="${escapeHtml(ownerLast)}" aria-label="Owner last name">
          </div>
          <span class="rcp-settings__status" id="rcp-settings-owner-status" aria-live="polite"></span>
        </div>
        <div class="registry-modal__field">
          <label>Co-registrant <span style="color:var(--reg-muted);font-weight:400">(optional)</span></label>
          <div class="rcp-settings__name-grid">
            <input id="rcp-settings-partner-first" name="partnerFirst" type="text" placeholder="First name" autocomplete="given-name" value="${escapeHtml(partnerFirst)}" aria-label="Co-registrant first name">
            <input id="rcp-settings-partner-last" name="partnerLast" type="text" placeholder="Last name" autocomplete="family-name" value="${escapeHtml(partnerLast)}" aria-label="Co-registrant last name">
          </div>
          <span class="rcp-settings__status" id="rcp-settings-partner-status" aria-live="polite"></span>
        </div>`;
  }

  // Wire the name / owner / co-registrant inputs inside `backdrop` for auto-save.
  // Every successful save updates state.registry AND re-applies the live titles
  // (applyRegistryNameTitles) so the nav + "Currently in your registry" headings
  // reflect edits instantly — no page refresh needed.
  function wireRegistryNameFields(backdrop, r) {
    const { API } = window.BirchRegistry;
    const { customerFirst, customerLast } = getOwnerDefaults(r);

    const nameInput = backdrop.querySelector("#rcp-settings-name");
    const ownerFirstInput = backdrop.querySelector("#rcp-settings-owner-first");
    const ownerLastInput = backdrop.querySelector("#rcp-settings-owner-last");
    const partnerFirstInput = backdrop.querySelector("#rcp-settings-partner-first");
    const partnerLastInput = backdrop.querySelector("#rcp-settings-partner-last");

    // Keep the name field's placeholder showing the live auto-generated name as
    // owner / co-registrant are typed, so it's clear what an empty name becomes.
    const refreshNamePlaceholder = () => {
      nameInput.placeholder = autoRegistryName(ownerFirstInput.value, ownerLastInput.value, partnerFirstInput.value);
    };
    [ownerFirstInput, ownerLastInput, partnerFirstInput].forEach((el) =>
      el.addEventListener("input", refreshNamePlaceholder));

    // Generic commit-on-change saver with per-field status + sequence guard.
    const bindTextSave = (input, statusEl, key, opts = {}) => {
      let seq = 0;
      input.addEventListener("change", async () => {
        let val = input.value.trim();
        // The registry name is required — regenerate the common-standard name
        // when the field is cleared rather than saving an empty string.
        if (opts.regenerateIfBlank && !val) {
          val = autoRegistryName(ownerFirstInput.value, ownerLastInput.value, partnerFirstInput.value);
          input.value = val;
        }
        const mySeq = ++seq;
        if (statusEl) statusEl.textContent = "Saving…";
        try {
          const { registry: updated } = await API.update(r.id, { [key]: val || null });
          if (mySeq !== seq) return;
          state.registry = { ...state.registry, ...updated };
          // Live-reflect into the nav + current-registry titles immediately.
          applyRegistryNameTitles(state.registry);
          if (statusEl) {
            statusEl.textContent = "Saved";
            setTimeout(() => { if (statusEl.textContent === "Saved") statusEl.textContent = ""; }, 1500);
          }
        } catch (_) {
          if (mySeq !== seq) return;
          if (statusEl) statusEl.textContent = "Couldn't save — try again";
        }
      });
    };

    bindTextSave(nameInput, backdrop.querySelector("#rcp-settings-name-status"), "name", { regenerateIfBlank: true });
    const ownerStatus = backdrop.querySelector("#rcp-settings-owner-status");
    bindTextSave(ownerFirstInput, ownerStatus, "registrantFirst");
    bindTextSave(ownerLastInput, ownerStatus, "registrantLast");
    const partnerStatus = backdrop.querySelector("#rcp-settings-partner-status");
    bindTextSave(partnerFirstInput, partnerStatus, "partnerFirst");
    bindTextSave(partnerLastInput, partnerStatus, "partnerLast");

    // "Default owner = current user": when the registry has no stored owner yet,
    // persist the prefilled customer name once so the share page (and any
    // exports) have a real owner even if the user never edits the field.
    if (!r.registrantFirst && !r.registrantLast && (customerFirst || customerLast)) {
      API.update(r.id, { registrantFirst: customerFirst || null, registrantLast: customerLast || null })
        .then(({ registry: updated }) => {
          state.registry = { ...state.registry, ...updated };
          applyRegistryNameTitles(state.registry);
        })
        .catch(() => {});
    }
  }

  // ── "Edit details" dialog (nav-header pencil) — name / owner / co-registrant
  function wireEditDetailsButton() {
    const btn = document.getElementById("rcp-edit-details-btn");
    if (!btn) return;
    btn.addEventListener("click", () => openRegistryDetailsDialog());
  }

  function openRegistryDetailsDialog() {
    if (!state.registry) {
      window.BirchRegistry.showToast?.("Start by adding an item — your registry is created automatically.");
      return;
    }
    const { escapeHtml } = window.BirchRegistry;
    const r = state.registry;

    const backdrop = document.createElement("div");
    backdrop.className = "registry-modal-backdrop";
    backdrop.innerHTML = `
      <div class="registry-modal rcp-settings-modal" role="dialog" aria-modal="true" aria-labelledby="rcp-details-title" style="max-width:440px;position:relative">
        <button type="button" class="rcp-settings-modal__close" data-action="close" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <h2 id="rcp-details-title" style="margin:0 0 8px;font-size:1.125rem;padding-right:24px">Registry details</h2>
        <p style="font-size:0.875rem;color:var(--reg-muted);margin:0 0 20px;line-height:1.55">
          Changes save automatically.
        </p>
        ${registryNameFieldsHtml(r, escapeHtml)}
      </div>`;
    document.body.appendChild(backdrop);
    const close = () => backdrop.remove();
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
    backdrop.querySelector("[data-action=close]").addEventListener("click", close);
    backdrop.addEventListener("keydown", (e) => { if (e.key === "Escape") close(); });

    wireRegistryNameFields(backdrop, r);

    // Focus the name field for quick edits.
    const nameInput = backdrop.querySelector("#rcp-settings-name");
    if (nameInput) setTimeout(() => { try { nameInput.focus(); } catch (_) {} }, 0);
  }

  // ── Registry details dialog (name, owners, due date, end registry) ────────
  function wireSettingsButton() {
    const btn = document.getElementById("rcp-settings-btn");
    if (!btn) return;
    btn.addEventListener("click", () => openSettingsDialog());
  }

  function openSettingsDialog() {
    if (!state.registry) {
      window.BirchRegistry.showToast?.("Start by adding an item — your registry is created automatically.");
      return;
    }
    const { escapeHtml, showToast, API } = window.BirchRegistry;
    const r = state.registry;
    const existingIso = r.dueDate ? new Date(r.dueDate).toISOString().slice(0, 10) : "";
    const isoToday = new Date().toISOString().slice(0, 10);
    const isEnded = r.status === "ended";

    const backdrop = document.createElement("div");
    backdrop.className = "registry-modal-backdrop";
    backdrop.innerHTML = `
      <div class="registry-modal rcp-settings-modal" role="dialog" aria-modal="true" aria-labelledby="rcp-settings-title" style="max-width:440px;position:relative">
        <button type="button" class="rcp-settings-modal__close" data-action="close" aria-label="Close registry details">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <h2 id="rcp-settings-title" style="margin:0 0 8px;font-size:1.125rem;padding-right:24px">Registry details</h2>
        <p style="font-size:0.875rem;color:var(--reg-muted);margin:0 0 20px;line-height:1.55">
          Changes save automatically.
        </p>
        ${registryNameFieldsHtml(r, escapeHtml)}
        <div class="registry-modal__field">
          <label for="rcp-settings-due">Due date</label>
          <input id="rcp-settings-due" name="dueDate" type="date" min="${isoToday}" value="${escapeHtml(existingIso)}">
          <span class="rcp-settings__status" id="rcp-settings-due-status" aria-live="polite"></span>
        </div>
        ${isGuest() ? "" : `
        <hr style="margin:20px 0;border:0;border-top:1px solid var(--reg-border, #e6dfd1)">
        <div>
          <div style="font-weight:600;font-size:0.9375rem;margin-bottom:6px">End registry</div>
          <p style="font-size:0.8125rem;color:var(--reg-muted);margin:0 0 10px;line-height:1.5">
            Ending releases any held shipments. You'll still be able to view items and gift activity.
          </p>
          <button type="button" class="registry-btn registry-btn--secondary registry-btn--block" id="rcp-settings-end" ${isEnded ? "disabled" : ""}>
            ${isEnded ? "Already ended" : "End registry"}
          </button>
        </div>`}
      </div>`;
    document.body.appendChild(backdrop);
    const close = () => backdrop.remove();
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
    backdrop.querySelector("[data-action=close]").addEventListener("click", close);

    // Auto-save due date on change, with a small status hint so the user
    // knows the network round-trip happened. Debounced via the native
    // `change` event (fires on commit, not on every keystroke).
    const dueInput = backdrop.querySelector("#rcp-settings-due");
    const dueStatus = backdrop.querySelector("#rcp-settings-due-status");
    let saveSeq = 0;
    dueInput.addEventListener("change", async () => {
      const val = dueInput.value;
      if (!val) { dueStatus.textContent = ""; return; }
      const mySeq = ++saveSeq;
      dueStatus.textContent = "Saving…";
      try {
        const { registry: updated } = await API.update(r.id, { dueDate: val });
        if (mySeq !== saveSeq) return; // a newer change has superseded
        state.registry = { ...state.registry, ...updated };
        dueStatus.textContent = "Saved";
        setTimeout(() => { if (dueStatus.textContent === "Saved") dueStatus.textContent = ""; }, 1500);
      } catch (_) {
        if (mySeq !== saveSeq) return;
        dueStatus.textContent = "Couldn't save — try again";
      }
    });

    // Name / owner / co-registrant auto-save (shared with the nav-header pencil
    // dialog; saves also live-reflect into the registry titles).
    wireRegistryNameFields(backdrop, r);

    backdrop.querySelector("#rcp-settings-end")?.addEventListener("click", async () => {
      if (isEnded) return;
      if (!confirm("End this registry? Held shipments will be released.")) return;
      const endBtn = backdrop.querySelector("#rcp-settings-end");
      endBtn.disabled = true;
      endBtn.textContent = "Ending…";
      try {
        await API.end(r.id);
        showToast?.("Registry ended");
        close();
        // Once ended, the customer is back at "create" — drop the active
        // registry from state, wipe the cache + cards, and re-render the
        // page in create mode. Hero labels, AI button, settings chip
        // visibility, and the sidebar all flip back to the fresh-start UI.
        try { localStorage.removeItem(LS_KEY); } catch (_) {}
        // Also remove ?id= from the URL so a reload doesn't reopen the ended
        // registry in edit mode (explicit ?id= still wins, by design).
        try {
          const u = new URL(location.href);
          if (u.searchParams.has("id")) {
            u.searchParams.delete("id");
            history.replaceState(null, "", u.toString());
          }
        } catch (_) {}
        state.registry = null;
        state.itemByProductId = {};
        state.itemByHandle = {};
        state.aiInjectedCounts = {};
        // Flip every "Added" tile back to "Add" and wipe the per-panel cards.
        document.querySelectorAll(".rcp-pgrid-card[data-product-id]").forEach((card) => {
          const btn = card.querySelector("[data-rcp-add]");
          if (btn) setUnadded(btn, card);
        });
        document.querySelectorAll("[data-injected-grid]").forEach((el) => el.remove());
        hideOtherPanel();
        updateCategoryCounts();
        setSettingsButtonVisibility(false);
        applyCompletionBanner({ status: "draft" });
        enterCreateMode();
      } catch (e) {
        showToast?.(e?.message || "Could not end registry");
        endBtn.disabled = false;
        endBtn.textContent = "End registry";
      }
    });
  }

  // ── Save button (only meaningful pre-registry; afterwards it goes to share) ─
  function wireSaveButton() {
    const btn = document.getElementById("rcp-save-registry");
    if (!btn) return;
    btn.addEventListener("click", async () => {
      const { showToast } = window.BirchRegistry;
      // For a guest this button's destination (the share page) is sign-in only,
      // and "Save" is exactly the word they need explained: the registry IS
      // saved; what sign-in adds is keeping it.
      if (guestGate(
        "save your registry to your account",
        "Your registry is already saved on our end — signing in attaches it to you so it's there next time and you can share it.",
      )) return;
      btn.disabled = true;
      btn.textContent = "Saving…";
      try {
        const id = await getOrCreateRegistry();
        location.href = `/pages/registry-share?id=${encodeURIComponent(id)}`;
      } catch (e) {
        showToast?.(e?.message || "Could not save registry");
        btn.disabled = false;
        btn.textContent = "Save Registry";
      }
    });
  }

  // ── "Add from Link" entry points ──────────────────────────────────────────
  // Both the sidebar "+ Add from any store" button (#re-add-url-btn) and the
  // nav-rail "Add from Link" action (#re-addlink-action) open openAddLinkModal.
  // Previously these smooth-scrolled to the inline bar at the bottom of the
  // page, but the page renders item cards/images asynchronously above that bar,
  // so the scroll target shifted mid-animation and often landed short. A popup
  // sidesteps the moving layout entirely and reuses the exact same add pipeline
  // (processAddUrl → addUrlWithReview → openItemForm) as the inline bar.
  function wireAddUrlButton() {
    document.addEventListener("click", (e) => {
      const btn = e.target.closest("#re-add-url-btn");
      if (!btn) return;
      e.preventDefault();
      openAddLinkModal();
    });
  }

  // Focused "paste a link" popup — the primary entry point for adding an item
  // from any store. Collects + validates the URL, then hands off to the shared
  // processAddUrl pipeline (which, with the review flag on, opens the editable
  // review popup where the registrant confirms details and picks a category).
  // The "Add from a link" dialog lives in registry-add-link.js (shared with the
  // guided flow, CUR-150); this page supplies what happens to the link.
  function openAddLinkModal() {
    if (!window.BirchAddLink) { window.BirchRegistry?.showToast?.("Couldn't open that — please reload and try again."); return; }
    window.BirchAddLink.promptForLink({ onSubmit: (url, submitBtn, opts) => processAddUrl(url, submitBtn, opts) });
  }

  // ── External URL input bar (always available) ─────────────────────────────
  function wireExternalUrlBar() {
    const btn = document.getElementById("rcp-url-add-btn");
    const input = document.getElementById("rcp-external-url");
    if (!btn || !input) return;
    btn.addEventListener("click", handleAddUrlBar);
    input.addEventListener("keydown", (e) => { if (e.key === "Enter") handleAddUrlBar(); });
  }

  async function handleAddUrlBar() {
    const { showToast } = window.BirchRegistry;
    const input = document.getElementById("rcp-external-url");
    const btn = document.getElementById("rcp-url-add-btn");
    const url = (input?.value || "").trim();
    if (!url) { showToast("Please paste a product URL"); return; }
    if (!/^https?:\/\//i.test(url)) { showToast("Please enter a valid URL starting with http"); return; }
    await processAddUrl(url, btn, { input });
  }

  // Shared core for adding an item from a pasted URL — the single pipeline
  // behind both the inline "Add anything else" bar and the "Add from Link"
  // popup (openAddLinkModal). `url` is assumed already validated. `btn` is the
  // progress surface (its label flips to "Fetching…"); `input`, when present,
  // is cleared on success. `onHandoff` fires the instant the flow hands off to
  // the editable review popup — the popup entry point uses it to close itself
  // as the review popup opens, so the two never stack.
  async function processAddUrl(url, btn, { input = null, onHandoff = null } = {}) {
    const { API, showToast } = window.BirchRegistry;

    // Feature flag (theme setting "Add-by-link review popup",
    // window.BIRCH_REGISTRY_ADD_REVIEW): when on, route through the editable
    // review popup so the registrant can correct/complete the scraped details
    // before the item is saved. When off, fall through to the direct-add path
    // below (scrape + add, server determines stage/priority). Default on.
    if (window.BIRCH_REGISTRY_ADD_REVIEW !== false) { await addUrlWithReview(url, input, btn, onHandoff); return; }

    const orig = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Fetching…";
    try {
      const registryId = await getOrCreateRegistry();
      // No stage/priority here — the server DETERMINES them from the scraped
      // title (classifyStage / classifyPriority) instead of dumping every pasted
      // item into "uncategorized / nice_to_have".
      const itemData = { source: "external", externalUrl: url };
      try {
        const scraped = await API.scrapeUrl(url);
        if (scraped) {
          itemData.title = scraped.title || "External item";
          itemData.imageUrl = scraped.imageUrl || scraped.image || null;
          // The scraper returns `price` in DOLLARS (priceCents is now also
          // derived server-side, but stay defensive so the price is never lost).
          itemData.priceCents = scraped.priceCents != null
            ? scraped.priceCents
            : (typeof scraped.price === "number" ? Math.round(scraped.price * 100)
              : (typeof scraped.price === "string" && scraped.price ? Math.round(parseFloat(scraped.price) * 100) : null));
          itemData.currency = scraped.currency || "USD";
          itemData.externalRetailer = scraped.retailer || scraped.domain || null;
          // Persist any scraped options (Amazon/Walmart color/size) so the
          // detail popup's variant picker has something to show later — without
          // this the scrape captures real variant data that then gets dropped
          // on the floor, and the picker for this item silently never appears.
          if (Array.isArray(scraped.variants) && scraped.variants.length) {
            itemData.variants = scraped.variants;
          }
        }
      } catch (_) { itemData.title = "External item"; }
      const { item } = await API.addItem(registryId, itemData);
      state.registry.items.push(item);
      if (input) input.value = "";
      let domain = "";
      try { domain = new URL(url).hostname.replace("www.", ""); } catch (_) {}
      showToast("Item added" + (domain ? " from " + domain : ""));
      renderInjectedItems();
      updateCategoryCounts();
    } catch (e) {
      showToast(e?.message || "Could not add item from URL");
    } finally {
      btn.disabled = false;
      btn.textContent = orig;
    }
  }

  // Feature-flagged review path for the paste-a-link bar (gated by
  // window.BIRCH_REGISTRY_ADD_REVIEW). Scrapes the URL, then shows the extracted
  // title/image/price/retailer in an editable popup (openItemForm) so the
  // registrant can correct or complete them — and choose which "Currently in
  // your registry" Category the item lands in — before it is saved. Cancelling
  // leaves the pasted URL in the bar.
  async function addUrlWithReview(url, input, btn, onHandoff = null) {
    const { API, showToast } = window.BirchRegistry;
    const orig = btn.textContent;
    // registry-add-link.js holds the scrape → review popup → payload (shared
    // with the guided flow, CUR-150). If it failed to load, say so rather than
    // leave the button stuck on "Fetching…".
    if (!window.BirchAddLink) { showToast("Couldn't open that — please reload and try again."); return; }
    btn.disabled = true;
    btn.textContent = "Fetching…";
    const reviewed = await window.BirchAddLink.reviewLink(url, {
      // This page files by Category: the live "Currently in your registry"
      // sections, auto-picked the same way a direct add would be filed.
      categoryOptions: registryCategoryOptions(),
      categoryFor: (scraped) => classifyItemToIcon({
        title: scraped?.title || "",
        productType: scraped?.productType || scraped?.category || "",
      }),
      onScraped: () => {
        // Restore the button before the (blocking) modal so it isn't stuck on "Fetching…".
        btn.disabled = false;
        btn.textContent = orig;
        // Scrape done — the editable review popup is about to open. Let the caller
        // (the "Add from Link" popup) close its own dialog now so the two never
        // stack; the inline bar passes no handoff and this is a no-op for it.
        try { onHandoff?.(); } catch (_) {}
      },
    });
    if (!reviewed) return; // user cancelled
    const { payload, data } = reviewed;

    try {
      const registryId = await getOrCreateRegistry();
      const chosenIcon = data.categoryIcon || "other";
      // Persist the chosen Category durably: stamp a canonical productType that
      // re-classifies to the picked section on reload (categoryIcon below is
      // in-memory only). "Other" persists the "other" sentinel productType, which
      // classifyItemToIcon short-circuits back to the catch-all bucket.
      if (ICON_TO_CANONICAL_TYPE[chosenIcon]) payload.productType = ICON_TO_CANONICAL_TYPE[chosenIcon];
      const { item } = await API.addItem(registryId, payload);
      // Immediate placement: stamp the chosen icon on the in-memory item so this
      // render files it under the picked section without waiting for a reload.
      item.categoryIcon = chosenIcon;
      state.registry.items.push(item);
      if (input) input.value = "";
      let domain = "";
      try { domain = new URL(url).hostname.replace("www.", ""); } catch (_) {}
      showToast("Item added" + (domain ? " from " + domain : ""));
      renderInjectedItems();
      updateCategoryCounts();
    } catch (e) {
      showToast(e?.message || "Could not add item");
    }
  }

  function openLoaderModal({ title, message }) {
    const { escapeHtml } = window.BirchRegistry;
    const backdrop = document.createElement("div");
    backdrop.className = "registry-modal-backdrop";
    backdrop.innerHTML = `
      <div class="registry-modal" role="alertdialog" aria-busy="true" style="max-width:380px;text-align:center">
        <div style="display:flex;justify-content:center;margin-bottom:14px">
          <span class="registry-loading" style="width:28px;height:28px;border-width:3px"></span>
        </div>
        <h2 style="font-size:1.0625rem;margin-bottom:6px">${escapeHtml(title)}</h2>
        ${message ? `<p style="color:var(--reg-muted);font-size:0.875rem;margin:0">${escapeHtml(message)}</p>` : ""}
      </div>`;
    document.body.appendChild(backdrop);
    return () => backdrop.remove();
  }

  // Placement targets for the add-item popup's Category dropdown: the live
  // "Currently in your registry" sections. These are the browse-category panels
  // (numeric data-cat-panel) — the gift-card / funds / other panels are not
  // valid targets for a pasted product. Value = the classifier icon the section
  // is keyed on (so the choice drives partitionRegistryIntoPanels), label = the
  // section's visible title. A trailing "Other" covers the catch-all bucket.
  function registryCategoryOptions() {
    const opts = [];
    const seen = new Set();
    document.querySelectorAll("[data-cat-panel][data-cat-icon]").forEach((panel) => {
      if (!/^\d+$/.test(String(panel.dataset.catPanel))) return; // skip giftcard/etc.
      const icon = panel.dataset.catIcon;
      if (!icon || seen.has(icon)) return;
      seen.add(icon);
      opts.push({ value: icon, label: macroLabelForPanel(panel) });
    });
    opts.push({ value: "other", label: "Other" });
    return opts;
  }

  // ── Copy / import a registry (from Birch or an external site) ─────────────
  // The "Copy a registry" sidebar button scrolls to the inline bar in the
  // "Already started a registry somewhere else?" section and focuses its input
  // (mirrors "+ Add from any store" → "Add anything else" bar). Pasting a link
  // there + Copy opens the preview modal: review the items it would import
  // (matched to Birch where possible, out-of-stock / already-added flagged) →
  // untick anything → commit onto the current registry.
  function wireCopyButton() {
    document.addEventListener("click", (e) => {
      const btn = e.target.closest("#re-copy-registry-btn");
      if (!btn) return;
      e.preventDefault();
      focusCopySection();
    });
  }

  // Scroll the "Copy a registry" box into view and put the cursor in its link
  // field. Shared by the sidebar button and the ?import=1 deep link.
  function focusCopySection() {
    const section = document.getElementById("rcp-copy-section");
    const input = document.getElementById("rcp-copy-url");
    if (section) section.scrollIntoView({ behavior: "smooth", block: "center" });
    if (input) setTimeout(() => { try { input.focus({ preventScroll: true }); } catch (_) { input.focus(); } }, 350);
  }

  // CUR-145: /pages/registry-edit?import=1 — the registry-public page's
  // "Import a Registry" button. Lands the visitor on the copy box ready to
  // paste; the preview and commit are the existing flow. The param is dropped
  // so a reload doesn't jump the page again.
  function openImportFromLink() {
    let params;
    try { params = new URLSearchParams(location.search); } catch (_) { return; }
    // CUR-145 — arriving from the public page's import popup, which has
    // already added the items: say how many, once.
    const imported = params.get("imported");
    if (imported !== null) {
      const n = parseInt(imported, 10) || 0;
      window.BirchRegistry.showToast(n
        ? "Added " + n + " item" + (n === 1 ? "" : "s") + " from the registry you imported"
        : "Nothing new to add — those items were already on your registry");
      params.delete("imported");
    }
    const wantsImport = params.get("import") === "1";
    params.delete("import");
    if (imported !== null || wantsImport) {
      const qs = params.toString();
      try { history.replaceState(null, "", location.pathname + (qs ? "?" + qs : "") + location.hash); } catch (_) {}
    }
    if (wantsImport) focusCopySection();
  }

  // The commit button names what it will do: with no registry yet, the commit
  // creates one first (getOrCreateRegistry) — guests included — then imports.
  function importCommitLabel() {
    return state.registry ? "Add to my registry" : "Create registry and import";
  }

  // Inline "Copy registry" bar inside the copy section.
  function wireCopyUrlBar() {
    const btn = document.getElementById("rcp-copy-btn");
    const input = document.getElementById("rcp-copy-url");
    if (!btn || !input) return;
    const go = () => startCopyImport(input.value);
    btn.addEventListener("click", go);
    input.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); go(); } });
  }

  // CUR-145 — the preview → choose → add steps live in registry-import.js,
  // shared with the registry-public page's "Import a Registry" popup. This bar
  // already has the link, so it starts at the preview; adding creates the
  // registry first when there isn't one (getOrCreateRegistry, guests included).
  function startCopyImport(rawUrl) {
    const { showToast } = window.BirchRegistry;
    if (!window.BirchRegistryImport) { showToast("Import isn't available right now. Please reload."); return; }
    window.BirchRegistryImport.preview(rawUrl, {
      registryId: state.registry?.id || null,
      getRegistryId: getOrCreateRegistry,
      commitLabel: importCommitLabel,
      onImported: ({ added, skipped }) => {
        showToast("Added " + added + " item" + (added === 1 ? "" : "s") + (skipped ? " (" + skipped + " skipped — already on your registry)" : ""));
        scheduleRefresh();
      },
    });
  }

  // ── AI form submit (delegated to registry-create-launch.js if present) ────
  function wireAiFormSubmit() {
    const form = document.getElementById("registry-create-form");
    if (!form) return;
    form.addEventListener("submit", (e) => e.preventDefault());
  }

  // ── Birch Gift Card denomination panel (ported from main's edit page) ──────
  // The gift-card backend (worker build) auto-injects a default card; this lets
  // the registrant add specific denominations from the "Birch Card" panel. The
  // backend dedups, so the two paths coexist. Handle: /products/birch-gift-card.

  // ── Cash Funds panel (registrant side) ───────────────────────────────────
  function wireCashFundsPanel() {
    const panel = document.getElementById("rcp-cat-funds");
    if (!panel) return;

    const PROXY = (window.BirchAI && window.BirchAI.routes && window.BirchAI.routes.aiProxy) || "/apps/ai";
    const esc = window.BirchRegistry ? window.BirchRegistry.escapeHtml : function(s){ return String(s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;"); };
    const showToast = window.BirchRegistry ? window.BirchRegistry.showToast.bind(window.BirchRegistry) : function(m){ console.warn("[toast]", m); };

    // ── API helper ───────────────────────────────────────────────────────────
    function fundsApi(path, opts) {
      return fetch(PROXY + "/cash-funds" + (path || ""), Object.assign(
        { credentials: "same-origin", headers: { "Content-Type": "application/json" } },
        opts || {}
      )).then(function(r){ return r.json(); });
    }

    // ── Render existing funds ─────────────────────────────────────────────
    function renderFunds(funds) {
      funds = funds || [];
      // Single funnel for the top-block "Cash Funds" card: cache funds in state so
      // renderCurrentRegistryTabs can show them, and re-render the top block — but
      // only when the SET of active funds actually changes, to avoid redundant full
      // re-renders on every panel open. This keeps the card in lockstep with any
      // in-session add/withdraw/close (all of which route through here).
      state.cashFunds = funds;
      var fundsSig = funds
        .filter(function(f){ return f && f.status === "active"; })
        .map(function(f){ return f.id + ":" + (f.name || ""); })
        .join("|");
      if (fundsSig !== state._cashFundsSig) {
        state._cashFundsSig = fundsSig;
        try { renderInjectedItems(); } catch (_e) { /* top block is optional */ }
      }

      const list = document.getElementById("rcp-funds-list");
      if (!list) return;

      const active = funds.filter(function(f){ return f.status === "active"; });
      const closed = (funds || []).filter(function(f){ return f.status !== "active"; });
      var navCount = document.getElementById("rcp-count-funds");
      if (navCount) navCount.textContent = active.length > 0 ? String(active.length) : "";

      if (!active.length && !closed.length) {
        list.innerHTML = '<p style="font-size:0.83rem;color:#aebda6;padding:0.5rem 0 1rem;text-align:center">No funds yet — add one below.</p>';
        return;
      }

      var icons = { diaper: "🍼", takeout: "🍕", feeding: "🥛", general: "🌿", birch_general: "🌿", custom: "✦" };
      list.innerHTML = '<div style="background:#f6f2ea;border:1px solid #e4d9c8;border-radius:12px;padding:14px 16px;">'
        + '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">'
        +   '<span style="font-size:0.72rem;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#5a8a6a">Your Cash Funds</span>'
        +   '<span style="font-size:0.72rem;color:#9a8a7a">' + active.length + ' active</span>'
        + '</div>'
        + active.concat(closed).map(function(f) {
            var raised = (f.total_raised_cents || 0) / 100;
            var goal   = f.goal_amount_cents ? f.goal_amount_cents / 100 : null;
            var pct    = goal ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
            var isClosed = f.status !== "active";
            var icon = icons[f.type] || "✦";
            return '<div class="rp-fund-card" data-fund-id="' + esc(f.id) + '">'
              + '<div class="rp-fund-card__icon">' + icon + '</div>'
              + '<div class="rp-fund-card__body">'
              +   '<div class="rp-fund-card__name">' + esc(f.name) + (isClosed ? ' <span style="font-size:0.68rem;color:#9a9a9a;font-weight:400">(closed)</span>' : '') + '</div>'
              +   '<div class="rp-fund-card__bar-wrap"><div class="rp-fund-card__bar" style="width:' + pct + '%"></div></div>'
              +   '<div class="rp-fund-card__stats">'
              +     '<span class="rp-fund-card__raised">$' + raised.toFixed(2) + ' gifted</span>'
              +     (goal ? '<span class="rp-fund-card__goal"> of $' + goal.toFixed(2) + '</span>' : '')
              +   '</div>'
              +   (!isClosed
                    ? '<div style="display:flex;gap:6px;margin-top:6px;flex-wrap:wrap;">'
                      + '<button class="rcp-fund-card__action" data-fund-contribs="' + esc(f.id) + '">View contributions</button>'
                      + '<button class="rcp-fund-card__action rcp-fund-card__action--withdraw" data-fund-withdraw="' + esc(f.id) + '" data-fund-name="' + esc(f.name) + '" data-fund-raised-cents="' + (f.total_raised_cents || 0) + '">Withdraw</button>'
                      + '<button class="rcp-fund-card__action rcp-fund-card__action--close" data-fund-close="' + esc(f.id) + '">Close</button>'
                      + '</div>'
                    : '')
              + '</div>'
              + '</div>';
          }).join("")
        + '</div>';

      list.querySelectorAll("[data-fund-close]").forEach(function(btn) {
        btn.addEventListener("click", function() {
          if (!confirm("Close this fund? Guests won't be able to contribute after closing, but the record is preserved.")) return;
          const fid = btn.getAttribute("data-fund-close");
          fundsApi("/" + fid, { method: "PATCH", body: JSON.stringify({ status: "closed" }) })
            .then(function(res) { if (res.ok) { showToast("Fund closed."); loadAndRender(); } else showToast(res.message || "Could not close fund."); })
            .catch(function(){ showToast("Something went wrong."); });
        });
      });

      list.querySelectorAll("[data-fund-contribs]").forEach(function(btn) {
        btn.addEventListener("click", function() {
          const fid = btn.getAttribute("data-fund-contribs");
          fundsApi("/" + fid + "/contributions").then(function(res) {
            if (!res.ok) return;
            const cs = res.contributions || [];
            const cleared = cs.filter(function(c){ return c.status === "cleared"; });
            const pending = cs.filter(function(c){ return c.status === "pending"; });
            var rows = cleared.map(function(c){ return (c.giver_name || "Anonymous") + " — $" + (c.amount_cents/100).toFixed(2) + (c.note ? " \"" + c.note + "\"" : ""); });
            if (pending.length) rows.push("(" + pending.length + " pending)");
            showToast(rows.length ? rows.slice(0,5).join(" | ") : "No cleared contributions yet.");
          });
        });
      });

      list.querySelectorAll("[data-fund-withdraw]").forEach(function(btn) {
        btn.addEventListener("click", function() {
          const fid         = btn.getAttribute("data-fund-withdraw");
          const fname       = btn.getAttribute("data-fund-name") || "Fund";
          const raisedCents = parseInt(btn.getAttribute("data-fund-raised-cents") || "0", 10);
          openWithdrawModal(fid, fname, raisedCents);
        });
      });
    }

    // ── Withdraw modal ────────────────────────────────────────────────────────
    function openWithdrawModal(fundId, fundName, raisedCents) {
      var dlg = document.createElement("div");
      dlg.style.cssText = "position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.45)";
      document.body.appendChild(dlg);

      function closeW() { if (dlg.parentNode) dlg.parentNode.removeChild(dlg); }
      dlg.addEventListener("click", function(e){ if (e.target === dlg) closeW(); });

      fundsApi("/connect/status").then(function(res) {
        var status  = (res && res.status) || "none";
        var isPending = (status === "pending");
        if (status === "active") {
          // Account linked — show amount form directly
          showWithdrawForm(dlg, fundId, fundName, raisedCents, closeW);
        } else {
          // No account or still pending onboarding
          showConnectScreen(dlg, fundId, fundName, raisedCents, closeW, isPending);
        }
      }).catch(function() {
        showConnectScreen(dlg, fundId, fundName, raisedCents, closeW, false);
      });
    }

    function showConnectScreen(dlg, fundId, fundName, availableCents, closeW, isPending) {
      dlg.innerHTML = '<div style="background:#fff;border-radius:16px;padding:28px 24px;max-width:400px;width:90%;font-family:inherit;">'
        + '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">'
        +   '<span style="font-size:1rem;font-weight:700;color:#1a2018">Withdraw funds</span>'
        +   '<button id="wdlg-close" style="background:none;border:none;cursor:pointer;font-size:1.2rem;color:#7a7060;padding:0">✕</button>'
        + '</div>'
        + '<p style="font-size:0.85rem;color:#6b5e4e;margin:0 0 8px">To receive your funds, link a bank account via Stripe.</p>'
        + (isPending
            ? '<p style="font-size:0.8rem;color:#b07a20;background:#fdf8ee;border-radius:8px;padding:8px 12px;margin:0 0 16px">Your Stripe account is still being verified. You can re-open the onboarding link to complete it.</p>'
            : '<p style="font-size:0.8rem;color:#5a8a6a;background:#f0f6f0;border-radius:8px;padding:8px 12px;margin:0 0 16px">This takes about 2 minutes and only needs to be done once.</p>')
        + '<button id="wdlg-connect" style="width:100%;padding:12px;background:#4a6b4a;color:#fff;border:none;border-radius:8px;font-size:0.9rem;font-weight:600;cursor:pointer;">'
        +   (isPending ? 'Continue Stripe setup' : 'Connect bank account')
        + '</button>'
        + '</div>';

      dlg.querySelector("#wdlg-close").addEventListener("click", closeW);
      dlg.querySelector("#wdlg-connect").addEventListener("click", function() {
        var connectBtn = dlg.querySelector("#wdlg-connect");
        connectBtn.disabled = true;
        connectBtn.textContent = "Opening Stripe…";
        fundsApi("/connect/onboard", {
          method: "POST",
          body: JSON.stringify({ return_url: window.location.href, refresh_url: window.location.href })
        }).then(function(res) {
          if (res.url) {
            window.open(res.url, "_blank");
            closeW();
          } else {
            connectBtn.disabled = false;
            connectBtn.textContent = isPending ? "Continue Stripe setup" : "Connect bank account";
            showToast((res && res.message) || "Could not start Stripe setup. Try again.");
          }
        }).catch(function() {
          connectBtn.disabled = false;
          connectBtn.textContent = isPending ? "Continue Stripe setup" : "Connect bank account";
          showToast("Something went wrong. Please try again.");
        });
      });
    }

    function showWithdrawForm(dlg, fundId, fundName, availableCents, closeW) {
      var available = availableCents / 100;
      var hasBalance = availableCents > 0;
      dlg.innerHTML = '<div style="background:#fff;border-radius:16px;padding:28px 24px;max-width:400px;width:90%;font-family:inherit;">'
        + '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">'
        +   '<span style="font-size:1rem;font-weight:700;color:#1a2018">Withdraw from ' + esc(fundName) + '</span>'
        +   '<button id="wdlg-close" style="background:none;border:none;cursor:pointer;font-size:1.2rem;color:#7a7060;padding:0">✕</button>'
        + '</div>'
        + '<p style="font-size:0.83rem;color:#6b5e4e;margin:0 0 4px">Available balance</p>'
        + '<p style="font-size:1.4rem;font-weight:700;color:#1a2018;margin:0 0 16px">$' + available.toFixed(2) + '</p>'
        + (hasBalance
            ? '<label style="display:block;font-size:0.83rem;color:#6b5e4e;margin-bottom:6px">Amount to withdraw</label>'
              + '<div style="display:flex;align-items:center;border:1px solid #c9bfb0;border-radius:8px;overflow:hidden;margin-bottom:4px;">'
              +   '<span style="padding:10px 12px;background:#f6f2ea;color:#7a7060;font-size:0.95rem">$</span>'
              +   '<input id="wdlg-amount" type="number" min="1" max="' + available.toFixed(2) + '" step="0.01" placeholder="' + available.toFixed(2) + '" style="border:none;outline:none;padding:10px 12px;font-size:0.95rem;width:100%;background:#fff;">'
              + '</div>'
              + '<p id="wdlg-err" style="font-size:0.78rem;color:#b05050;margin:0 0 12px;min-height:16px"></p>'
              + '<button id="wdlg-submit" style="width:100%;padding:12px;background:#4a6b4a;color:#fff;border:none;border-radius:8px;font-size:0.9rem;font-weight:600;cursor:pointer;">Transfer to my bank</button>'
            : '<p style="font-size:0.85rem;color:#7a7060;background:#f6f2ea;border-radius:8px;padding:12px;margin:0">No gifts have come in for this fund yet. Once contributions clear you\'ll be able to transfer them here.</p>')
        + '</div>';

      dlg.querySelector("#wdlg-close").addEventListener("click", closeW);
      if (!hasBalance) return;

      var amtInput  = dlg.querySelector("#wdlg-amount");
      var errEl     = dlg.querySelector("#wdlg-err");
      var submitBtn = dlg.querySelector("#wdlg-submit");

      submitBtn.addEventListener("click", function() {
        errEl.textContent = "";
        var dollars = parseFloat(amtInput.value);
        if (isNaN(dollars) || dollars <= 0) { errEl.textContent = "Enter a valid amount."; return; }
        if (dollars > available + 0.005) { errEl.textContent = "Amount exceeds available balance ($" + available.toFixed(2) + ")."; return; }
        var cents = Math.round(dollars * 100);

        submitBtn.disabled = true;
        submitBtn.textContent = "Processing…";
        fundsApi("/withdraw", {
          method: "POST",
          body: JSON.stringify({ fund_id: fundId, amount_cents: cents })
        }).then(function(res) {
          if (res.ok) {
            closeW();
            showToast("$" + dollars.toFixed(2) + " transfer initiated! It may take 1-2 business days to arrive.");
            loadAndRender();
          } else {
            submitBtn.disabled = false;
            submitBtn.textContent = "Transfer to my bank";
            errEl.textContent = res.message || "Transfer failed. Please try again.";
          }
        }).catch(function() {
          submitBtn.disabled = false;
          submitBtn.textContent = "Transfer to my bank";
          errEl.textContent = "Something went wrong. Please try again.";
        });
      });
    }

    function loadAndRender() {
      const registryId = state.registry && state.registry.id;
      if (!registryId) {
        renderFunds([]);
        return;
      }
      fundsApi("?registry_id=" + encodeURIComponent(registryId))
        .then(function(res) { renderFunds(res.funds || []); })
        .catch(function(err) {
          console.warn("[cash-funds] load failed", err);
          renderFunds([]);
        });
    }

    // Watch for rcp-cat-panel--active being added (that's how the nav shows panels)
    new MutationObserver(function() {
      if (panel.classList.contains("rcp-cat-panel--active")) loadAndRender();
    }).observe(panel, { attributes: true, attributeFilter: ["class"] });

    // Also hook the nav button directly as a belt-and-suspenders fallback
    var fundsNavBtn = document.querySelector('[data-cat-nav="rcp-cat-funds"]');
    if (fundsNavBtn) fundsNavBtn.addEventListener("click", function() { loadAndRender(); });

    // Eagerly load funds ONCE as soon as the registry id is known, so the
    // top-block "Cash Funds" card appears on first paint (incl. reloads) — not
    // only after the Funds panel is first opened. Single deduped call; later panel
    // opens still refetch for freshness via the observer/nav handlers above.
    var _fundsEagerLoaded = false;
    function eagerLoadFundsOnce() {
      if (_fundsEagerLoaded || !(state.registry && state.registry.id)) return;
      _fundsEagerLoaded = true;
      loadAndRender();
    }

    // Load when the registry becomes available (async discovery), chaining the
    // existing ready hook rather than clobbering it.
    var origEnter = window._rcpOnRegistryReady;
    window._rcpOnRegistryReady = function(r) {
      if (origEnter) origEnter(r);
      eagerLoadFundsOnce();
    };

    // If the registry id is already known at wire time (deep link / warm load).
    eagerLoadFundsOnce();

    // ── Creation modal ────────────────────────────────────────────────────
    var modal     = document.getElementById("rcp-fund-create-modal");
    var modalTitle= document.getElementById("rcp-fcm-title");
    var nameInput = document.getElementById("rcp-fcm-name");
    var descInput = document.getElementById("rcp-fcm-desc");
    var goalInput = document.getElementById("rcp-fcm-goal");
    var saveBtn   = document.getElementById("rcp-fcm-save");
    var cancelBtn = document.getElementById("rcp-fcm-cancel");
    var closeBtn  = document.getElementById("rcp-fcm-close");
    var pendingType = "custom";

    function openModal(type, presetName, presetDesc, presetGoalCents) {
      pendingType = type;
      if (modalTitle) modalTitle.textContent = presetName ? "Add " + presetName : "Create a Custom Fund";
      if (nameInput)  { nameInput.value = presetName || ""; }
      if (descInput)  { descInput.value = presetDesc || ""; }
      if (goalInput)  { goalInput.value = presetGoalCents ? (presetGoalCents / 100) : ""; }
      if (modal)      modal.hidden = false;
      if (nameInput)  setTimeout(function(){ nameInput.focus(); }, 50);
    }

    function closeModal() {
      if (modal) modal.hidden = true;
    }

    if (closeBtn)  closeBtn.addEventListener("click",  closeModal);
    if (cancelBtn) cancelBtn.addEventListener("click", closeModal);
    if (modal) modal.addEventListener("click", function(e){ if (e.target === modal) closeModal(); });

    if (saveBtn) {
      saveBtn.addEventListener("click", function() {
        const registryId = state.registry && state.registry.id;
        if (!registryId) { showToast("Save your registry first before adding a fund."); return; }
        const name = (nameInput ? nameInput.value.trim() : "").slice(0, 40);
        if (!name) { if (nameInput) { nameInput.focus(); nameInput.style.borderColor = "#b05050"; } return; }
        if (nameInput) nameInput.style.borderColor = "";
        const desc     = descInput ? descInput.value.trim().slice(0, 200) : "";
        const goalDollars = goalInput ? parseFloat(goalInput.value) : NaN;
        const goalCents = !isNaN(goalDollars) && goalDollars > 0 ? Math.round(goalDollars * 100) : null;

        saveBtn.disabled = true;
        saveBtn.textContent = "Adding…";
        fundsApi("", {
          method: "POST",
          body: JSON.stringify({
            registry_id: registryId,
            type: pendingType,
            name: name,
            description: desc || undefined,
            goal_amount_cents: goalCents,
          }),
        }).then(function(res) {
          saveBtn.disabled = false;
          saveBtn.textContent = "Add to registry";
          if (res.ok) { closeModal(); showToast("\"" + name + "\" added to your registry!"); loadAndRender(); }
          else showToast(res.message || "Could not create fund.");
        }).catch(function() {
          saveBtn.disabled = false;
          saveBtn.textContent = "Add to registry";
          showToast("Something went wrong.");
        });
      });
    }

    // Preset buttons → open pre-filled modal
    panel.querySelectorAll("[data-fund-type]").forEach(function(btn) {
      btn.addEventListener("click", function() {
        const type = btn.getAttribute("data-fund-type");
        const name = btn.getAttribute("data-fund-name") || "";
        const desc = btn.getAttribute("data-fund-desc") || "";
        const goalCents = parseInt(btn.getAttribute("data-fund-goal") || "0", 10) || 0;
        openModal(type, name, desc, goalCents);
      });
    });
  }

  // ── Birch Gift Card denomination picker ──────────────────────────────────
  function wireGiftCardPanel() {
    const panel = document.getElementById("rcp-cat-giftcard");
    if (!panel) return;
    const { API, showToast } = window.BirchRegistry;

    async function addGiftCard(denomCents) {
      const registryId = state.registry?.id;
      if (!registryId) {
        showToast("Start by creating your registry first.");
        return;
      }
      const denom = denomCents / 100;
      const gcUrl = window.location.origin + "/products/birch-gift-card";
      const gcImageUrl = "https://cdn.shopify.com/s/files/1/0675/0761/7858/files/gift_card.png";
      const payload = {
        source: "external",
        externalUrl: gcUrl,
        imageUrl: gcImageUrl,
        externalRetailer: "Birch",
        title: `Birch Gift Card — $${denom}`,
        priceCents: denomCents,
        currency: "USD",
        productType: "Gift Card",
        stage: "uncategorized",
        priority: "nice_to_have",
        groupGiftEnabled: true,
        reasonText:
          "A flexible gift — friends and family can purchase this card or contribute any amount toward your registry fund.",
        reasonType: "why_we_love_it",
      };
      try {
        const { item } = await API.addItem(registryId, payload);
        state.registry.items = [...(state.registry.items || []), item];
        renderInjectedItems();
        updateCategoryCounts();
        showToast(`$${denom} Birch Gift Card added to your registry.`);
      } catch (e) {
        const msg = e?.body?.error || e?.message || "Could not add gift card";
        showToast(msg);
      }
    }

    // Wire preset denomination buttons.
    panel.querySelectorAll(".rcp-gc-denom-btn[data-gc-denom]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        const denom = parseInt(btn.dataset.gcDenom, 10);
        if (!denom || denom <= 0) return;
        btn.classList.add("rcp-gc-denom-btn--adding");
        btn.textContent = "Adding…";
        try {
          await addGiftCard(denom * 100);
        } finally {
          btn.classList.remove("rcp-gc-denom-btn--adding");
          btn.textContent = `$${denom}`;
        }
      });
    });

    // Wire custom amount add button.
    const customInput = document.getElementById("rcp-gc-custom-amount");
    const customBtn = document.getElementById("rcp-gc-custom-add");
    if (customInput && customBtn) {
      async function handleCustomAdd() {
        const val = parseInt(customInput.value, 10);
        if (!val || val < 10 || val > 2000) {
          showToast("Enter an amount between $10 and $2,000.");
          customInput.focus();
          return;
        }
        customBtn.disabled = true;
        const orig = customBtn.textContent;
        customBtn.textContent = "Adding…";
        try {
          await addGiftCard(val * 100);
          customInput.value = "";
        } finally {
          customBtn.disabled = false;
          customBtn.textContent = orig;
        }
      }
      customBtn.addEventListener("click", handleCustomAdd);
      customInput.addEventListener("keydown", (e) => {
        if (e.key === "Enter") { e.preventDefault(); handleCustomAdd(); }
      });
    }
  }

  // ── Live build feed: chat-companion events refresh the in-page state ──────
  let liveRefreshTimer = null;
  function scheduleRefresh() {
    if (liveRefreshTimer) return;
    liveRefreshTimer = setTimeout(async () => {
      liveRefreshTimer = null;
      if (!state.registry) return;
      try {
        const { API } = window.BirchRegistry;
        const { registry } = await API.get(state.registry.id);
        state.registry = registry;
        // Re-partition: suggestions into their own bucket, active into the
        // editor working set (see enterEditMode).
        state.suggestions = (registry.items || []).filter((it) => it.status === "suggested");
        state.registry.items = (registry.items || []).filter((it) => it.status !== "suggested");
        state.itemByProductId = {};
        state.itemByHandle = {};
        state.registry.items.forEach((it) => {
          if (it.source !== "shopify") return;
          if (it.shopifyProductId) state.itemByProductId[String(it.shopifyProductId)] = it;
          if (it.shopifyHandle) state.itemByHandle[String(it.shopifyHandle)] = it;
        });
        document.querySelectorAll(".rcp-pgrid-card[data-product-id]").forEach((card) => {
          const btn = card.querySelector("[data-rcp-add]");
          if (btn) setUnadded(btn, card);
        });
        markAddedProducts();
        renderInjectedItems();
        renderSuggestions();
        // Reconcile the frozen subcategory carousels too, so a chat-driven add /
        // remove / quantity change flips the matching card's CTA (Add⇄Remove) in
        // place — without it, those rows only updated on a full page reload.
        renderCategoryAlternates();
        updateCategoryCounts();
        // Covers a fresh build that started from an empty registry: land on the
        // All tab the first time it becomes populated. One-time (guarded), so a
        // later refresh never yanks the user off their chosen tab.
        maybeDefaultToAllItems();
      } catch (_e) { /* tolerate */ }
    }, 800);
  }
  // Phase 11 (live build): poll D1 buildStatus while a build runs server-side,
  // re-rendering as items land. Stops on complete/failed. Detects a STALE
  // 'building' (started long ago, never completed → the build crashed or the tab
  // was closed before it finished) and offers "finish building" (a rebuild that
  // resumes the newest draft). Idempotent — guarded by buildPollTimer.
  let buildPollTimer = null;
  let buildPollElapsed = 0;
  const BUILD_POLL_MS = 2500;
  const BUILD_STALE_MS = 4 * 60 * 1000; // 4 min with no completion ⇒ stale
  function showBuildingBanner(show, opts) {
    let el = document.getElementById("rcp-building-banner");
    // The live "building…" banner is intentionally NOT rendered: the build now
    // streams live in the chat connection and the edit page just refreshes items
    // via polling, so a spinner banner at the top of the page is redundant. Only
    // the stale-build recovery (an actionable "finish building" CTA, shown after
    // BUILD_STALE_MS of a stuck 'building') is surfaced. Polling still runs.
    const showStale = !!(show && opts && opts.stale);
    if (!showStale) { if (el) el.remove(); return; }
    if (!el) {
      el = document.createElement("div");
      el.id = "rcp-building-banner";
      el.className = "rcp-building-banner";
      const anchor = document.querySelector("[data-rcp-grid]") || document.querySelector(".rcp-pgrid") || document.body;
      anchor.parentNode ? anchor.parentNode.insertBefore(el, anchor) : document.body.appendChild(el);
    }
    el.innerHTML =
      '<span class="rcp-building-banner__txt">Your build didn’t finish — want me to pick up where it left off?</span>' +
      '<button type="button" class="rcp-building-banner__btn" data-rcp-finish-building>Finish building</button>';
    const btn = el.querySelector("[data-rcp-finish-building]");
    if (btn) btn.addEventListener("click", () => {
      // Resume via the assistant — build_registry_draft resumes the newest draft.
      window.dispatchEvent(new CustomEvent("birch:open-with-query", {
        detail: { query: "Finish building my registry — add whatever’s still missing.", intent: "registry_builder" },
      }));
    }, { once: true });
  }
  async function pollBuildStatus() {
    if (buildPollTimer || !state.registry) return;
    showBuildingBanner(true);
    const tick = async () => {
      buildPollTimer = null;
      if (!state.registry) return;
      buildPollElapsed += BUILD_POLL_MS;
      try {
        const { API } = window.BirchRegistry;
        const { registry } = await API.get(state.registry.id);
        if (!registry) return;
        const prevCount = (state.registry.items || []).length;
        state.registry = registry;
        state.suggestions = (registry.items || []).filter((it) => it.status === "suggested");
        state.registry.items = (registry.items || []).filter((it) => it.status !== "suggested");
        state.itemByProductId = {}; state.itemByHandle = {};
        state.registry.items.forEach((it) => {
          if (it.source !== "shopify") return;
          if (it.shopifyProductId) state.itemByProductId[String(it.shopifyProductId)] = it;
          if (it.shopifyHandle) state.itemByHandle[String(it.shopifyHandle)] = it;
        });
        markAddedProducts(); renderInjectedItems(); renderSuggestions(); updateCategoryCounts();
        if ((state.registry.items || []).length !== prevCount) maybeDefaultToAllItems();
        const status = registry.buildStatus;
        if (status === "complete" || status === "failed") {
          showBuildingBanner(false);
          try { const u = new URL(location.href); u.searchParams.delete("building"); history.replaceState(null, "", u.toString()); } catch (_) {}
          return; // stop polling
        }
        if (buildPollElapsed >= BUILD_STALE_MS) { showBuildingBanner(true, { stale: true }); return; }
      } catch (_) { /* tolerate a blip; keep polling */ }
      buildPollTimer = setTimeout(tick, BUILD_POLL_MS);
    };
    buildPollTimer = setTimeout(tick, BUILD_POLL_MS);
  }

  function wireLiveFeed() {
    [
      "birch:registry:item_added",
      "birch:registry:item_removed",
      "birch:registry:item_swapped",
      // Quantity / variant / field edits (update_registry_item) — was missing, so
      // "set the quantity to 2" never reflected until a manual reload.
      "birch:registry:item_updated",
      "birch:registry:build_complete",
      "birch:registry:rebuild_started",
    ].forEach((ev) => window.addEventListener(ev, scheduleRefresh));
    // Catch-all safety net: refresh once at the END of every assistant turn. Some
    // chat mutations emit no per-item event — most notably an add that DEDUPE-
    // MERGES into an existing item (quantity bump) inserts nothing, so no
    // item_added fires. turn_done covers those (and any future tool) so a "Done!"
    // from the agent always reflects without a page reload. scheduleRefresh is
    // debounced + no-ops when there's no active registry, so this is cheap.
    window.addEventListener("birch:turn_done", scheduleRefresh);
    // When a build (or rebuild) kicks off, bring "Your Registry" into view so
    // the user watches it fill in instead of staring at the intake form above.
    [
      "birch:registry:build_started",
      "birch:registry:rebuild_started",
    ].forEach((ev) => window.addEventListener(ev, scrollToBuilder));
    // The chat asks for the finished registry to be uncovered — either on its
    // own once a build's summary has finished streaming (it drops out of full
    // screen so the panel returns to its side column), or from the "See your
    // registry" button in the build panel. All we owe it is putting the right
    // block in front of the user.
    window.addEventListener("birch:registry:show_registry", () => scrollToCurrentRegistry());
  }

  // Bring "Currently in your registry" into view. Right after a build the block
  // is often still hidden — the live refresh that renders the new items is
  // debounced 800ms behind build_complete — so fall back to the builder section
  // and re-aim at the real block once it appears. The retry is bounded and
  // stops on the first successful aim, so it never fights the user's scrolling.
  let currentRegistryRevealTimer = null;
  function scrollToCurrentRegistry() {
    if (currentRegistryRevealTimer) {
      clearInterval(currentRegistryRevealTimer);
      currentRegistryRevealTimer = null;
    }
    const aim = () => {
      const current = document.getElementById("rcp-current-registry");
      if (current && !current.hidden) {
        current.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      }
      return false;
    };
    if (aim()) return;
    scrollToBuilder();
    let waited = 0;
    currentRegistryRevealTimer = setInterval(() => {
      waited += 300;
      if (aim() || waited >= 4500) {
        clearInterval(currentRegistryRevealTimer);
        currentRegistryRevealTimer = null;
      }
    }, 300);
  }

  // Reveal + scroll to the "Your Registry" builder section (#rcp-browse).
  // `opts.instant` skips the animation: the chat panel pins the body at the
  // CURRENT scroll position when it opens on mobile, so a smooth scroll still
  // in flight gets frozen — and later restored — mid-animation. Callers that
  // are about to open the chat ask for the instant form. `behavior: "instant"`
  // is what beats the theme's `html { scroll-behavior: smooth }` (base.css).
  function scrollToBuilder(opts) {
    const browse = document.getElementById("rcp-browse");
    if (!browse) return;
    browse.hidden = false;
    browse.scrollIntoView({ behavior: opts && opts.instant ? "instant" : "smooth", block: "start" });
  }

  // ── "Make it truly yours" profile nudge: dismissable + remembered ─────────
  // "Skip for now" removes the ribbon and records the choice so it stays gone
  // on future visits (per-browser). The server still renders it only while the
  // profile is < 70% complete, so completing the profile retires it naturally.
  const NUDGE_DISMISS_KEY = "birchProfileNudgeDismissed";
  function wireProfileNudge() {
    const nudge = document.getElementById("rcp-profile-nudge");
    if (!nudge) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(NUDGE_DISMISS_KEY) === "1"; } catch (_) {}
    if (dismissed) { nudge.remove(); return; }
    const skip = document.getElementById("rcp-nudge-skip");
    if (skip) skip.addEventListener("click", () => {
      nudge.remove();
      try { localStorage.setItem(NUDGE_DISMISS_KEY, "1"); } catch (_) {}
    });
  }

  // ── Bootstrap ─────────────────────────────────────────────────────────────
  //
  // MUST stay the LAST statement in this IIFE. registry-edit.liquid loads this
  // file with `defer`, so it executes at readyState "interactive" and takes the
  // `else ready()` branch SYNCHRONOUSLY, mid-evaluation. Anything init() touches
  // has to be initialized by then — and top-level `let`/`const` are only
  // initialized when control reaches their line, so a bootstrap placed above
  // them throws "Cannot access '<x>' before initialization" (TDZ). Under the
  // old parser-blocking `script_tag` this was masked: readyState was "loading",
  // so ready() waited for DOMContentLoaded and the whole file had evaluated.
  //
  // Keeping it last makes the file order-independent again: every declaration
  // above is live before init() can run, whatever the script tag says.
  const ready = () => {
    if (!window.BirchRegistry) return setTimeout(ready, 30);
    init();
  };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", ready);
  else ready();
})();
